<!-- eslint-disable vue/multi-word-component-names -- component name is capitalised, and unlikely that html will introduce a similar tag -->
<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="sbi-main">
    <div class="left-menu" ref="leftMenu">
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>
    <div class="right-container">
      <div class="top-section">
        <div class="filter-desc">{{ filterText }}</div>
        <ButtonFilter
          class="button-filter-component"
          filterPosition="right"
          buttonText="Change Filter"
          :filterWidth="300"
          :isOpen.sync="mainFilterIsOpen"
          :buttonIsDisabled="mainFilterIsDisabled"
        >
          <div class="filter-popup">
            <BoxContainer
              title="Date"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="filterPaymentByDate"
            >
              <div>
                <DateTimePicker
                  :isRequired="filterPaymentByDate"
                  :showTime="false"
                  placeholder="From"
                  :isDisabled="!filterPaymentByDate"
                  :value.sync="paymentListFromDate"
                  :errorMessage.sync="fromDateErrMsg"
                />
                <DateTimePicker
                  :isRequired="filterPaymentByDate"
                  :showTime="false"
                  placeholder="To"
                  :isDisabled="!filterPaymentByDate"
                  :value.sync="paymentListToDate"
                  :errorMessage.sync="toDateErrMsg"
                />
              </div>
            </BoxContainer>
            <BoxContainer
              title="Search"
              :isCheckBox="true"
              style="margin-botton: 5px"
              :isChecked.sync="filterPaymentBySearch"
            >
              <div>
                <Dropdown
                  ref="searchby"
                  placeholder="Search By"
                  :items="searchFilterItems"
                  itemKey="Search By"
                  :isDisabled="!filterPaymentBySearch"
                  :isRequired="filterPaymentBySearch"
                  :selectedObject.sync="searchByValue"
                  :errorMessage.sync="searchByErrMsg"
                />
                <Dropdown
                  ref="searchcriteria"
                  placeholder="Search Criteria"
                  :items="searchCriteriaItems"
                  itemKey="name"
                  :isDisabled="!filterPaymentBySearch"
                  :isRequired="filterPaymentBySearch"
                  :selectedObject.sync="selectedFilterSearchCriteria"
                  :errorMessage.sync="searchCriteriaErrMsg"
                />
                <Input
                  ref="search"
                  placeholder="Search"
                  :inputValue.sync="searchInputValue"
                  :isDisabled="!filterPaymentBySearch"
                  :isRequired="filterPaymentBySearch"
                  :errorMessage.sync="searchInputErrMsg"
                />
              </div>
            </BoxContainer>
            <button 
              @click="refreshClicked('PaymentList')" 
              :disabled="!filterPaymentByDate && !filterPaymentBySearch">Refresh
            </button>
          </div>
        </ButtonFilter>
        <div>Last Refreshed: {{ lastRefreshed }}</div>
      </div>
      <div class="bottom-section">
        <TableBox
          title="Payment"
          :tableData="paymentListing"
          :selectedRow.sync="selectedPaymentListing"
          :loading="paymentTableBoxLoading"
          :hideColumns="[]"
        />
      </div>
    </div>

    <PopupEntry
      v-if="showPopup"
      :show.sync="showPopup"
      :title="popupTitle"
      :createdBy="createdBy"
      :updatedBy="updatedBy"
      @save="saveClicked"
      :requiredError="requiredError"
      @requiredClicked="requiredClicked"
      :successMessages="successMessages"
      :errorMessages="errorMessages"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
      style="display: flex; flex-direction: row; justify-content: space-around; overflow: scroll"
      :canPrint="true"
      @print="popupPrintClicked"
    >
      <div class="payment-entryfields">
        <!-- Summary of Events -->
        <BoxContainer
          v-if="action != 'N'"
          title="Summary of Events"
          :isCollapsible="true"
          :isCollapsed="summaryIsCollapsed"
        >
          <div class="summary-row">
            <div class="summary-block" v-for="(m, i) in summaryItems" :key="i">
              <Tooltip
                v-if="m['visible']"
                class="milestone-tooltip"
                :minWidth="210"
                :tooltipText="m['Label'].join(',')"
              >
                <div></div>
              </Tooltip>
              <div
                v-if="m['Title'] == 'Indicator' || m['Title'] == 'Authorized' || m['Title'] == 'LHDN Submission'"
                class="summary-item"
                :style="{ backgroundColor: m['bgcolorToShow'] }"
              >
                {{ m['indicatorToShow'] }}
              </div>
              <div
                v-if="m['Title'] == 'Statement No' && m['visible']"
                class="summary-item"
                :style="{ backgroundColor: m['bgcolorToShow'], minWidth: `${m['indicatorToShow'].length * 9}px` }"
              >
                {{ m['indicatorToShow'] }}
              </div>
              <div v-if="m['Title'] == 'PV Count'" class="summary-item">
                {{ m['indicatorToShow'] }}
                <img :src="m['Src']" height="20px" />
              </div>
            </div>
          </div>
          <div class="summary-row">
            <div v-for="(l, i) in Object.entries(newLegend)" :key="i" class="legend-items">
              <div class="legend-box" :style="{ backgroundColor: `${l[1]}` }"></div>
              {{ l[0] }}
            </div>
          </div>
        </BoxContainer>

        <!-- Payment Header -->
        <BoxContainer title="Payment Header" :isCollapsible="true" :isCollapsed="headerIsCollapsed">
          <div class="payment-header">
            <div class="header-containers">
              <Dropdown
                ref="paymenttype"
                placeholder="Payment Type"
                :items="paymentTypeList"
                itemKey="Description"
                :selectedObject.sync="selectedPaymentType"
                :errorMessage.sync="paymentTypeErr"
                :isRequired="true"
                :isDisabled="(viewMode && action != 'N') || selectedPaymentType != null"
                style="width: 75%"
                @selected="payTypeSelected"
              />
              <InputQuery
                ref="payeetype"
                placeholder="Payee Type"
                :items="payeeTypeList"
                itemKey="Party"
                :showDesc="false"
                :selectedObject.sync="selectedPayeeType"
                :errorMessage.sync="payeeTypeErr"
                :isRequired="true"
                :isDisabled="viewMode"
                @selected="payeeTypeSelected"
                style="width: 50%"
              />
              <InputQuery
                ref="payeecode"
                placeholder="Payee Code"
                :items="payeeCodeList"
                :itemKey="payeeCodeKey"
                :showDesc="false"
                :selectedObject.sync="selectedPayeeCode"
                :errorMessage.sync="payeeCodeErr"
                :isRequired="true"
                :isDisabled="
                  viewMode || payeeCodeNameDisabled || (selectedPayeeCode != null && jobChargesDetailsList.length > 0)
                "
                @selected="payeeCodeSelected"
                style="width: 50%"
              />
              <Input
                ref="payeename"
                placeholder="Payee Name"
                :inputValue.sync="payeeName"
                :isDisabled="viewMode || payeeCodeNameDisabled"
                :errorMessage.sync="payeeNameErr"
                validator="text,100"
                style="width: 75%"
                :isRequired="true"
              />
            </div>
            <div class="header-containers" style="margin-right: 50px">
              <InputQuery
                ref="doctype"
                placeholder="Doc. Type"
                :items="docTypeList"
                itemKey="Doc Code"
                :showDesc="false"
                :selectedObject.sync="selectedDocType"
                :errorMessage.sync="docTypeErr"
                :isDisabled="viewMode"
                style="width: 50%"
              />
              <Input
                ref="docrefno"
                placeholder="Doc. Ref. No."
                :inputValue.sync="docRefNo"
                :isDisabled="viewMode"
                :errorMessage.sync="docRefNoErr"
                validator="text,35"
                style="width: 70%"
              />
              <Dropdown
                placeholder="Self-Billed"
                :isDisabled="viewMode"
                :items="selfBilledOptions"
                itemKey="Type"
                :selectedObject.sync="selectedSelfBilledType"
                :errorMessage.sync="selfBilledErr"
                style="width: 70%"
              />
              <CheckBox title="Advance Payment" :isChecked.sync="isAdvancePayment" :isDisabled="viewMode" />
              <CheckBox
                title="Private & Confidential"
                v-if="sasFieldsControl['chkPrivate']"
                :isChecked.sync="isPnC"
                :isDisabled="viewMode"
              />
            </div>
            <div class="header-containers" style="margin-right: 50px">
              <div style="display: flex">
                <InputQuery
                  placeholder="Currency"
                  :isRequired="true"
                  :items="currencyList"
                  itemKey="Currency Code"
                  descKey="Description"
                  :isDisabled="viewMode || jobChargesDetailsList.length > 0 || directPostingDetailsList.length > 0"
                  :selectedObject.sync="selectedHeaderCurr"
                  ref="headercurr"
                  :errorMessage.sync="headerCurrErr"
                  :showDesc="false"
                  style="margin-right: 5px"
                  @selected="headerCurrencySelected"
                />
                <Input
                  placeholder="Exch. Rate"
                  :isRequired="true"
                  :isDisabled="viewMode || (selectedHeaderCurr && selectedHeaderCurr['Currency Code'] == 'MYR')"
                  textAlign="right"
                  :inputValue.sync="headerExchRate"
                  :errorMessage.sync="headerExchRateErr"
                  validator="positiveDecimal,15,4"
                  ref="headerrate"
                  style="margin-right: 5px"
                  @focusChanged="headerRateLossFocus"
                />
                <Input
                  placeholder="Exch. Factor"
                  :isDisabled="true"
                  textAlign="right"
                  :inputValue.sync="headerExchFactor"
                  validator="positiveDecimal,15,4"
                />
              </div>
              <RemarksBox
                :text.sync="remarks"
                :isDisabled="viewMode"
                title="Remarks"
                :maxLength="200"
                style="height: 75%"
              />
            </div>
            <div class="header-containers">
              <Input
                ref="paymentno"
                placeholder="Payment No."
                :inputValue.sync="paymentNo"
                :isDisabled="action != 'N' || disablePaymentNo"
                :errorMessage.sync="paymentNoErr"
                validator="text,25"
              />
              <DateTimePicker
                ref="paymentdate"
                placeholder="Payment Date"
                :value.sync="paymentDate"
                :showTime="false"
                :isDisabled="viewMode"
                :errorMessage.sync="paymentDateErr"
                :isRequired="true"
                :maxDate="new Date('2079-06-06')"
              />
              <div style="display: flex">
                <InputQuery
                  ref="paymentmethod"
                  placeholder="Payment Method"
                  :items="paymentMethodList"
                  itemKey="Payment Type"
                  :showDesc="false"
                  :selectedObject.sync="selectedPaymentMethod"
                  :errorMessage.sync="paymentMethodErr"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  @selected="paymentMethodSelected"
                />
                <InputQuery
                  ref="bank"
                  placeholder="Bank"
                  :items="bankList"
                  itemKey="BankCode"
                  :showDesc="false"
                  :selectedObject.sync="selectedBank"
                  :errorMessage.sync="bankErr"
                  :isDisabled="viewMode"
                  :isRequired="createCheque || bankRequired"
                />
              </div>
              <BoxContainer
                title="Create Cheque"
                :isCheckBox="true"
                :isChecked.sync="createCheque"
                :checkBoxIsDisabled="disableCheque"
              >
                <Input
                  ref="chequegiro"
                  placeholder="Cheque No./Giro No."
                  :inputValue.sync="chequeGiroNo"
                  :errorMessage.sync="chequeGiroErr"
                  :isDisabled="viewMode || !createCheque || selectedPaymentMethod['Payment Type'] == 'CS'"
                  validator="text,25,,spacedash"
                  :isRequired="createCheque"
                />
              </BoxContainer>
            </div>
          </div>
        </BoxContainer>

        <!-- Bank Details -->
        <BoxContainer title="Bank Details" :isCollapsible="true" :isCollapsed="bankIsCollapsed">
          <div style="display: flex">
            <Input placeholder="Bank Name" :inputValue.sync="bankName" style="margin-right: 5px" :isDisabled="true" />
            <Input
              placeholder="Bank Acc. No."
              :inputValue.sync="bankAccNo"
              style="margin-right: 5px"
              :isDisabled="true"
            />
            <Input
              placeholder="Bank Swift Code"
              :inputValue.sync="bankSwift"
              style="margin-right: 5px"
              :isDisabled="true"
            />
            <Input placeholder="Co.Registration No." :inputValue.sync="coRegNo" :isDisabled="true" />
          </div>
        </BoxContainer>

        <div style="height: 100%; margin-top: 10px">
          <Tab
            :preventTabSwitch="true"
            :tabs="tabsItems"
            :tabIndex.sync="currentTabIndex"
            @tabClicked="tabClicked"
            style="height: 100%"
          >
            <!-- Job Charges / AP Invoices Tab -->
            <div slot="Job Charges / AP Invoices" class="tab-slot">
              <div
                class="tab-details-styling"
                :style="{
                  width: showAPInvoices || showJobCharges || showInputCharges ? '50%' : '100%',
                }"
              >
                <div style="display: flex; flex-direction: row; justify-content: space-between">
                  <div class="tab-buttons" style="width: 100%">
                    <button
                      v-if="!viewMode || action != ''"
                      @click="openAPInvoices"
                      style="min-width: 100px"
                      :disabled="showAPInvoices"
                    >
                      Select AP Invoices
                    </button>
                    <button
                      v-if="!viewMode || action != ''"
                      @click="openJobCharges"
                      style="min-width: 100px"
                      :disabled="showJobCharges"
                    >
                      Select From Job Charges
                    </button>
                    <button
                      v-if="!viewMode || action != ''"
                      @click="openNewChargeCodeFields"
                      style="min-width: 100px"
                      :disabled="showInputCharges"
                    >
                      Add Charge Code
                    </button>
                    <button
                      v-if="!viewMode || action != ''"
                      @click="deleteJobCharges"
                      style="min-width: 100px"
                      :disabled="showInputCharges"
                    >
                      Delete Charges
                    </button>
                  </div>
                  <div
                    :style="{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'flex-end',
                      margin: showAPInvoices || showJobCharges || showInputCharges ? '5px' : '',
                    }"
                  >
                    <div style="margin-right: 15px; align-self: center">
                      <b style="font-size: 1em">Total Tax</b>
                      <div class="total-block" style="font-size: 1em; padding-top: 5px">
                        {{ totalTaxCharges }} MYR
                      </div>
                    </div>
                    <div style="margin-right: 10px; align-self: center">
                      <b style="font-size: 1em">Total Amount</b>
                      <div style="display: flex">
                        <div class="total-block" style="font-size: 1em; padding-top: 5px">
                          {{
                            jcMultiCurr ? 'N/A' : (
                              `${selectedHeaderCurr ? selectedHeaderCurr['Currency Code'] : 'MYR'} ${totalFAmountCharges}`
                            )
                          }}
                        </div>
                        <div
                          class="total-block"
                          style="
                            background-color: #467cdc;
                            color: white;
                            font-weight: bold;
                            font-size: 1em;
                            padding-top: 5px;
                          "
                        >
                          MYR {{ totalAmountCharges }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <TableBox
                  title="Job Charges / AP Invoices"
                  v-if="showJobChargesTable"
                  @buttonClicked="jobChargesTableButtonClicked"
                  :tableData="jobChargesDetailsList"
                  :dataType="jobChargesDataType"
                  style="height: auto; z-index: 0"
                  :additionalButtons="action == '' ? [] : tabTableButtons"
                  :selectedRow.sync="selectedJobChargeEntry"
                  :hideColumns="[
                    'action',
                    'JobChargesID',
                    'TxnID',
                    'Total Tax',
                    'Total Amount',
                    'F.Total Amount',
                    'Payment No',
                    'RecordSource',
                    'Pending Billing Charges',
                    'execnewjc',
                  ]"
                />
              </div>

              <!-- Select AP Invoices -->
              <div v-if="showAPInvoices" class="side-entry-open" :style="{ width: showAPInvoices ? '50%' : '0%' }">
                <div style="margin-top: 3px">
                  <div class="title-button">
                    <b>Select AP Invoices</b>
                    <div class="buttons-arr">
                      <button @click="selectDeselectAPClicked"> {{ selectDeselectAP }} All </button>
                      <button @click="addSelectedAPInv">Add AP Invoices</button>
                      <button @click="cancelSelectAPInv">Cancel</button>
                    </div>
                  </div>
                  <BoxContainer style="margin-top: 5px; padding-top: 1px; padding-bottom: 1px">
                    <div> <b>Payee Type:</b> {{ selectedPayeeType['Party'] }} </div>
                    <div> <b>Payee Code:</b> {{ selectedPayeeCode[payeeCodeKey] }} </div>
                    <div><b>Payee:</b> {{ payeeName }}</div>
                  </BoxContainer>
                  <div>
                    <TableBox
                      title="AP Invoice"
                      :tableData="APInvoiceListing"
                      :selectedRow.sync="selectedAPInvoices"
                      :maxSelection="-1"
                      :hideColumns="[]"
                      :dataType="apinvDataType"
                    />
                  </div>
                </div>
              </div>

              <!-- Select from Job Charges -->
              <div v-if="showJobCharges" class="side-entry-open" :style="{ width: showJobCharges ? '50%' : '0%' }">
                <div class="title-button" style="margin-top: 5px">
                  <b>Select From Job Charges</b>
                  <div class="buttons-arr">
                    <button @click="selectDeselectJobChargesClicked"> {{ selectDeselectJC }} All </button>
                    <button @click="addSelectedJobCharges"> Add Job Charges </button>
                    <ButtonFilter
                      class="button-filter-component"
                      filterPosition="right"
                      buttonText="Change Filter"
                      :filterWidth="600"
                      :isOpen.sync="jobChargesFilterOpen"
                      :buttonIsDisabled="jobChargesFilterBtnDisabled"
                      style="margin-right: 5px"
                    >
                      <div class="filter-popup" style="display: flex; flex-direction: row">
                        <div style="width: 100%">
                          <RadioButtonSet
                            title="Payee"
                            :buttons="payeeFilterSelection"
                            labelKey="label"
                            :selected.sync="selectedPayeeFilterJC"
                            :isHorizontal="true"
                            :isDisabled="viewMode"
                            style="margin-right: 5px"
                          />
                          <BoxContainer
                            title="As At"
                            :isCheckBox="true"
                            style="margin-bottom: 5px"
                            :isChecked.sync="filterJobByAsAt"
                          >
                            <div>
                              <DateTimePicker
                                :isRequired="filterJobByAsAt"
                                :showTime="false"
                                placeholder="Date"
                                :isDisabled="!filterJobByAsAt"
                                :value.sync="asAtDate"
                                :errorMessage.sync="asAtDateErr"
                              />
                            </div>
                          </BoxContainer>
                          <InputQuery
                            placeholder="Job No"
                            :items="jobNoList"
                            itemKey="Job No"
                            :showDesc="false"
                            :selectedObject.sync="selectedJobNoSearch"
                            :isDisabled="viewMode"
                            style="width: 60%"
                            :showRowLimit="true"
                            :rowLimitInputValue.sync="jobnoRowLimit"
                            @rowLimitFocusChanged="jobnoRowLimitChanged"
                          />
                          <InputQuery
                            placeholder="Charges Code"
                            :items="expenseCodeList"
                            itemKey="Expenses Code"
                            descKey="Description"
                            :selectedObject.sync="selectedChargeCodeSearch"
                            :isDisabled="viewMode"
                          />
                        </div>
                        <div style="margin-left: 10px; width: 100%">
                          <div style="width: 100%"> Container No ({{ contSearchCount }}) </div>
                          <textarea
                            title="1 Container No per line"
                            v-model="searchContNo"
                            style="width: 100%; height: 90%"
                          ></textarea>
                        </div>
                      </div>
                      <button
                        @click="refreshClicked('Job')"
                        :disabled="searchJobChargesFilterDisable"
                        style="margin-top: 10px"
                      >
                        Refresh
                      </button>
                    </ButtonFilter>
                    <button @click="cancelSelectJobCharges">Cancel</button>
                  </div>
                </div>
                <div style="overflow: scroll">
                  <div style="display: flex">
                    <BoxContainer style="margin-top: 5px; padding-top: 1px; padding-bottom: 1px; width: 70%">
                      <div> <b>Payee Type:</b> {{ selectedPayeeType['Party'] }} </div>
                      <div> <b>Payee Code:</b> {{ selectedPayeeCode[payeeCodeKey] }} </div>
                      <div><b>Payee:</b> {{ payeeName }}</div>
                    </BoxContainer>
                    <!-- <Input
                      placeholder="No of Rows Shown"
                      :inputValue.sync="jobchargesRowLimit"
                      :isDisabled="viewMode"
                      validator="integer,6"
                      @focusChanged="jobChargesListingRowChange"
                      style="width: 30%; margin: 0px 5px; align-self: center"
                    /> -->
                  </div>
                  <TableBox
                    title="Job Charges"
                    :tableData="jobChargesListing"
                    :selectedRow.sync="selectedJobCharges"
                    :maxSelection="-1"
                    :hideColumns="['TxnID', 'Pending Billing Charges', 'MTR Job Charges']"
                    :loading="jobChargesTableBoxLoading"
                    style="z-index: 0"
                  />
                </div>
              </div>

              <!-- Charges Input Entry -->
              <div v-if="showInputCharges" class="side-entry-open" :style="{ width: showInputCharges ? '50%' : '0%' }">
                <div class="title-button">
                  <b>{{ subtitleJC }}</b>
                  <div class="buttons-arr">
                    <ButtonFilter
                      class="button-filter-component"
                      filterPosition="center"
                      buttonText="View Job Billing Cust Credit"
                      :filterWidth="430"
                      :isOpen.sync="custCreditOpen"
                      :buttonIsDisabled="custCreditDisable"
                      style="margin-right: 5px"
                    >
                      <div class="filter-popup" style="align-items: flex-start" v-if="billCreditDetails != null">
                        <div>
                          <b>Job No:</b>
                          {{ selectedJobNo ? selectedJobNo['Job No'] : '' }}
                        </div>
                        <div><b>Billing Customer:</b> {{ billCustName }}</div>
                        <div style="display: flex; justify-content: space-between; width: 100%">
                          <div style="width: 50%; text-align: left">
                            <b>Credit Limit:</b>
                            {{ billCreditDetails['Currency'] }}
                            {{ billCreditDetails['CreditLimit'] }}
                          </div>
                          <div style="width: 50%; text-align: left; margin-left: 10px">
                            <b>Credit Terms:</b>
                            {{ billCreditDetails['CreditTerm'] }}
                            {{ billCreditDetails['CreditTypeDesc'] }}
                          </div>
                        </div>
                        <div style="margin-top: 5px">
                          <b style="align-self: center">Status:</b>
                          <span
                            class="status-container"
                            :style="{
                              backgroundColor: billCreditDetails['CusStatusColor'],
                            }"
                            >{{ billCreditDetails['CusStatus'] }}</span
                          >
                        </div>
                        <div style="margin-top: 5px; display: flex; width: 100%">
                          <b style="align-self: center">Outstanding:</b>
                          <Tooltip
                            position="top"
                            :tooltipText="`${billCreditDetails['Currency']} ${billCreditDetails['Outstanding']}`"
                            style="width: 100%"
                            :xOffset="6"
                            :yOffset="2"
                          >
                            <span class="outstanding-prog">
                              <div
                                style="z-index: 1; display: flex; justify-content: center; width: 100%; cursor: default"
                              >
                                {{ `${billCreditDetails['PerOutstanding']}%` }}
                              </div>
                              <div
                                class="progress-ind"
                                :style="{
                                  width: `${billCreditDetails['PerOutstanding'] > 100 ? 100 : billCreditDetails['PerOutstanding']}%`,
                                  backgroundColor: billCreditDetails['PerOutstandingColor'],
                                }"
                              ></div>
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                    </ButtonFilter>
                    <button v-if="!viewMode" @click="saveChargesClicked"> Save Charges </button>
                    <button @click="cancelJobChargesClicked">Cancel</button>
                  </div>
                </div>
                <div style="overflow: scroll; min-height: calc(100% - 40px)">
                  <div style="display: flex; justify-content: space-between; margin-top: 5px">
                    <div style="width: 100%; margin-right: 10px">
                      <InputQuery
                        placeholder="Charges Code"
                        :items="expenseCodeList"
                        itemKey="Expenses Code"
                        descKey="Description"
                        :showDesc="false"
                        :selectedObject.sync="selectedCharges"
                        :isRequired="chargeCodeRequired"
                        :isDisabled="viewMode || !chargeCodeRequired || billedJob"
                        :errorMessage.sync="chargesErr"
                        ref="jccharges"
                      />
                      <InputQuery
                        placeholder="Job No"
                        :items="jobNoList"
                        itemKey="Job No"
                        :showDesc="false"
                        :selectedObject.sync="selectedJobNo"
                        :isDisabled="viewMode || !chargeCodeRequired || billedJob"
                        :showRowLimit="true"
                        :rowLimitInputValue.sync="jobnoRowLimit"
                        @rowLimitFocusChanged="jobnoRowLimitChanged"
                      />
                      <InputQuery
                        placeholder="Cont No"
                        :items="containerList"
                        itemKey="Cont No"
                        :showDesc="false"
                        :selectedObject.sync="selectedContainer"
                        :isDisabled="viewMode || !chargeCodeRequired"
                        @selected="contNoSelected"
                        :showRowLimit="true"
                        :rowLimitInputValue.sync="contnoRowLimit"
                        @rowLimitFocusChanged="contnoRowLimitChanged"
                      />
                      <InputQuery
                        placeholder="Cost Code"
                        :items="costCodeList"
                        itemKey="Cost Code"
                        descKey="Cost Desc"
                        :showDesc="false"
                        :selectedObject.sync="selectedJCCostCode"
                        :isDisabled="viewMode"
                      />
                      <Input
                        placeholder="SMK No"
                        :inputValue.sync="smkNoJC"
                        :isDisabled="viewMode"
                        :errorMessage.sync="smkNoJCErr"
                        validator="text,35"
                        ref="jcsmkno"
                      />
                      <Input
                        placeholder="Ref #"
                        :inputValue.sync="jobChargeRefNo"
                        :isDisabled="viewMode"
                        :errorMessage.sync="jobChargeRefNoErr"
                        validator="text,35"
                        ref="jcrefno"
                      />
                    </div>
                    <div style="width: 100%; margin: 0px 10px">
                      <RemarksBox
                        :text.sync="jobChargeRemarks"
                        :isDisabled="viewMode"
                        title="Remarks"
                        :maxLength="200"
                        style="height: 100%"
                      />
                    </div>
                  </div>

                  <div style="margin: 5px; margin-top: 15px">
                    <div class="foreign-currency-main">
                      <div class="foreign-currency-controls">
                        <div class="foreign-currency-row">
                          <div class="foreign-currency-column">
                            <div class="foreign-currency-row">
                              <Input
                                textAlign="right"
                                placeholder="Quantity"
                                style="max-width: 120px"
                                :isRequired="true"
                                :inputValue.sync="chargesQty"
                                ref="jcqty"
                                :errorMessage.sync="chargesQtyErr"
                                :isDisabled="viewMode"
                                validator="positiveInteger,4,,none"
                                @focusChanged="chargesPriceLossFocus($event, 'qty')"
                              />
                              <InputQuery
                                placeholder="UOM"
                                :items="uomList"
                                itemKey="UOM Code"
                                :showDesc="false"
                                :selectedObject.sync="selectedChargesUOM"
                                :isDisabled="viewMode"
                              />
                            </div>
                          </div>
                          <div class="foreign-currency-column" style="text-align: center"></div>
                          <div class="foreign-currency-column" style="text-align: center"> MYR Equivalent </div>
                        </div>
                        <BoxContainer background="rgba(225, 255, 255)">
                          <div class="foreign-currency-row">
                            <div class="foreign-currency-column">
                              <div class="foreign-currency-row">
                                <InputQuery
                                  placeholder="Currency"
                                  :isRequired="true"
                                  :items="currencyList"
                                  itemKey="Currency Code"
                                  descKey="Description"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  :selectedObject.sync="selectedChargesCurrency"
                                  ref="chargesCurrency"
                                  :errorMessage.sync="chargesCurrencyErr"
                                  :showDesc="false"
                                  @selected="chargesCurrSelected"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-right: 5px; padding-left: 10px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesUPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'unitPrice')"
                                  :errorMessage.sync="chargesUPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcuprice"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="chargesTotalPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'totalPrice')"
                                  :errorMessage.sync="chargesTotalPriceErr"
                                  validator="decimal,14,4"
                                  ref="jxtotprice"
                                />
                                <Input
                                  placeholder="Exch. Rate"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable || chargeExchgRateDisabled"
                                  textAlign="right"
                                  :inputValue.sync="chargesExchangeRate"
                                  :errorMessage.sync="chargesExchangeRateErr"
                                  @focusChanged="chargesPriceLossFocus($event, 'exchgRate')"
                                  validator="positiveDecimal,15,4"
                                  ref="jcexchgrate"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-left: 5px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="myrChargesUPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'unitMYRPrice')"
                                  :errorMessage.sync="myrChargesUPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcmyruprice"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode || chargesAmtInputDisable"
                                  textAlign="right"
                                  :inputValue.sync="myrChargesTotalPrice"
                                  @focusChanged="chargesPriceLossFocus($event, 'totalMYRPrice')"
                                  :errorMessage.sync="myrChargesTotalPriceErr"
                                  validator="decimal,14,4"
                                  ref="jcmyrtotprice"
                                />
                              </div>
                            </div>
                          </div>
                        </BoxContainer>
                        <BoxContainer background="rgba(225, 255, 255)">
                          <div class="foreign-currency-row">
                            <div class="foreign-currency-column">
                              <div class="foreign-currency-row">
                                <InputQuery
                                  placeholder="Tax Code"
                                  itemKey="Tax Code"
                                  :allowFreeText="false"
                                  descKey="Tax Code Desc"
                                  :showDesc="false"
                                  :isDisabled="
                                    viewMode ||
                                    chargesAmtInputDisable ||
                                    !sasFieldsControl['cmdTaxCode'] ||
                                    !sasFieldsControl['TxtTaxCode']
                                  "
                                  :items="taxCodeList"
                                  :selectedObject.sync="selectedChargesTaxCode"
                                  @selected="taxCodeChrgSelected"
                                />
                                <Input
                                  placeholder="Tax Rate %"
                                  style="width: 50%; margin-left: 4px"
                                  :isDisabled="viewMode || chargesAmtInputDisable || !sasFieldsControl['TxtTaxRate']"
                                  textAlign="right"
                                  :inputValue.sync="chargesTaxRate"
                                  @focusChanged="chargesPriceLossFocus($event, 'taxRate')"
                                  :isRequired="selectedChargesTaxCode != null"
                                  validator="positiveInteger,4"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-right: 5px; padding-left: 10px">
                              <div>Net Inclusive Tax</div>
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Tax Amt"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="chargesTaxAmt"
                                  :errorMessage.sync="chargesTaxAmtErr"
                                  validator="decimal,14,4"
                                  ref="jctaxamt"
                                />
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="chargesUPriceInclTax"
                                  :errorMessage.sync="chargesUPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="chargesTotalPriceInclTax"
                                  :errorMessage.sync="chargesTotalPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-left: 5px">
                              <div>Net Inclusive Tax</div>
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Tax Amt"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="myrChargesTaxAmt"
                                  :errorMessage.sync="myrChargesTaxAmtErr"
                                  validator="decimal,14,4"
                                />
                                <Input
                                  placeholder="U.Price"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="myrChargesUPriceInclTax"
                                  :errorMessage.sync="myrChargesUPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="true"
                                  textAlign="right"
                                  :inputValue.sync="myrChargesTotalPriceInclTax"
                                  :errorMessage.sync="myrChargesTotalPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                              </div>
                            </div>
                          </div>
                        </BoxContainer>
                        <!-- Hide rebate amount for now first -->
                        <div v-if="enableVendorRebate && false" style="margin-top: 10px; width: calc(100% / 3)">
                          <Input
                            textAlign="left"
                            placeholder="Rebate Amt"
                            style="max-width: 100%"
                            :inputValue.sync="chargesRebate"
                            :errorMessage.sync="chargesRebateErr"
                            :isDisabled="viewMode"
                            validator="decimal,14,4"
                          />
                        </div>
                      </div>
                      <div class="foreign-currency-background">
                        <div class="background-left"></div>
                        <div class="background-middle"></div>
                        <div class="background-right"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Direct Posting Tab -->
            <div slot="Direct Posting" class="tab-slot">
              <div
                class="tab-details-styling"
                :style="{
                  width: showExpenses || showInputDirectPosting ? '50%' : '100%',
                }"
              >
                <div style="display: flex; flex-direction: row; justify-content: space-between">
                  <div class="tab-buttons" style="width: 100%">
                    <button v-if="!viewMode || action != ''" @click="openExpenses" :disabled="showExpenses">
                      Select Expenses
                    </button>
                    <button
                      v-if="!viewMode || action != ''"
                      @click="openNewDirectPostingFields"
                      :disabled="showInputDirectPosting"
                    >
                      Add Direct Posting
                    </button>
                    <button
                      v-if="!viewMode || action != ''"
                      @click="deleteDirectPosting"
                      :disabled="showInputDirectPosting"
                    >
                      Delete Direct Posting
                    </button>
                  </div>
                  <div
                    :style="{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'flex-end',
                      margin: showExpenses || showInputDirectPosting ? '5px' : '',
                    }"
                  >
                    <div style="margin-right: 15px; align-self: center">
                      <b style="font-size: 1em">Total Tax</b>
                      <div class="total-block" style="font-size: 1em; padding-top: 5px"> {{ totalTaxDP }} MYR </div>
                    </div>
                    <div style="margin-right: 10px; align-self: center">
                      <b style="font-size: 1em">Total Amount</b>
                      <div style="display: flex">
                        <div class="total-block" style="font-size: 1em; padding-top: 5px">
                          {{
                            dpMultiCurr ? 'N/A' : (
                              `${selectedHeaderCurr ? selectedHeaderCurr['Currency Code'] : 'MYR'} ${totalDPFAmt}`
                            )
                          }}
                        </div>
                        <div
                          class="total-block"
                          style="
                            background-color: #467cdc;
                            color: white;
                            font-weight: bold;
                            font-size: 1em;
                            padding-top: 5px;
                          "
                        >
                          MYR {{ totalDPAmt }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <TableBox
                  title="Direct Posting"
                  v-if="showDirectPostingTable"
                  @buttonClicked="directPostingTableButtonClicked"
                  :tableData="directPostingDetailsList"
                  :dataType="directPostingDataType"
                  style="height: auto; z-index: 0"
                  :additionalButtons="action == '' ? [] : tabTableButtons"
                  :selectedRow.sync="selectedDirectPostingEntry"
                  :hideColumns="[
                    'action',
                    'Total Tax',
                    'Total DP Amount',
                    'Total DP F.Amount',
                    'Payment No',
                    'ExpenseGUID',
                    'Seq No',
                  ]"
                />
              </div>

              <!-- Select Expenses -->
              <div v-if="showExpenses" class="side-entry-open" :style="{ width: showExpenses ? '50%' : '0%' }">
                <div class="title-button">
                  <b>Select Expenses</b>
                  <div class="buttons-arr">
                    <button @click="selectDeselectExpensesClicked"> {{ selectDeselectExpenses }} All </button>
                    <button @click="addSelectedExpenses">Add Expenses</button>
                    <ButtonFilter
                      class="button-filter-component"
                      filterPosition="right"
                      buttonText="Change Filter"
                      :filterWidth="500"
                      :isOpen.sync="expensesFilterOpen"
                      :buttonIsDisabled="expensesFilterBtnDisabled"
                      style="margin-right: 5px"
                    >
                      <div style="display: flex; flex-direction: row" class="filter-popup">
                        <div style="width: 100%">
                          <BoxContainer
                            title="Expense Date"
                            :isCheckBox="true"
                            style="margin-bottom: 5px"
                            :isChecked.sync="filterExpensesByDate"
                          >
                            <div>
                              <DateTimePicker
                                :isRequired="filterExpensesByDate"
                                :showTime="false"
                                placeholder="From"
                                :isDisabled="!filterExpensesByDate"
                                :value.sync="filterExpenseFromDate"
                                :errorMessage.sync="filterExpenseFromDateErr"
                              />
                              <DateTimePicker
                                :isRequired="filterExpensesByDate"
                                :showTime="false"
                                placeholder="To"
                                :isDisabled="!filterExpensesByDate"
                                :value.sync="filterExpenseToDate"
                                :errorMessage.sync="filterExpenseToDateErr"
                              />
                            </div>
                          </BoxContainer>
                          <InputQuery
                            placeholder="Expense Code"
                            :items="expenseCodeList"
                            itemKey="Expenses Code"
                            descKey="Description"
                            :selectedObject.sync="selectedExpenseCode"
                            :isDisabled="viewMode"
                          />
                          <CheckBox title="GST Inclusive" :isChecked.sync="gstInclusive" :isDisabled="viewMode" />
                        </div>
                        <div style="width: 100%; margin: 10px">
                          <InputQuery
                            placeholder="Vehicle #"
                            :items="vehicleNoList"
                            itemKey="Lorry No"
                            :showDesc="false"
                            :selectedObject.sync="selectedExpenseVehicle"
                            :isDisabled="viewMode"
                          />
                          <Input
                            placeholder="Job #"
                            :inputValue.sync="expenseJobNo"
                            :isDisabled="viewMode"
                            :errorMessage.sync="expenseJobNoErr"
                            validator="text,25"
                            style="width: 92%"
                          />
                          <Input
                            placeholder="Cash Voucher #"
                            :inputValue.sync="expenseCashVoucher"
                            :isDisabled="viewMode"
                            :errorMessage.sync="expenseCashVoucherErr"
                            validator="text,25"
                            style="width: 92%"
                          />
                          <InputQuery
                            placeholder="Expenses Doc Ref #"
                            :items="expensesDocRefNoList"
                            itemKey="Expenses  Ref #"
                            :showDesc="false"
                            :selectedObject.sync="selectedExpensesRefNo"
                            :isDisabled="viewMode"
                          />
                        </div>
                      </div>
                      <button
                        @click="refreshClicked('Expenses')"
                        :disabled="searchJobChargesFilterDisable"
                        style="margin-top: 10px"
                      >
                        Refresh
                      </button>
                    </ButtonFilter>
                    <button @click="cancelSelectExpenses">Cancel</button>
                  </div>
                </div>
                <div style="overflow: scroll">
                  <TableBox
                    title="Expenses"
                    :tableData="expensesListing"
                    :selectedRow.sync="selectedExpenses"
                    :maxSelection="-1"
                    :hideColumns="['ExpenseGUID']"
                    style="z-index: 0"
                    :loading="expensesTableLoading"
                  />
                </div>
              </div>

              <!-- Add Direct Posting -->
              <div
                v-if="showInputDirectPosting"
                class="side-entry-open"
                :style="{ width: showInputDirectPosting ? '50%' : '0%' }"
              >
                <div class="title-button">
                  <b>{{ viewMode ? 'Direct Posting' : subtitleDP }}</b>
                  <div class="buttons-arr">
                    <button @click="saveDirectPosting" v-if="!viewMode"> Save Direct Posting </button>
                    <button @click="cancelDirectPostingClicked">Cancel</button>
                  </div>
                </div>
                <div style="overflow: scroll; min-height: calc(100% - 40px)">
                  <div style="display: flex; justify-content: space-between; margin-top: 5px">
                    <div style="width: 100%">
                      <InputQuery
                        placeholder="Debit Account"
                        :items="debitAccList"
                        itemKey="Acc No"
                        descKey="Description"
                        :selectedObject.sync="selectedDebitAcc"
                        :isRequired="true"
                        :isDisabled="viewMode"
                        :errorMessage.sync="selectedDebitAccErr"
                        ref="dpdebitacc"
                      />
                      <InputQuery
                        placeholder="Tax Debit Account"
                        :items="debitAccList"
                        itemKey="Acc No"
                        descKey="Description"
                        :selectedObject.sync="selectedTaxDebitAcc"
                        :isDisabled="viewMode"
                        :errorMessage.sync="selectedTaxDebitAccErr"
                      />
                      <InputQuery
                        placeholder="Cost Code"
                        :items="costCodeList"
                        itemKey="Cost Code"
                        descKey="Cost Desc"
                        :showDesc="false"
                        :selectedObject.sync="selectedDPCostCode"
                        :isDisabled="viewMode"
                      />
                      <Input
                        placeholder="SMK No"
                        :inputValue.sync="smkNoDP"
                        :isDisabled="viewMode"
                        :errorMessage.sync="smkNoDPErr"
                        validator="text,35"
                        ref="dpsmkno"
                      />
                      <InputQuery
                        placeholder="Vehicle No."
                        :items="vehicleNoList"
                        itemKey="Lorry No"
                        descKey="Vehicle Code"
                        :showDesc="false"
                        :selectedObject.sync="selectedDPVehicleNo"
                        :isDisabled="viewMode"
                      />
                    </div>
                    <div style="width: 100%; margin: 0px 10px">
                      <RemarksBox
                        :text.sync="directPostingRemarks"
                        :isDisabled="viewMode"
                        title="Remarks"
                        :maxLength="200"
                        style="height: 100%"
                      />
                    </div>
                  </div>
                  <div style="margin: 5px; margin-top: 15px">
                    <div class="foreign-currency-main">
                      <div class="foreign-currency-controls">
                        <div class="foreign-currency-row">
                          <div class="foreign-currency-column"></div>
                          <div class="foreign-currency-column" style="text-align: center"></div>
                          <div class="foreign-currency-column" style="text-align: center"> MYR Equivalent </div>
                        </div>
                        <BoxContainer background="rgba(225, 255, 255)">
                          <div class="foreign-currency-row">
                            <div class="foreign-currency-column">
                              <div class="foreign-currency-row">
                                <InputQuery
                                  placeholder="Currency"
                                  :isRequired="true"
                                  :items="currencyList"
                                  itemKey="Currency Code"
                                  descKey="Description"
                                  :isDisabled="viewMode"
                                  :selectedObject.sync="selectedDPCurrency"
                                  ref="dpCurrency"
                                  :errorMessage.sync="dpCurrencyErr"
                                  :showDesc="false"
                                  @selected="dpCurrSelected"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-right: 5px; padding-left: 10px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode"
                                  textAlign="right"
                                  :inputValue.sync="dpTotalPrice"
                                  @focusChanged="dpPriceLossFocus($event, 'totalPrice')"
                                  :errorMessage.sync="dpTotalPriceErr"
                                  validator="decimal,14,4"
                                />
                                <Input
                                  placeholder="Exch. Rate"
                                  :isRequired="true"
                                  :isDisabled="viewMode || dpExchgRateDisabled"
                                  textAlign="right"
                                  :inputValue.sync="dpExchangeRate"
                                  :errorMessage.sync="dpExchangeRateErr"
                                  validator="positiveDecimal,15,4"
                                  @focusChanged="dpPriceLossFocus($event, 'exchgRate')"
                                  ref="dpexchrate"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-left: 5px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Total"
                                  :isRequired="true"
                                  :isDisabled="viewMode"
                                  textAlign="right"
                                  :inputValue.sync="myrDPTotalPrice"
                                  @focusChanged="dpPriceLossFocus($event, 'totalMYRPrice')"
                                  :errorMessage.sync="myrDPTotalPriceErr"
                                  validator="decimal,14,4"
                                />
                              </div>
                            </div>
                          </div>
                        </BoxContainer>
                        <BoxContainer background="rgba(225, 255, 255)">
                          <div class="foreign-currency-row">
                            <div class="foreign-currency-column">
                              <div class="foreign-currency-row">
                                <InputQuery
                                  placeholder="Tax Code"
                                  itemKey="Tax Code"
                                  :allowFreeText="false"
                                  descKey="Tax Code Desc"
                                  :showDesc="false"
                                  :isDisabled="
                                    viewMode || !sasFieldsControl['cmdTaxCodeDP'] || !sasFieldsControl['TxtTaxCodeDP']
                                  "
                                  :items="taxCodeList"
                                  :selectedObject.sync="selectedDPTaxCode"
                                  @selected="taxCodeDPSelected"
                                />
                                <Input
                                  placeholder="Tax Rate %"
                                  style="width: 50%; margin-left: 4px"
                                  :isDisabled="viewMode || !sasFieldsControl['TxtTaxRateDP']"
                                  textAlign="right"
                                  :inputValue.sync="dpTaxRate"
                                  @focusChanged="dpPriceLossFocus($event, 'taxRate')"
                                  validator="positiveInteger,4"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-right: 5px; padding-left: 10px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Tax Amt"
                                  :isRequired="true"
                                  :isDisabled="viewMode || true"
                                  textAlign="right"
                                  :inputValue.sync="dpTaxAmt"
                                  :errorMessage.sync="dpTaxAmtErr"
                                  validator="decimal,14,4"
                                />
                                <Input
                                  placeholder="Net Inclusive Tax"
                                  :isRequired="true"
                                  :isDisabled="viewMode || true"
                                  textAlign="right"
                                  :inputValue.sync="dpTotalPriceInclTax"
                                  :errorMessage.sync="dpTotalPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                              </div>
                            </div>
                            <div class="foreign-currency-column" style="padding-left: 5px">
                              <div class="foreign-currency-row">
                                <Input
                                  placeholder="Tax Amt"
                                  :isRequired="true"
                                  :isDisabled="viewMode || true"
                                  textAlign="right"
                                  :inputValue.sync="myrDPTaxAmt"
                                  :errorMessage.sync="myrDPTaxAmtErr"
                                  validator="decimal,14,4"
                                />
                                <Input
                                  placeholder="Net Inclusive Tax"
                                  :isRequired="true"
                                  :isDisabled="viewMode || true"
                                  textAlign="right"
                                  :inputValue.sync="myrDPTotalPriceInclTax"
                                  :errorMessage.sync="myrDPTotalPriceInclTaxErr"
                                  validator="decimal,14,4"
                                />
                              </div>
                            </div>
                          </div>
                        </BoxContainer>
                      </div>
                      <div class="foreign-currency-background">
                        <div class="background-left"></div>
                        <div class="background-middle"></div>
                        <div class="background-right"></div>
                      </div>
                    </div>
                  </div>
                  <div v-if="sasFieldsControl['cmdDirectGST']">
                    <BoxContainer title="GST Details" :isCollapsible="true" :isCollapsed.sync="gstDetailsCollapse">
                      <Input
                        placeholder="Payee Name"
                        :inputValue.sync="gstPayeeName"
                        :isDisabled="viewMode"
                        :errorMessage.sync="gstPayeeNameErr"
                        validator="text,100"
                        ref="gstpayeename"
                      />
                      <Input
                        placeholder="Registration No."
                        :inputValue.sync="gstRegNo"
                        :isDisabled="viewMode"
                        :errorMessage.sync="gstRegNoErr"
                        validator="text,20"
                        ref="gstregno"
                      />
                      <Input
                        placeholder="GST License No."
                        :inputValue.sync="gstLicenseNo"
                        :isDisabled="viewMode"
                        :errorMessage.sync="gstLicenseNoErr"
                        validator="text,50"
                        ref="gstlicenseno"
                      />
                      <Input
                        placeholder="Invoice No."
                        :inputValue.sync="gstInvNo"
                        :isDisabled="viewMode"
                        :errorMessage.sync="gstInvNoErr"
                        validator="text,25"
                        ref="gstinvno"
                      />
                      <BoxContainer
                        title="Invoice Date"
                        :isCheckBox="true"
                        :isChecked.sync="gstInvDateChecked"
                        :checkBoxIsDisabled="viewMode"
                      >
                        <DateTimePicker
                          :isRequired="gstInvDateChecked"
                          :showTime="false"
                          placeholder="Invoice Date"
                          :isDisabled="!gstInvDateChecked || viewMode"
                          :value.sync="gstInvDate"
                          :errorMessage.sync="gstInvDateErr"
                          ref="gstinvdate"
                        />
                      </BoxContainer>
                    </BoxContainer>
                  </div>
                </div>
              </div>
            </div>

            <!-- Attachments Tab -->
            <div slot="Attachment" class="tab-slot">
              <div class="tab-details-styling" style="width: 100%">
                <div style="display: flex; justify-content: end; margin-top: 5px">
                  <button v-if="!viewMode" @click="openAttachmentPopup('new')" style="margin-right: 10px">
                    Add Attachment
                  </button>
                  <button
                    v-if="!viewMode && attachmentsList.length > 0"
                    @click="openAttachmentPopup('edit')"
                    style="margin-right: 10px"
                  >
                    Edit Attachment
                  </button>
                  <button
                    v-if="!viewMode && attachmentsList.length > 0"
                    @click="deleteDocClicked"
                    style="margin-right: 10px"
                  >
                    Del Attachment
                  </button>
                </div>
                <TableBox
                  v-if="showAttTable"
                  title="Attachment"
                  :tableData="attachmentsList"
                  :selectedRow.sync="selectedAtt"
                  :additionalButtons="attAdditionalBtns"
                  @buttonClicked="attAdditionalBtnsClicked"
                  :hideColumns="[
                    'Action',
                    'Doc Image',
                    'Sequence No',
                    'Document GUID',
                    'ID',
                    'Doc Ref No',
                    'Job No',
                    'Doc Code',
                    'Doc Type',
                    'Doc Rec Date',
                    'Doc Rec Time',
                    '_attachmentAction',
                  ]"
                />
              </div>
            </div>

            <!-- Events Tab -->
            <div slot="Events" class="tab-slot">
              <div class="tab-details-styling" style="width: 100%">
                <div style="margin-bottom: 5px">
                  <BoxContainer title="Show Details">
                    <div style="display: flex">
                      <CheckBoxV2 :isChecked.sync="showGLEntry" title="GL Entry" />
                      <!-- <CheckBoxV2 :isChecked.sync="showOffsetDocs" title="Offset Documents" /> -->
                      <CheckBoxV2 :isChecked.sync="showeInvoice" title="eInvoice Submission" />
                    </div>
                  </BoxContainer>
                </div>
                <div style="display: flex; flex-direction: row; margin-top: 10px">
                  <BoxContainer
                    v-if="showGLEntry"
                    title="GL Entry"
                    :style="{
                      width: '100%',
                      marginTop: '0px',
                      marginRight: showGLEntry && !showOffsetDocs && !showeInvoice ? '' : '5px',
                    }"
                  >
                    <div>
                      <TableBox :tableData="glEntryList" :maxSelection="0" :dataType="glDataType" />
                    </div>
                    <div style="display: flex; justify-content: space-around; margin-top: 10px">
                      <div style="display: flex">
                        <div style="font-size: 1em; align-content: center"> TOTAL DEBIT </div>
                        <div class="total-block" style="margin-left: 5px">
                          {{ totalDebit }}
                        </div>
                      </div>

                      <div style="display: flex">
                        <div style="font-size: 1em; align-content: center"> TOTAL CREDIT </div>
                        <div class="total-block" style="margin-left: 5px">
                          {{ totalCredit }}
                        </div>
                      </div>
                    </div>
                  </BoxContainer>
                  <BoxContainer
                    v-if="showOffsetDocs"
                    title="Offset Documents"
                    :style="{
                      width: '100%',
                      marginTop: '0px',
                      marginRight: showOffsetDocs && !showGLEntry && !showeInvoice ? '' : '5px',
                    }"
                  >
                    <TableBox title="Offset Documents" :tableData="offsetDocsList" :maxSelection="0" />
                  </BoxContainer>
                  <BoxContainer v-if="showeInvoice" title="LHDN Submission" style="width: 100%; margin-top: 0px">
                    <TableBox title="eInvoice Submission" :tableData="eInvoiceList" :maxSelection="0" />
                  </BoxContainer>
                </div>
              </div>
            </div>
          </Tab>
        </div>
      </div>

      <!-- Total Payment and Total Outstanding -->
      <BoxContainer>
        <div style="display: flex; flex-direction: row; justify-content: flex-end">
          <div style="display: flex; margin-right: 20px">
            <div style="align-content: center; margin-right: 10px; font-size: 1.1em"> Total Payment </div>
            <div style="display: flex">
              <div
                class="total-block"
                style="font-size: 1.1em; text-align: right; display: inline; padding-right: 5px; min-width: 210px"
              >
                {{
                  multiCurr ? 'N/A' : (
                    `${selectedHeaderCurr ? selectedHeaderCurr['Currency Code'] : 'MYR'} ${totalPaymentFAmt}`
                  )
                }}
              </div>
              <div
                class="total-block"
                style="
                  background-color: #467cdc;
                  color: white;
                  font-size: 1.1em;
                  font-weight: bold;
                  text-align: right;
                  display: inline;
                  padding-right: 5px;
                  min-width: 210px;
                "
              >
                MYR {{ totalPaymentAmt }}
              </div>
            </div>
          </div>
          <div style="display: flex">
            <div style="align-content: center; margin-right: 10px; font-size: 1.1em"> Total Outstanding </div>
            <div style="display: flex">
              <div
                class="total-block"
                style="font-size: 1.1em; text-align: right; display: inline; padding-right: 5px; min-width: 210px"
              >
                {{
                  multiCurr ? 'N/A' : (
                    `${selectedHeaderCurr ? selectedHeaderCurr['Currency Code'] : 'MYR'} ${totalFOutstanding}`
                  )
                }}
              </div>
              <div
                class="total-block"
                style="
                  background-color: #467cdc;
                  color: white;
                  font-size: 1.1em;
                  font-weight: bold;
                  text-align: right;
                  display: inline;
                  padding-right: 5px;
                  min-width: 210px;
                "
              >
                MYR {{ totalOutstanding }}
              </div>
            </div>
          </div>
        </div>
      </BoxContainer>
    </PopupEntry>

    <PopupV2
      :show="showDeletePayment"
      title="Delete Payment"
      @buttonClicked="deletePopupClicked"
      :buttons="deletePopupButtons"
    >
      <div>
        Please state reason to delete
        {{ selectedPaymentListing.length > 0 ? selectedPaymentListing[0]['Payment No'] : '' }}?
      </div>
      <Input
        placeholder="Delete Remarks"
        :inputValue.sync="deletePaymentRemarks"
        :errorMessage.sync="deletePaymentRemarksErr"
        :isRequired="true"
        validator="text,100"
      />
    </PopupV2>

    <PopupV2 :show="showEnterPswd" title="Enter Password" @buttonClicked="pswdPopupClicked" :buttons="pswdPopupButtons">
      <div>Please enter password to proceed:</div>
      <Input
        placeholder="Job Closed Password"
        :inputValue.sync="jobclosepswd"
        :errorMessage.sync="jobclosepswdErr"
        textType="password"
        :isRequired="true"
        validator="text,100"
      />
    </PopupV2>

    <div class="attachments-popup-container">
      <PopupV2
        class="attachments-popup"
        :show="showAttachmentsPopup"
        :title="attachmentsPopupTitle"
        :buttons="attachmentsPopupButtons"
        @buttonClicked="attachmentsPopupBtnsClicked"
      >
        <div class="attachments-popup-content">
          <Input
            textType="file"
            placeholder="Select File"
            ref="docFile"
            :isDisabled="viewMode"
            :inputValue.sync="documents['docName']"
            @fileChanged="handleFileUpload"
            :errorMessage.sync="attachmentsRequiredErrorMessage"
          />
          <div v-if="attachmentsAction == 'E'" class="attached-file">
            <strong>Attached file: </strong>
            <span
              class="file-link"
              title="Click to view"
              @click="
                projectAttachment({
                  'File Name': selectedAtt.length > 0 ? selectedAtt[0]['File Name'] : '',
                  'MIME Type': null,
                  'File Data': selectedAtt.length > 0 ? selectedAtt[0]['Doc Image'] : '',
                })
              "
            >
              {{ selectedAtt.length > 0 ? selectedAtt[0]['File Name'] : '' }}
            </span>
          </div>
          <RemarksBox
            :maxLength="200"
            style="height: 50%"
            title="Remarks"
            :isDisabled="viewMode"
            :text.sync="documents['remarks']"
          />
        </div>
      </PopupV2>
    </div>
    <div v-if="showAttachmentProjector" class="attachment-projector">
      <div class="attachment-projection">
        <object
          v-if="isProjectable"
          :data="attachmentProjectorData"
          :type="attachmentProjectorType"
          draggable="false"
        ></object>
        <div v-else class="attachment-not-projectable-container">
          <span>
            {{ selectedAtt[0]['File Type'].replaceAll('.', '').toUpperCase() }}
            files cannot be previewed. <br />
            Please download the file and view it instead.
          </span>
          <button @click="downloadAttachment">
            <img src="https://dsmlib.logistics.systems/Icons/Download.png" alt="Download icon" />
            Download
          </button>
        </div>
      </div>
      <div class="close-button" @click="closeProjector">&times;</div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    // whether to disable listing filter
    searchJobChargesFilterDisable: function () {
      return (
        !this.selectedPayeeFilterJC &&
        !this.filterJobByAsAt &&
        !this.selectedJobNoSearch &&
        this.selectedChargeCodeSearch
      );
    },
    // Select/Deselect All button text for "Select from AP Invoice"
    selectDeselectAP: function () {
      return this.APInvoiceListing.length > 0 && this.selectedAPInvoices.length === this.APInvoiceListing.length ?
          'Deselect'
        : 'Select';
    },
    // Select/Deselect All button text for "Select from Job Charges"
    selectDeselectJC: function () {
      return this.jobChargesListing.length > 0 && this.selectedJobCharges.length === this.jobChargesListing.length ?
          'Deselect'
        : 'Select';
    },
    // To populate the # of containers entered into the filter box
    contSearchCount: function () {
      let containers = [];
      if (this.searchContNo.trim().length > 0) {
        containers = this.searchContNo.trim().split('\n');
        containers = containers.filter((cont) => {
          cont.trim() != '' || cont.trim() != ',';
        });
      }
      return containers.length;
    },
    // Select/Deselect All button text for "Select from Expenses"
    selectDeselectExpenses: function () {
      return this.expensesListing.length > 0 && this.selectedExpenses.length === this.expensesListing.length ?
          'Deselect'
        : 'Select';
    },

    disableCheque: function () {
      return (
        this.viewMode || !this.allowChq ||
        (this.selectedPaymentMethod && ['LG', 'CS'].includes(this.selectedPaymentMethod['Payment Type'])) ||
        (this.action != 'N' && this.paymentDetails?.Indicator == 'P') 
      );
    },
  },
  data: function () {
    return {
      menu: [
        {
          title: 'New Payment',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png',
          enabled: false,
          key: 'mnuNew',
        },
        {
          title: 'Edit Payment',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          enabled: false,
          key: 'mnuEdit',
        },
        {
          title: 'Delete Payment',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png',
          enabled: false,
          key: 'mnuDelete',
        },
        {
          title: 'View Payment',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png',
          enabled: false,
        },
        {
          title: 'Duplicate Payment',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png',
          enabled: false,
          key: 'mnuCopy',
        },
        {
          title: 'Authorize',
          icon: 'https://dsmlib.logistics.systems/Icons/Approve.png',
          enabled: false,
          key: 'mnAuthorized',
        },
        {
          title: 'Print Payment Voucher',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          enabled: true,
          key: 'mnuPrintPaymentVoucher',
        },
      ],
      sasFieldsControl: {
        chkPrivate: true, // hide pnc checkbox
        cmdDirectGST: true, // hide gst details field
        TxtTaxRate: true, // disable tax rate field
        TxtTaxRateDP: true, // disable tax rate field in direct posting
        cmdTaxCodeDP: true, // disable query for tax code
        TxtTaxCodeDP: true, // disable tax code field in direct posting
        cmdTaxCode: true, // disable query for tax code
        TxtTaxCode: true, // disable tax code field in direct posting
      },

      viewMode: false,
      action: '',
      currentTabIndex: 0,
      tabsItems: [
        { title: 'Job Charges / AP Invoices', disabled: false },
        { title: 'Direct Posting', disabled: false },
        { title: 'Attachment', disabled: false },
        { title: 'Events', disabled: false }, // hide IRBM section
      ],
      settingsJobClosePswd: '',

      filterText: '',
      mainFilterIsOpen: false,
      mainFilterIsDisabled: false,
      filterPaymentByDate: true,
      paymentListFromDate: new Date(),
      fromDateErrMsg: '',
      paymentListToDate: new Date(),
      toDateErrMsg: '',
      lastRefreshed: '',

      filterPaymentBySearch: false,
      searchFilterItems:[
        {'Search By': 'Payment No', key: 'PV'},
      ],
      searchByValue: null,
      searchByErrMsg: '',
      searchCriteriaItems: [
        { name: 'EQUALS' }, 
        { name: 'STARTS WITH' }, 
        { name: 'CONTAINS' }, 
        { name: 'ENDS WITH' }
      ],
      selectedFilterSearchCriteria: null,
      searchCriteriaErrMsg: '',
      searchInputValue: '',
      searchInputErrMsg: '',

      paymentListing: [],
      selectedPaymentListing: [],
      paymentTableBoxLoading: false,

      showPopup: false,
      popupTitle: '',
      createdBy: '',
      updatedBy: '',
      summaryIsCollapsed: false,
      summaryItems: [
        {
          Title: 'Indicator',
          Label: ['New', 'Cancelled', 'Paid', 'Cancel from Cheque'],
          Indicator: [
            { code: 'N', name: 'New Payment', bg: '#ffff99' },
            { code: 'C', name: 'Cancelled', bg: 'FFFFFF' },
            { code: 'P', name: 'Paid', bg: '#79d279' },
            { code: 'CA', name: 'Cancel from Cheque', bg: 'FFFFFF' },
          ],
          indicatorToShow: '',
          bgcolorToShow: '',
          visible: true,
        },
        {
          Title: 'Authorized',
          Label: ['Authorized', 'Unauthorize'],
          Indicator: [
            { code: 'A', name: 'Authorized', bg: '#79d279' },
            { code: 'N', name: 'Unauthorize', bg: '#ffff99' },
          ],
          indicatorToShow: '',
          bgcolorToShow: '',
          visible: true,
        },
        {
          Title: 'Statement No',
          Label: [],
          Indicator: [{ code: 'Statement No', name: '' }],
          indicatorToShow: '',
          bgcolorToShow: '#79d279',
          visible: false,
        },
        {
          Title: 'LHDN Submission',
          Label: ['LHDN Queue', 'LHDN Sent', 'LHDN Accepted', 'LHDN Rejected', 'LHDN Cancelled', 'LHDN Pending'],
          Indicator: [
            { code: 'Q', name: 'LHDN Queue', bg: '#ffff99' },
            { code: 'S', name: 'LHDN Sent', bg: '#ffff99' },
            { code: 'A', name: 'LHDN Accepted', bg: '#79d279' },
            { code: 'R', name: 'LHDN Rejected', bg: '#ff8787' },
            { code: 'C', name: 'LHDN Cancelled', bg: 'FFFFFF' },
            { code: 'P', name: 'LHDN Pending', bg: '#ffff99' },
          ],
          indicatorToShow: '',
          bgcolorToShow: '',
          visible: true,
        },
        {
          Title: 'PV Count',
          Src: 'https://dsmlib.logistics.systems/Icons/Print.png',
          Label: ['Print Count'],
          Indicator: [{ code: '0', name: 'Print Count' }],
          indicatorToShow: '',
          bgcolorToShow: '',
          visible: true,
        },
      ],
      newLegend: {
        // eslint-disable-next-line vue/no-unused-properties -- used in template
        Completed: '#79d279',
        // eslint-disable-next-line vue/no-unused-properties -- used in template
        Pending: '#ffff99',
      },
      headerIsCollapsed: false,
      bankIsCollapsed: false,

      requiredError: [],
      successMessages: [],
      errorMessages: [],
      paymentDetails: [],

      showDeletePayment: false,
      deletePopupButtons: [
        { text: 'Delete', color: 'indianred', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],
      deletePaymentRemarks: '',
      deletePaymentRemarksErr: '',

      //#region header details
      disablePaymentNo: true,
      paymentNo: '',
      paymentNoErr: '',

      paymentTypeList: [],
      selectedPaymentType: null,
      paymentTypeErr: '',

      payeeTypeList: [],
      selectedPayeeType: null,
      payeeTypeErr: '',

      payeeCodeNameDisabled: true,
      payeeCodeKey: '',
      payeeCodeDescKey: '',
      payeeCodeList: [],
      selectedPayeeCode: null,
      payeeCodeErr: '',

      payeeName: '',
      payeeNameErr: '',

      paymentDate: null,
      paymentDateErr: '',

      docRefNo: '',
      docRefNoErr: '',

      docTypeList: [],
      selectedDocType: null,
      docTypeErr: '',

      selfBilledOptions: [
        //hard code for now
        { Type: 'Self-Billed Invoice', APIKey: '1' },
        { Type: 'Self-Billed Debit Note', APIKey: '2' },
        // {Type: "Self-Billed Credit Note", APIKey: '3'},
        // {Type: "Self-Billed Refund Note", APIKey: '4'}
      ],
      selectedSelfBilledType: null,
      selfBilledErr: '',

      paymentMethodList: [],
      selectedPaymentMethod: null,
      paymentMethodErr: '',

      oriBankList: [],
      bankList: [],
      bankRequired: false,
      selectedBank: null,
      bankErr: '',
      defaultCHBank: '',
      defaultCSBank: '',

      createCheque: false,
      chequeGiroNo: '',
      chequeGiroErr: '',
      mandatoryChq: false,
      allowChq: false,

      isAdvancePayment: false,
      isPnC: false,

      selectedHeaderCurr: null,
      headerCurrErr: '',
      oldRate: '',
      headerExchRate: '',
      headerExchRateErr: '',
      headerExchFactor: '',

      remarks: '',

      totalPaymentAmt: '0.00',
      totalPaymentFAmt: '0.00',
      totalOutstanding: '0.00',
      totalFOutstanding: '0.00',
      multiCurr: false,
      //#endregion

      //#region bank details
      bankName: '',
      bankAccNo: '',
      bankSwift: '',
      coRegNo: '',
      //#endregion

      taxCodeList: [],

      //#region job charges tab
      showEnterPswd: false,
      pswdPopupButtons: [
        { text: 'OK', color: 'green', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],
      jobclosepswd: '',
      jobclosepswdErr: '',

      totalTaxCharges: '0.00',
      totalAmountCharges: '0.00',
      totalFAmountCharges: '0.00',
      showJobChargesTable: true,
      jobChargesDetailsList: [],
      deletedJobChargesDetails: [],
      jobChargesDataType: {
        '#': 'number',
        'AP Inv No': 'string',
        'Job No': 'string',
        'Cont No': 'string',
        'Spp Inv No': 'string',
        'Charges Code': 'string',
        Amount: 'currency',
        'Tax Code': 'string',
        'Tax Rate': 'currency',
        'Tax Amt': 'currency',
        'Net Inclusive Tax': 'currency',
        'F.Amount': 'currency',
        'F.Tax Amt': 'currency',
        'F.Net Inclusive Tax': 'currency',
        Currency: 'string',
        Rate: 'currency',
        'User ID': 'string',
        'Date Update': 'date',
        Remark: 'string',
        'Seq No': 'number',
        Qty: 'number',
        UOM: 'string',
        'Rebate Amt': 'currency',
        'Cost Code': 'string',
        'SMK No': 'string',
        'Unit Price': 'currency',
        'F.Unit Price': 'currency',
        'Ref #': 'string',
      },
      tabTableButtons: [{ text: 'Edit', index: 0, width: 80 }],
      selectedJobChargeEntry: [],

      showAPInvoices: false,
      oriAPInvoiceListing: [],
      APInvoiceListing: [],
      selectedAPInvoices: [],
      apinvDataType: {
        'Inv Date': 'date',
        'Doc Date': 'date',
        'Payment Amount': 'currency',
        'Total Amount': 'currency',
        Balance: 'currency',
        Rate: 'currency',
        'F.Total Amount': 'currency',
        'F.Balance': 'currency',
      },
      showJobCharges: false,
      jobNoList: [],
      jobnoRowLimit: '1000',
      containerList: [],
      contnoRowLimit: '1000',
      expenseCodeList: [],
      costCodeList: [],

      //#region job charges filter
      jobChargesFilterOpen: false,
      jobChargesFilterBtnDisabled: false,
      filterJobByAsAt: false,
      asAtDate: null,
      asAtDateErr: '',
      selectedJobNoSearch: null,
      selectedChargeCodeSearch: null,
      searchContNo: '',
      payeeFilterSelection: [
        { label: 'Selected Payee', keyValue: 'selected' },
        { label: 'All Payee Type', keyValue: 'all' },
      ],
      selectedPayeeFilterJC: null,
      //#endregion

      jobChargesTableBoxLoading: false,
      oriJobChargesListing: [],
      jobChargesListing: [],
      selectedJobCharges: [],
      uomList: [],
      currencyList: [],
      custCreditOpen: false,
      custCreditDisable: true,
      billCustName: '',
      billCreditDetails: null,

      //#region new job charge
      subtitleJC: '',
      billedJob: false,
      saveJCAction: '',
      chargesAmtInputDisable: true,
      showInputCharges: false,
      selectedJobNo: null,
      selectedContainer: null,
      selectedCharges: null,
      chargesErr: '',
      chargeCodeRequired: true,
      jobChargeRemarks: '',
      selectedJCCostCode: null,
      smkNoJC: '',
      smkNoJCErr: '',
      jobChargeRefNo: '',
      jobChargeRefNoErr: '',
      enableVendorRebate: true,
      chargesRebate: '',
      chargesRebateErr: '',
      chargesQty: '',
      chargesQtyErr: '',
      selectedChargesUOM: null,
      selectedChargesCurrency: null,
      chargesCurrencyErr: '',
      chargesUPrice: '',
      chargesUPriceErr: '',
      chargesTotalPrice: '',
      chargesTotalPriceErr: '',
      chargesExchangeRate: '',
      chargesExchangeRateErr: '',
      chargesFactor: '',
      chargeExchgRateDisabled: false,
      myrChargesUPrice: '',
      myrChargesUPriceErr: '',
      myrChargesTotalPrice: '',
      myrChargesTotalPriceErr: '',
      selectedChargesTaxCode: null,
      chargesTaxRate: '',
      chargesTaxAmt: '',
      chargesTaxAmtErr: '',
      chargesUPriceInclTax: '',
      chargesUPriceInclTaxErr: '',
      chargesTotalPriceInclTax: '',
      chargesTotalPriceInclTaxErr: '',
      myrChargesTaxAmt: '',
      myrChargesTaxAmtErr: '',
      myrChargesUPriceInclTax: '',
      myrChargesUPriceInclTaxErr: '',
      myrChargesTotalPriceInclTax: '',
      myrChargesTotalPriceInclTaxErr: '',
      jcMultiCurr: false,
      //#endregion
      //#endregion

      //#region direct posting tab
      totalTaxDP: '0.00',
      totalDPAmt: '0.00',
      totalDPFAmt: '0.00',
      showDirectPostingTable: true,
      directPostingDetailsList: [],
      deletedDirectPostingDetails: [],
      directPostingDataType: {
        // available data type string, number, date, currency
        '#': 'number',
        Debit: 'string',
        'Dr. Acc. Desc': 'string',
        Amount: 'currency',
        'Tax Dr.Acc.': 'string',
        'Tax Code': 'string',
        'Tax Rate': 'number',
        'Taxable Amt': 'currency',
        'Net Inclusive Tax': 'currency',
        'F.Amount': 'currency',
        'F.Taxable Amt': 'currency',
        'F.Net Inclusive Tax': 'currency',
        Currency: 'string',
        Rate: 'currency',
        UserID: 'string',
        'Date Update': 'date',
        Remark: 'string',
        'Cost Code': 'string',
        'Vehicle No.': 'string',
        'SMK.No.': 'string',
        'Payee Name': 'string',
        'Registration No.': 'string',
        'GST License No.': 'string',
        'Invoice No.': 'string',
        'Invoice Date': 'date',
      },
      selectedDirectPostingEntry: [],
      dpMultiCurr: false,

      showExpenses: false,
      oriExpensesListing: [],
      expensesListing: [],
      expensesTableLoading: false,
      selectedExpenses: [],
      vehicleNoList: [],
      expensesDocRefNoList: [],
      //#region expenses filter
      expensesFilterOpen: false,
      expensesFilterBtnDisabled: false,
      filterExpensesByDate: true,
      filterExpenseFromDate: new Date(),
      filterExpenseFromDateErr: '',
      filterExpenseToDate: new Date(),
      filterExpenseToDateErr: '',
      selectedExpenseCode: null,
      selectedExpenseVehicle: null,
      expenseJobNo: '',
      expenseJobNoErr: '',
      expenseCashVoucher: '',
      expenseCashVoucherErr: '',
      selectedExpensesRefNo: null,
      gstInclusive: false,
      //#endregion
      showInputDirectPosting: false,
      debitAccList: [],
      selectedDebitAcc: null,
      selectedDebitAccErr: '',
      selectedTaxDebitAcc: null,
      selectedTaxDebitAccErr: '',
      directPostingRemarks: '',
      selectedDPCostCode: null,
      smkNoDP: '',
      smkNoDPErr: '',
      selectedDPVehicleNo: null,

      //#region input direct posting
      subtitleDP: '',
      saveDPAction: '',
      selectedDPCurrency: null,
      dpCurrencyErr: '',
      dpTotalPrice: '',
      dpTotalPriceErr: '',
      dpExchangeRate: '',
      dpExchangeRateErr: '',
      dpExchgRateDisabled: false,
      myrDPTotalPrice: '',
      myrDPTotalPriceErr: '',
      selectedDPTaxCode: null,
      dpTaxRate: '',
      dpTaxAmt: '',
      dpTaxAmtErr: '',
      dpTotalPriceInclTax: '',
      dpTotalPriceInclTaxErr: '',
      myrDPTaxAmt: '',
      myrDPTaxAmtErr: '',
      myrDPTotalPriceInclTax: '',
      myrDPTotalPriceInclTaxErr: '',

      gstDetailsCollapse: true,
      gstPayeeName: '',
      gstPayeeNameErr: '',
      gstRegNo: '',
      gstRegNoErr: '',
      gstLicenseNo: '',
      gstLicenseNoErr: '',
      gstInvNo: '',
      gstInvNoErr: '',
      gstInvDateChecked: false,
      gstInvDate: new Date(),
      gstInvDateErr: '',
      //#endregion
      //#endregion

      //#region Attachments data
      showAttTable: true,
      attachmentsList: [],
      deletedAttList: [],
      selectedAtt: [],
      attAdditionalBtns: [{ text: 'View', index: 0, width: 60 }],
      showAttachmentsPopup: false,
      attachmentsPopupTitle: '',
      attachmentsPopupButtons: [],
      documents: {
        docName: '',
        attachmentFile: [],
        filetype: '',
        docGuid: '',
        remarks: '',
        docID: 0,
      },
      oldDoc: '',
      attachmentsAction: '', // 'N', 'E', 'V'
      attachmentsRequiredErrorMessage: '',
      showAttachmentProjector: false,
      isProjectable: false,
      attachmentProjectorFileName: '',
      attachmentProjectorData: '',
      attachmentProjectorType: '',
      //#endregion

      //#region Events tab
      showGLEntry: true,
      showOffsetDocs: false,
      showeInvoice: false,

      glEntryList: [],
      totalDebit: '0.0000',
      totalCredit: '0.0000',
      glDataType: {},
      offsetDocsList: [],
      eInvoiceList: [],
      //#endregion
    };
  },
  props: {},
  methods: {
    menuClicked: async function (e) {
      this.clearMessages();
      this.clearAllData();
      this.mainFilterIsOpen = false;
      this.currentTabIndex = 0;

      switch (e['title']) {
        case 'New Payment': {
          this.action = 'N';
          this.saveJCAction = 'N';
          this.saveDPAction = 'N';
          this.popupTitle = e['title'];
          this.paymentNo = this.disablePaymentNo ? '**********' : '';
          this.paymentDate = new Date();
          this.filterExpenseFromDate = new Date();
          this.filterExpenseToDate = new Date();
          this.selectedHeaderCurr = this.currencyList.find((curr) => curr['Currency Code'] == 'MYR');
          this.selectedPayeeFilterJC = this.payeeFilterSelection[0];
          if (this.paymentTypeList.length == 1) {
            this.selectedPaymentType = this.paymentTypeList[0];
            this.viewMode = false;
          } else {
            this.viewMode = true;
          }
          this.showPopup = true;
          break;
        }

        case 'Edit Payment': {
          if (this.selectedPaymentListing.length == 0) {
            this.$showAlert('error', 'Edit Payment', 'Please select a payment to edit');
            return;
          }

          if (this.selectedPaymentListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Payment Already Cancelled',
              'You are not allowed to edit cancelled payment!',
            );
            return;
          }

          if (['Queue', 'Sent', 'Accepted'].includes(this.selectedPaymentListing[0]['LHDN Status'])) {
            this.$showAlert('error', 'Selected Payment Already Submitted', 'You are not allowed to edit submitted eInvoice');
            return;
          }

          this.$fullscreenLoading('Checking Payment Editable');
          const isEditable = await this.$apiCallV2('/Payment/CheckPaymentEditable', {
            chequeno: this.selectedPaymentListing[0]['Cheque No'],
            paymentno: this.selectedPaymentListing[0]['Payment No'],
          });
          this.$fullscreenLoading();

          if (isEditable[0]['Results'] == '1') {
            this.$showAlert('error', 'Cannot Edit Selected Payment', isEditable[0]['ErrMsg']);
            return;
          }

          this.action = 'E';
          this.viewMode = false;
          await this.getPayment(e['title']);
          this.filterExpenseFromDate = new Date();
          this.filterExpenseToDate = new Date();
          this.selectedPayeeFilterJC = this.payeeFilterSelection[0];

          await this.refreshClicked('Expenses');
          this.updateSummaryOfEvents();
          this.showPopup = true;
          break;
        }

        case 'Delete Payment': {
          if (this.selectedPaymentListing.length == 0) {
            this.$showAlert('error', 'Delete Payment', 'Please select a payment to delete');
            return;
          }

          if (this.selectedPaymentListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Payment Already Cancelled',
              'You are not allowed to delete cancelled payment!',
            );
            return;
          } else if (this.selectedPaymentListing[0]['Indicator'] == 'P') {
            this.$showAlert('error', 'Selected Payment Already Paid', 'You are not allowed to delete paid payment!');
            return;
          }

          if (['Queue', 'Sent', 'Accepted'].includes(this.selectedPaymentListing[0]['LHDN Status'])) {
            this.$showAlert('error', 'Selected Payment Already Submitted', 'You are not allowed to delete submitted eInvoice');
            return;
          }

          this.action = 'D';
          this.deletePaymentRemarks = '';
          this.deletePaymentRemarksErr = '';
          this.showDeletePayment = true;

          break;
        }

        case 'View Payment':
          if (this.selectedPaymentListing.length == 0) {
            this.$showAlert('error', 'View Payment', 'Please select a payment to view');
            return;
          }
          this.action = '';
          this.viewMode = true;
          await this.getPayment(e['title']);
          this.updateSummaryOfEvents();
          this.showPopup = true;
          break;

        case 'Duplicate Payment': {
          if (this.selectedPaymentListing.length == 0) {
            this.$showAlert('error', 'Duplicate Payment', 'Please select a payment to duplicate');
            return;
          }
          this.$fullscreenLoading('Duplicating Payment');
          this.paymentDetails = (
            await this.$apiCallV2('/Master/GetDetailsMaster', {
              masterfile: 'payment',
              recordid: this.selectedPaymentListing[0]['Payment No'],
            })
          )['Payment'];
          await this.populatePaymentHeader();
          // await this.payTypeSelected(this.selectedPaymentType);
          this.$fullscreenLoading();
          this.paymentDetails = [];
          this.viewMode = false;
          this.action = 'N';
          this.saveJCAction = 'N';
          this.saveDPAction = 'N';
          this.popupTitle = 'New Payment';
          this.paymentNo = this.disablePaymentNo ? '**********' : '';
          this.payeeCodeNameDisabled = false;
          this.filterExpenseFromDate = new Date();
          this.filterExpenseToDate = new Date();
          await this.refreshClicked('Expenses');
          this.showPopup = true;
          break;
        }

        case 'Authorize':
        case 'Unauthorize': {
          if (this.selectedPaymentListing.length == 0) {
            this.$showAlert('error', 'Authorize Payment', 'Please select a payment to authorize');
            return;
          } else if (this.selectedPaymentListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Payment Already Cancelled',
              `You are not allowed to ${e['title'].toLowerCase()} cancelled payment!`,
            );
            return;
          }

          this.$fullscreenLoading(`${e['title'] == 'Authorize' ? 'Authorizing' : 'Unauthorizing'} Payment`);
          const dataToPost = {
            action: e['title'] == 'Authorize' ? 'A' : 'U', //A-auth, U-Unauth
            docrefname: 'payment voucher',
            docrefid: this.selectedPaymentListing[0]['Payment No'],
            date: this.selectedPaymentListing[0]['Payment Date'],
          };
          const authorizeResult = await this.$apiCallV2('/Payment/Authorization', dataToPost);
          this.$fullscreenLoading();

          if (authorizeResult[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              `${e['title']}d!`,
              `Payment ${this.selectedPaymentListing[0]['Payment No']} was successfully ${e['title']}d.`,
            );
            this.paymentListing = [];
            this.selectedPaymentListing = [];
            this.$nextTick(async () => await this.refreshClicked('PaymentList'));
          } else {
            this.$showAlert('error', `${e['title']} Unsuccessful`, `${authorizeResult[0]['ErrMsg']}`);
            this.paymentListing = [];
            this.selectedPaymentListing = [];
            this.$nextTick(async () => await this.refreshClicked('PaymentList'));
          }
          break;
        }

        case 'Print Payment Voucher': {
          if (this.selectedPaymentListing.length == 0) {
            this.openPrintPVPage();
          } else if (this.selectedPaymentListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Payment Already Cancelled',
              'You are not allowed to print cancelled payment!',
            );
          } else {
            this.openPrintPVPage(this.selectedPaymentListing[0]['Payment No']);
          }
          break;
        }
      }
    },

    // Clear all error messages
    clearMessages: function () {
      this.requiredError = [];
      this.successMessages = [];
      this.errorMessages = [];
      this.paymentNoErr = '';
      this.paymentTypeErr = '';
      this.payeeTypeErr = '';
      this.payeeCodeErr = '';
      this.payeeNameErr = '';
      this.paymentDateErr = '';
      this.docRefNoErr = '';
      this.docTypeErr = '';
      this.paymentMethodErr = '';
      this.bankErr = '';
      this.selfBilledErr = '';
      this.chequeGiroErr = '';
      this.headerCurrErr = '';
      this.headerExchRateErr = '';

      this.clearAllChargesError();
      this.clearAllDPError();
    },

    // Set all data in popup entry to initial value
    clearAllData: function () {
      this.currentTabIndex = 0;
      this.paymentDetails = [];
      this.paymentNo = '';
      this.selectedPaymentType = null;
      this.selectedPayeeType = null;
      this.payeeCodeList = [];
      this.payeeCodeKey = '';
      this.payeeCodeDescKey = '';
      this.selectedPayeeCode = null;
      this.payeeName = '';
      this.paymentDate = null;
      this.docRefNo = '';
      this.selectedDocType = null;
      this.selectedPaymentMethod = null;
      this.selectedBank = null;
      this.selectedSelfBilledType = null;
      this.createCheque = false;
      this.chequeGiroNo = '';
      this.isAdvancePayment = false;
      this.isPnC = false;
      this.remarks = '';
      this.selectedHeaderCurr = null;
      this.headerExchRate = '1.00';
      this.headerExchFactor = '1';

      this.bankName = '';
      this.bankAccNo = '';
      this.bankSwift = '';
      this.coRegNo = '';

      this.jobnoRowLimit = '1000';
      this.contnoRowLimit = '1000';

      this.jobChargesDetailsList = [];
      this.directPostingDetailsList = [];
      this.deletedJobChargesDetails = [];
      this.deletedDirectPostingDetails = [];

      this.jobNoList = [];
      this.selectedAPInvoices = [];
      this.selectedJobCharges = [];
      this.selectedPayeeFilterJC = null;
      this.filterJobByAsAt = false;
      this.selectedJobNoSearch = null;
      this.selectedChargeCodeSearch = null;
      this.searchContNo = '';
      this.selectedExpenses = [];

      this.showAPInvoices = false;
      this.showJobCharges = false;
      this.showInputCharges = false;
      this.showExpenses = false;
      this.showInputDirectPosting = false;

      this.filterExpensesByDate = true;

      this.attachmentsList = [];
      this.deletedAttList = [];
      this.selectedAtt = [];
      this.oldDoc = '';
      this.attachmentsAction = '';

      this.totalPaymentAmt = '0.00';
      this.totalPaymentFAmt = '0.00';
      this.totalOutstanding = '0.00';
      this.totalFOutstanding = '0.00';

      this.totalTaxCharges = '0.00';
      this.totalAmountCharges = '0.00';
      this.totalFAmountCharges = '0.00';

      this.totalTaxDP = '0.00';
      this.totalDPAmt = '0.00';
      this.totalDPFAmt = '0.00';

      this.clearAllChargesDetails();
      this.clearAllDirectPostingDetails();
      this.clearAllAttData();
    },

    // Set all charges data to initial value
    clearAllChargesDetails: function () {
      this.selectedJobNo = null;
      this.selectedCharges = null;
      this.selectedContainer = null;
      this.jobChargeRemarks = '';
      this.selectedJCCostCode = null;
      this.smkNoJC = '';
      this.jobChargeRefNo = '';
      this.chargesRebate = '';
      this.chargesQty = '';
      this.selectedChargesUOM = null;
      this.selectedChargesCurrency = null;
      this.chargesUPrice = '';
      this.chargesTotalPrice = '';
      this.chargesExchangeRate = '';
      this.chargesFactor = '';
      this.myrChargesUPrice = '';
      this.myrChargesTotalPrice = '';
      this.selectedChargesTaxCode = null;
      this.chargesTaxRate = '';
      this.chargesTaxAmt = '';
      this.chargesUPriceInclTax = '';
      this.chargesTotalPriceInclTax = '';
      this.myrChargesTaxAmt = '';
      this.myrChargesUPriceInclTax = '';
      this.myrChargesTotalPriceInclTax = '';

      this.custCreditDisable = true;
      this.billCustName = '';
      this.billCreditDetails = null;

      this.clearAllChargesError();
    },

    // Set all charges error to initial value
    clearAllChargesError: function () {
      this.requiredError = [];
      this.chargesErr = '';
      this.smkNoJCErr = '';
      this.jobChargeRefNoErr = '';
      this.chargesQtyErr = '';
      this.chargesCurrencyErr = '';
      this.chargesUPriceErr = '';
      this.chargesTotalPriceErr = '';
      this.chargesExchangeRateErr = '';
      this.myrChargesUPriceErr = '';
      this.myrChargesTotalPriceErr = '';
      this.chargesTaxAmtErr = '';
      this.chargesUPriceInclTaxErr = '';
      this.chargesTotalPriceInclTaxErr = '';
      this.myrChargesTaxAmtErr = '';
      this.myrChargesUPriceInclTaxErr = '';
      this.myrChargesTotalPriceInclTaxErr = '';
    },

    // Set all direct posting data to initial value
    clearAllDirectPostingDetails: function () {
      this.selectedDirectPostingEntry = [];
      this.selectedDebitAcc = null;
      this.selectedTaxDebitAcc = null;
      this.selectedDPCostCode = null;
      this.smkNoDP = '';
      this.selectedDPVehicleNo = null;
      this.directPostingRemarks = '';
      this.selectedDPCurrency = null;
      this.dpTotalPrice = '';
      this.dpExchangeRate = '';
      this.myrDPTotalPrice = '';
      this.selectedDPTaxCode = null;
      this.dpTaxRate = '';
      this.dpTaxAmt = '';
      this.dpTotalPriceInclTax = '';
      this.myrDPTaxAmt = '';
      this.myrDPTotalPriceInclTax = '';
      this.gstPayeeName = '';
      this.gstRegNo = '';
      this.gstLicenseNo = '';
      this.gstInvNo = '';
      this.gstInvDate = new Date();
      this.gstInvDateChecked = false;

      this.clearAllDPError();
    },

    // Set all direct posting error messages to initial value
    clearAllDPError: function () {
      this.requiredError = [];
      this.selectedDebitAccErr = '';
      this.selectedTaxDebitAccErr = '';
      this.smkNoDPErr = '';
      this.dpCurrencyErr = '';
      this.dpTotalPriceErr = '';
      this.dpExchangeRateErr = '';
      this.myrDPTotalPriceErr = '';
      this.dpTaxAmtErr = '';
      this.dpTotalPriceInclTaxErr = '';
      this.myrDPTaxAmtErr = '';
      this.myrDPTotalPriceInclTaxErr = '';
      this.gstInvDateErr = '';
    },

    // Set attachment data to initial value
    clearAllAttData: function () {
      this.documents = {
        docName: '',
        attachmentFile: [],
        filetype: '',
        docGuid: '',
        remarks: '',
        docID: 0,
      };
      this.attachmentsRequiredErrorMessage = '';
    },

    // Refresh click for payment listing, job, and expenses
    refreshClicked: async function (content) {
      let dataToPost = '';
      switch (content) {
        case 'PaymentList': {
          let paymentListingError = false;
          if (this.filterPaymentByDate) {
            if (!this.paymentListFromDate) {
              this.fromDateErrMsg = 'Enter from date!';
              paymentListingError = true;
            }

            if (!this.paymentListToDate) {
              this.toDateErrMsg = 'Enter to date!';
              paymentListingError = true;
            }
          }

          if (this.filterPaymentBySearch) {
            if (!this.searchByValue) {
              this.searchByErrMsg = 'Select search by!';
              paymentListingError = true;
            }

            if (!this.selectedFilterSearchCriteria) {
              this.searchCriteriaErrMsg = 'Select search criteria!';
              paymentListingError = true;
            }

            if (!this.searchInputValue.trim()) {
              this.searchInputErrMsg = 'Enter search value!';
              paymentListingError = true;
            }
          }

          if (paymentListingError) return;

          this.mainFilterIsOpen = false;
          dataToPost = {
            startdate: this.filterPaymentByDate ? this.paymentListFromDate.formatString('yyyy-MM-dd') : '',
            enddate: this.filterPaymentByDate ? this.paymentListToDate.formatString('yyyy-MM-dd') : '',
            searchfield: this.filterPaymentBySearch ? this.searchByValue['key'] : '',
            searchcriteria: this.filterPaymentBySearch ? this.selectedFilterSearchCriteria['name'] : '',
            searchvalue: this.filterPaymentBySearch ? this.searchInputValue : '',
          };

          const filterItems = [];
          if (this.filterPaymentByDate) {
            filterItems.push(
              `date from ${this.paymentListFromDate.formatString('yyyy-MM-dd')} to ${this.paymentListToDate.formatString('yyyy-MM-dd')}`,
            );
          }
          if (this.filterPaymentBySearch) {
            filterItems.push(
              `${this.searchByValue['Search By']} ${this.selectedFilterSearchCriteria['name']} ${this.searchInputValue}`,
            );
          }
          this.filterText = `Filter by ${filterItems.join(', ')}`;

          this.$fullscreenLoading('Getting Payment Listing');
          this.paymentTableBoxLoading = true;
          this.paymentListing = [];
          const paymentlist = await this.$apiCallV2('/Payment/GetPaymentListing', dataToPost);
          this.paymentListing = paymentlist;
          this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
          this.paymentTableBoxLoading = false;
          this.$fullscreenLoading();
          break;
        }

        case 'Job': {
          if (this.filterJobByAsAt && !this.asAtDate) {
            this.asAtDateErr = 'Enter As At Date!';
            return;
          }

          this.jobChargesFilterOpen = false;
          let contNoArr = '';
          if (this.searchContNo.trim().length > 0) {
            let containers = this.searchContNo.trim().replaceAll(',', '').split('\n');
            containers = containers.filter((cont) => cont.trim() != '');
            contNoArr = containers.join(',');
          }
          const showAllPayeeType = this.selectedPayeeFilterJC?.keyValue == 'all';

          dataToPost = {
            payeetype: showAllPayeeType ? '' : this.selectedPayeeType['Party Code'],
            payeecode: showAllPayeeType ? '' : this.selectedPayeeCode[this.payeeCodeKey],
            allpayeetype: showAllPayeeType ? '1' : '0',
            asat: this.filterJobByAsAt ? new Date(this.asAtDate).formatString('yyyy-MM-dd') : '',
            chargecode: this.selectedChargeCodeSearch ? this.selectedChargeCodeSearch['Expenses Code'] : '',
            containerno: contNoArr,
            jobno: this.selectedJobNoSearch ? this.selectedJobNoSearch['Job No'] : '',
          };

          this.$fullscreenLoading('Getting Job Charges');
          this.jobChargesTableBoxLoading = true;
          this.oriJobChargesListing = [];
          this.jobChargesListing = [];
          const joblist = await this.$apiCallV2('/Payment/GetPaymentJobChargesListing', dataToPost);
          this.oriJobChargesListing = joblist;
          this.jobChargesListing = this.filterJobChargesListing();
          this.updateJobChargesListStatus();
          this.jobChargesTableBoxLoading = false;
          this.$fullscreenLoading();
          break;
        }

        case 'Expenses': {
          this.expensesFilterOpen = false;
          dataToPost = {
            expcode: this.selectedExpenseCode ? this.selectedExpenseCode['Expense Code'] : '',
            startdate: this.filterExpensesByDate ? new Date(this.filterExpenseFromDate).formatString('yyyy-MM-dd') : '',
            enddate: this.filterExpensesByDate ? new Date(this.filterExpenseToDate).formatString('yyyy-MM-dd') : '',
            lorryno: this.selectedExpenseVehicle ? this.selectedExpenseVehicle['Lorry No'] : '',
            expdocrefno: this.selectedExpensesRefNo ? this.selectedExpensesRefNo['Ref No'] : '',
            jobno: this.expenseJobNo,
            cvdocno: this.expenseCashVoucher,
            chkinclgst: this.gstInclusive ? '1' : '0',
          };

          this.$fullscreenLoading('Getting Expenses');
          this.expensesTableLoading = true;
          this.oriExpensesListing = [];
          const explist = await this.$apiCallV2('/Payment/GetExpensesListing', dataToPost);
          this.oriExpensesListing = explist;
          this.expensesTableLoading = false;
          this.$fullscreenLoading();
          this.expensesListing =
            this.oriExpensesListing.length > 0 ? this.filterExpensesListing() : this.oriExpensesListing;
          break;
        }
      }
    },

    // Click on print button in popup entry
    popupPrintClicked: function () {
      this.openPrintPVPage(this.paymentNo);
    },

    // add label for billed and mtr job charges in job charges list
    updateJobChargesListStatus: function () {
      this.jobChargesListing = this.jobChargesListing.map((j) => ({
        Billed:
          j['Pending Billing Charges'] == 0 ?
            `<div height="15" width="15" style="background-color: green; padding: 5px; border-radius:5px; color: white; pointer-events: none">Billed</div>`
          : `<div height="15" width="15" style="background-color: orange; padding: 5px; border-radius:5px; color: white ; pointer-events: none">Pending</div>`,
        MTR:
          j['MTR Job Charges'] == 1 ?
            `<div height="15" width="15" style="background-color: red; padding: 5px; border-radius:5px; color: white; pointer-events: none">MTR</div>`
          : '',
        ...j,
      }));
    },

    // Input fields validation (required fields and input fields length)
    checkInputFields: function () {
      let error = false;

      if (!this.selectedPaymentType) {
        this.paymentTypeErr = 'Payment Type is required!';
        this.requiredError.push({
          key: 'paymenttype',
          value: this.paymentTypeErr,
        });
        error = true;
      }

      if (!this.selectedPayeeType) {
        this.payeeTypeErr = 'Payee Type is required!';
        this.requiredError.push({ key: 'payeetype', value: this.payeeTypeErr });
        error = true;
      }

      if (!this.selectedPayeeCode) {
        this.payeeCodeErr = 'Payee Code is required!';
        this.requiredError.push({ key: 'payeecode', value: this.payeeCodeErr });
        error = true;
      }

      if (this.payeeName.trim().length == 0) {
        this.payeeNameErr = 'Payee Name is required!';
        this.requiredError.push({ key: 'payeename', value: this.payeeNameErr });
        error = true;
      }

      if (!this.disablePaymentNo && this.paymentNo.trim() == '') {
        this.paymentNoErr = 'Payment No is required!';
        this.requiredError.push({ key: 'paymentno', value: this.paymentNoErr });
        error = true;
      }

      if (!this.paymentDate) {
        this.paymentDateErr = 'Payment Date is required!';
        this.requiredError.push({
          key: 'paymentdate',
          value: this.paymentDateErr,
        });
        error = true;
      } else if (this.paymentDate != null && this.paymentDate.setHours(0, 0, 0, 0) > new Date('2079-06-06')) {
        this.paymentDateErr = 'Payment Date Invalid!';
        this.requiredError.push({
          key: 'paymentdate',
          value: this.paymentDateErr,
        });
        error = true;
      }

      if (!this.selectedPaymentMethod) {
        this.paymentMethodErr = 'Payment Method is required!';
        this.requiredError.push({
          key: 'paymentmethod',
          value: this.paymentMethodErr,
        });
        error = true;
      }

      if (!this.selectedHeaderCurr) {
        this.headerCurrErr = 'Header Currency is required!';
        this.requiredError.push({
          key: 'headercurr',
          value: this.headerCurrErr,
        });
        error = true;
      }

      if (!this.headerExchRate.trim()) {
        this.headerExchRateErr = 'Exchange Rate is required!';
        this.requiredError.push({
          key: 'headerrate',
          value: this.headerExchRateErr,
        });
        error = true;
      } else if (this.headerExchRate && Number(this.headerExchRate) <= 0) {
        this.headerExchRateErr = 'Exchange Rate is invalid!';
        this.requiredError.push({
          key: 'headerrate',
          value: this.headerExchRateErr,
        });
        error = true;
      }

      if (this.createCheque && !this.selectedBank) {
        this.bankErr = 'Bank is required!';
        this.requiredError.push({ key: 'bank', value: this.bankErr });
        error = true;
      }

      if (this.createCheque && this.chequeGiroNo.trim() == '') {
        this.chequeGiroErr = 'Cheque/GIRO No is required!';
        this.requiredError.push({
          key: 'chequegiro',
          value: this.chequeGiroErr,
        });
        error = true;
      }

      return error;
    },

    saveClicked: async function () {
      this.errorMessages = [];
      this.requiredError = [];

      // Check if side input fields are opened
      if (this.showAPInvoices) {
        if (this.selectedAPInvoices.length > 0) {
          const discard = await this.promptDiscardAPInv();
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showAPInvoices = false;
          this.selectedAPInvoices = [];
        });
      }

      if (this.showJobCharges) {
        if (this.selectedJobCharges.length > 0) {
          const discard = await this.promptDiscardJobCharges();
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showJobCharges = false;
          this.selectedJobCharges = [];
        });
      }

      if (this.showInputCharges) {
        const discard = await this.promptDiscardChargesDetails();

        if (discard === 'Cancel') {
          return;
        }

        this.$nextTick(() => {
          this.showInputCharges = false;
          this.clearAllChargesDetails();
        });
      }

      if (this.showExpenses) {
        if (this.selectedExpenses.length > 0) {
          const discard = await this.promptDiscardExpenses();
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showExpenses = false;
          this.selectedExpenses = [];
        });
      }

      if (this.showInputDirectPosting) {
        const discard = await this.promptDiscardDPDetails();

        if (discard === 'Cancel') {
          return;
        }

        this.$nextTick(() => {
          this.showInputDirectPosting = false;
          this.clearAllDirectPostingDetails();
        });
      }

      if (this.checkInputFields()) return;

      // Check if payment total is 0
      if (Number(this.totalPaymentAmt.replaceAll(',', '')) == 0) {
        const confirmsave = await this.$showAlert(
          'warning',
          'Total Payment Amount is Zero',
          'Do you want to continue?',
          [
            { text: 'Yes', color: 'green', textColor: 'white' },
            { text: 'No', color: 'red', textColor: 'white' },
          ],
        );
        if (confirmsave == 'No') return;
      } else if (Number(this.totalPaymentAmt.replaceAll(',', '')) < 0) {
        this.$showAlert('error', 'Total Payment Amount cannot be negative!', '');
        return;
      }

      // let inv = this.jobChargesDetailsList.filter((j) => j['AP Inv No'] != '');
      // if (inv.length > 0) {
      //   for (const i of inv) {
      //     const validationResults = await this.$apiCallV2('/Payment/OutstandingValidation', { apinvno: i['AP Inv No'], total: `${i['Net Inclusive Tax']}`.replaceAll(',','') });
      //     if (validationResults[0]['Results'] == 1) {
      //       await this.$showAlert('error', `Offset Amount for ${i['AP Inv No']} Invalid!`, `Offset amount for ${i['AP Inv No']} exceeded outstanding amount`);
      //       return;
      //     }
      //   }
      // }

      // Checking if there's job charges created in payment that is deleted
      const manualJCDeleted = this.deletedJobChargesDetails.some((j) => j['RecordSource'] == '2');
      let cancelJobCharges = '';
      if (this.action == 'E' && manualJCDeleted) {
        const confirmDelete = await this.$showAlert(
          'warning',
          'Job Charges Deleted',
          'Do you want to cancel Job Charges keyed with this payment?',
          [
            { text: 'Yes', color: 'indianred', textColor: 'white' },
            { text: 'No', color: 'lightgray', textColor: 'black' },
          ],
        );
        cancelJobCharges = confirmDelete == 'Yes' ? '1' : '0';
      }

      this.$fullscreenLoading('Saving Payment'); // Move to top so fullscreen loading blocks all buttons after validation
      // map job charges into api json
      const allJC = this.jobChargesDetailsList.concat(this.deletedJobChargesDetails);
      let jcToSave = allJC.map((jc) => {
        return {
          action: jc['action'] ?? this.action,
          jobno: jc['Job No'] ?? '',
          contno: jc['Cont No'] ?? '',
          chargescode: jc['Charges Code'] ?? '',
          amount: jc['Net Inclusive Tax'] ?? '',
          famount: jc['F.Net Inclusive Tax'] ?? '',
          currency: jc['Currency'] ?? '',
          exrate: jc['Rate'] ?? '',
          remark: jc['Remark'] ?? '',
          indicator: '',
          apinvno: jc['AP Inv No'] ?? '',
          qty: jc['Qty'] ?? '',
          uom: jc['UOM'] ?? '',
          costcode: jc['Cost Code'] ?? '',
          gst2taxcode: jc['Tax Code'] ?? '',
          gst2taxrate: Number(jc['Tax Rate']).toString() ?? '',
          taxable_amount: jc['Tax Amt'] ?? '',
          ftaxable_amount: jc['F.Tax Amt'] ?? '',
          smkno: jc['SMK No'] ?? '',
          unitprice: jc['Unit Price'] ?? '',
          funitprice: jc['F.Unit Price'] ?? '',
          refno: jc['Ref #'] ?? '',
          rebateamt: jc['Rebate Amt'] ?? '',
          commissionamt: '',
          bkstname: '',
          bkstdate: '',
          txnguid: jc['TxnID'] || '',
          jobchargesid: jc['JobChargesID'] || '',
          canceloption: cancelJobCharges,
          execnewjc: jc['execnewjc'] ?? 'N',
          seqno: jc['Seq No'] || '',
        };
      });
      jcToSave = jcToSave.filter((jc) => jc['action'] != '');

      // map direct posting values into api json
      const allDC = this.directPostingDetailsList.concat(this.deletedDirectPostingDetails);
      let dcToSave = allDC.map((dc) => {
        return {
          action: dc['action'] ?? this.action,
          debitac: dc['Debit'] ?? '',
          creditac: '',
          amount: dc['Net Inclusive Tax'] ?? '',
          appayment: '',
          remark: dc['Remark'] ?? '',
          famount: dc['F.Net Inclusive Tax'],
          fcurrency: dc['Currency'] ?? '',
          frate: dc['Rate'] ?? '',
          seqno: dc['Seq No'] ?? '',
          costcode: dc['Cost Code'] ?? '',
          lorry_no: dc['Vehicle No.'] ?? '',
          gst2debit_ac: dc['Tax Dr.Acc.'] ?? '',
          gst2credit_ac: '',
          gst2taxcode: dc['Tax Code'] ?? '',
          gst2taxrate: dc['Tax Rate'] ?? '',
          taxable_amountgst2: dc['Taxable Amt'] ?? '',
          ftaxable_amountgst2: dc['F.Taxable Amt'] ?? '',
          smkno: dc['SMK.No.'] ?? '',
          receivername: dc['Payee Name'] ?? '',
          recrocno: dc['Registration No.'] ?? '',
          recgst2license: dc['GST License No.'] ?? '',
          recinvno: dc['Invoice No.'] ?? '',
          recinvdate: dc['Invoice Date'] ?? '',
          expenseguid: dc['ExpenseGUID'],
          indicator: '',
          commissionamt: '',
          bkstname: '',
          bkstdate: '',
        };
      });
      dcToSave = dcToSave.filter((dc) => dc['action'] != '');

      // map attachments into api json
      const allDocs = this.deletedAttList.concat(this.attachmentsList);
      const docsToSave = allDocs.map((doc) => {
        return {
          action: doc['Action'] ?? this.action,
          doccode: 'ATT',
          docrefno: '',
          remarks: doc['Doc Remark'],
          status: '',
          docname: doc['File Name'],
          docimage: doc['Doc Image'],
          docimagetype: doc['File Type'],
          docguid: this.action == 'N' ? '' : doc['Document GUID'],
        };
      });

      const distinctJobNo = Array.from(new Set(this.jobChargesDetailsList.map((jc) => jc['Job No'])));

      const allDetails = {
        action: this.action,
        paymentno:
          this.action == 'N' ?
            this.disablePaymentNo ?
              ''
            : this.paymentNo
          : this.paymentNo,
        paymentdate: this.paymentDate ? new Date(this.paymentDate).formatString('yyyy-MM-dd') : '',
        paymentmethod: this.selectedPaymentMethod ? this.selectedPaymentMethod['Payment Type'] : '',
        paymenttype: this.selectedPaymentType ? this.selectedPaymentType['JobTypeID'] : '',
        payeetype: this.selectedPayeeType ? this.selectedPayeeType['Party Code'] : '',
        payeecode: this.selectedPayeeCode ? this.selectedPayeeCode[this.payeeCodeKey] : '',
        payee: this.payeeName || '',
        doctype: this.selectedDocType ? this.selectedDocType['Doc Code'] : '',
        docref: this.docRefNo || '',
        remark: this.remarks || '',
        totalpayment: this.totalPaymentAmt || '',
        adv: this.isAdvancePayment ? '1' : '0',
        ftotalpayment: this.totalPaymentFAmt || '',
        currency: this.selectedHeaderCurr['Currency Code'] || '',
        exrate: this.headerExchRate || '',
        jobno: distinctJobNo.length == 1 ? distinctJobNo[0] : '',
        payment: this.isPnC ? '1' : '0',
        bankcode: this.selectedBank ? this.selectedBank['BankCode'] : '',
        payeebankname: this.bankName || '',
        payeebankaccno: this.bankAccNo || '',
        payeebankswiftcode: this.bankSwift || '',
        payeecorefno: this.coRegNo || '',
        reason: '',
        chqno: this.chequeGiroNo || '',
        fyear: '',
        chequecreate: this.createCheque ? '1' : '0',
        selfbilled: this.selectedSelfBilledType ? this.selectedSelfBilledType['APIKey'] : '0',
        jobcharges: jcToSave ?? [],
        directposting: dcToSave ?? [],
        attachments: docsToSave ?? [],
      };

      const result = await this.$apiCallV2('/Payment/SavePayment', allDetails);
      this.$fullscreenLoading();

      if (result[0]['Results'] != 0) {
        if (this.action == 'N') {
          this.errorMessages.push(`There was an error creating Payment: ${result[0]['ErrMsg']}`);
        } else {
          this.errorMessages.push(`There was an error editing Payment: ${result[0]['ErrMsg']}`);
        }
        return;
      }

      this.showJobChargesTable = false;
      this.showDirectPostingTable = false;
      this.showAttTable = false;
      this.clearMessages();
      const paymentno = result[0]['Payment No'];
      this.$nextTick(async () => {
        await this.populatePaymentDetails(paymentno);
        this.updateSummaryOfEvents();
        this.showJobChargesTable = true;
        this.showDirectPostingTable = true;
        this.showAttTable = true;
        await this.refreshClicked('PaymentList');
      });

      if (this.action == 'N') {
        this.successMessages.push(`${result[0]['ErrMsg']}`);
      } else {
        this.successMessages.push(`${result[0]['ErrMsg']}`);
      }

      this.viewMode = true;
      this.popupTitle = 'View Payment';
      this.action = '';
    },

    // Behaviours when loss focus on header exchange rate
    headerRateLossFocus: async function (focus) {
      if (!focus) {
        // Check if exchange rate is 0/invalid
        if (Number(this.headerExchRate) <= 0) {
          await this.$showAlert('error', 'Exchange Rate Invalid', 'Exchange rate cannot be 0!');
          this.headerExchRate = Number(this.oldRate).toFixed(4);
          return;
        }

        // Check if exchange rate is different from job charges and direct posting
        const allDetails = this.jobChargesDetailsList.concat(this.directPostingDetailsList);
        const currDiff =
          allDetails.length > 0 ? allDetails.some((d) => Number(d['Rate']) != Number(this.headerExchRate)) : false;
        if (this.selectedHeaderCurr['Currency Code'] != 'MYR' && currDiff) {
          const confirm = await this.$showAlert(
            'warning',
            'Header Exch. Rate Changed',
            `Are you sure you want to change all Exch. Rate?`,
            [
              { text: 'Yes', color: 'red', textColor: 'white' },
              { text: 'No', color: 'lightgray', textColor: 'black' },
            ],
          );

          if (confirm == 'Yes') {
            this.showJobChargesTable = false;
            this.showDirectPostingTable = false;

            // Recalculate job charges and direct posting after exchange rate change
            this.headerExchRate = Number(this.headerExchRate ?? '1').toFixed(4);
            if (this.jobChargesDetailsList.length > 0) {
              this.jobChargesDetailsList.forEach((j) => {
                j.Rate = this.headerExchRate;
                j['Unit Price'] = (Number(j['F.Unit Price']) * Number(j['Rate'])) / Number(this.headerExchFactor);
                j['Amount'] = Number(j['Unit Price']) * Number(j['Qty']);
                j['Tax Amt'] = Number(j['Amount']) * (Number(j['Tax Rate']) / 100);
                j['Net Inclusive Tax'] = Number(j['Amount']) + Number(j['Tax Amt']);
              });
            }
            if (this.directPostingDetailsList.length > 0) {
              this.directPostingDetailsList.forEach((d) => {
                d.Rate = this.headerExchRate;
                d['Amount'] = (Number(d['F.Amount']) * Number(d['Rate'])) / Number(this.headerExchFactor);
                d['Taxable Amt'] = Number(d['Amount']) * (Number(d['Tax Rate']) / 100);
                d['Net Inclusive Tax'] = Number(d['Amount']) + Number(d['Taxable Amt']);
              });
            }

            this.$nextTick(() => {
              this.showJobChargesTable = true;
              this.showDirectPostingTable = true;
            });
          } else {
            this.headerExchRate = Number(this.oldRate).toFixed(4);
          }
        } else {
          this.headerExchRate = Number(this.headerExchRate ?? '1').toFixed(4);
        }
      }
    },

    // Click on delete button in delete popup
    deletePopupClicked: async function (e) {
      switch (e) {
        case 'Delete': {
          // Check if delete remarks is empty
          if (this.deletePaymentRemarks.trim().length == 0) {
            this.deletePaymentRemarksErr = 'Please enter delete remarks!';
            return;
          }

          this.$fullscreenLoading('Deleting Selected Payment');
          const deleteBody = {
            action: this.action,
            paymentno: this.selectedPaymentListing[0]['Payment No'],
            reason: this.deletePaymentRemarks || '',
          };

          const deleteResult = await this.$apiCallV2('/Payment/SavePayment', deleteBody);
          this.$fullscreenLoading();
          this.showDeletePayment = false;

          if (deleteResult[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              'Deleted!',
              `Payment ${this.selectedPaymentListing[0]['Payment No']} was successfully deleted.`,
            );
            this.paymentListing = [];
            this.selectedPaymentListing = [];
            this.$nextTick(async () => {
              await this.refreshClicked('PaymentList');
            });
          } else {
            this.$showAlert('error', 'Delete Unsuccessful', `${deleteResult[0]['ErrMsg']}`);
            this.paymentListing = [];
            this.selectedPaymentListing = [];
            this.$nextTick(async () => {
              await this.refreshClicked('PaymentList');
            });
          }
          break;
        }

        case 'Cancel': {
          this.deletePaymentRemarks = '';
          this.showDeletePayment = false;
          break;
        }
      }
    },

    requiredClicked: function (item) {
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },

    tabClicked: async function (_tabItem, tabIndex) {
      if (this.showAPInvoices && tabIndex != 0) {
        if (this.action != '' && this.selectedAPInvoices.length > 0) {
          const discard = await this.promptDiscardAPInv();
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showAPInvoices = false;
          this.selectedAPInvoices = [];
        });
      }

      if (this.showJobCharges && tabIndex != 0) {
        if (this.action != '' && this.selectedJobCharges.length > 0) {
          const discard = await this.promptDiscardJobCharges();
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showJobCharges = false;
          this.selectedJobCharges = [];
        });
      }

      if (this.action != '' && this.showInputCharges && tabIndex != 0) {
        const discard = await this.promptDiscardChargesDetails();

        if (discard === 'Cancel') {
          return;
        }

        this.$nextTick(() => {
          this.showInputCharges = false;
          this.clearAllChargesDetails();
        });
      }

      if (this.showExpenses && tabIndex != 1) {
        if (this.action != '' && this.selectedExpenses.length > 0) {
          const discard = await this.promptDiscardExpenses();
          if (discard === 'Cancel') {
            return;
          }
        }
        this.$nextTick(() => {
          this.showExpenses = false;
          this.selectedExpenses = [];
        });
      }

      if (this.action != '' && this.showInputDirectPosting && tabIndex != 1) {
        const discard = await this.promptDiscardDPDetails();

        if (discard === 'Cancel') {
          return;
        }

        this.$nextTick(() => {
          this.showInputDirectPosting = false;
          this.clearAllDirectPostingDetails();
        });
      }
      this.currentTabIndex = tabIndex;
    },

    // Prompt to discard AP Invoices
    promptDiscardAPInv: async function () {
      return await this.$showAlert('warning', 'AP Invoices not added yet', 'Do you wish to discard the changes?', [
        { text: 'Discard', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
    },

    // Prompt to discard Job Charges
    promptDiscardJobCharges: async function () {
      return await this.$showAlert('warning', 'Job Charges not added yet', 'Do you wish to discard the changes?', [
        { text: 'Discard', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
    },

    // Prompt to discard Charges Details
    promptDiscardChargesDetails: async function () {
      return await this.$showAlert('warning', 'Charges details not added yet', 'Do you wish to discard the changes?', [
        { text: 'Discard', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
    },

    // Prompt to discard expenses
    promptDiscardExpenses: async function () {
      return await this.$showAlert('warning', 'Expenses not added yet', 'Do you wish to discard the changes?', [
        { text: 'Discard', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
    },

    // Prompt to discard direct posting details
    promptDiscardDPDetails: async function () {
      return await this.$showAlert(
        'warning',
        'Direct Posting details not added yet',
        'Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );
    },

    // Check if payee type and payee code is selected
    checkPayeeTypeCodeSelected: async function () {
      const notSelectedVal = [];
      if (!this.selectedPayeeType) {
        notSelectedVal.push('Payee Type');
      }
      if (!this.selectedPayeeCode) {
        notSelectedVal.push('Payee Code');
      }
      const selects = notSelectedVal.join(' & ');
      if (notSelectedVal.length > 0) {
        const warningMsg = `Please Select ${selects} First!`;
        await this.$showAlert('error', `${selects} Not Selected`, warningMsg);
        return false;
      }
      return true;
    },

    // Get payment details on 'edit'/'view' mode
    getPayment: async function (title) {
      this.popupTitle = title;
      if (this.selectedPaymentListing.length > 0 && this.action != 'N') {
        const paymentNo = this.selectedPaymentListing[0]['Payment No'];
        await this.populatePaymentDetails(paymentNo);
      }
    },

    // Populate payment header details
    populatePaymentHeader: async function () {
      this.selectedPaymentType =
        this.paymentDetails['Payment Type'] ?
          this.paymentTypeList.find((type) => type['JobTypeID'] == (this.paymentDetails['Payment Type'] || '0'))
        : null;
      await this.getJobTypeListing(this.selectedPaymentType);
      if (this.action != 'N' || this.viewMode) {
        this.disablePaymentNo = true;
      }
      this.paymentDate = this.paymentDetails['Date'] ? new Date(this.paymentDetails['Date']) : null;
      this.selectedPaymentMethod =
        this.paymentDetails['Payment Method'] ?
          this.paymentMethodList.find((method) => method['Payment Type'] == this.paymentDetails['Payment Method'])
        : null;
      this.selectedPayeeType =
        this.paymentDetails['Payee Type'] ?
          this.payeeTypeList.find((payee) => payee['Party Code'] == this.paymentDetails['Payee Type'])
        : null;
      await this.getPayeeCodeList(this.selectedPayeeType);
      this.selectedPayeeCode =
        this.paymentDetails['Payee Code'] ?
          this.payeeCodeList.find((payee) => payee[this.payeeCodeKey] == this.paymentDetails['Payee Code'])
        : null;
      await this.payeeCodeSelected(this.selectedPayeeCode);
      this.headerExchRate = this.paymentDetails['rate'] ?? this.selectedHeaderCurr['Currency Rate'];
      this.selectedHeaderCurr = this.currencyList.find(
        (curr) => curr['Currency Code'] == (this.paymentDetails['Currency'] ?? 'MYR'),
      );
      this.headerExchFactor = this.selectedHeaderCurr['Factor'].toString();
      this.payeeName = this.paymentDetails['Payee'] ?? '';
      this.selectedBank =
        this.paymentDetails['Bank'] ?
          this.bankList.find((bank) => bank['BankCode'] == this.paymentDetails['Bank'])
        : null;
      this.createCheque = this.paymentDetails['Chk Chq'] == '1';
      this.docRefNo = this.paymentDetails['Doc Ref #'] ?? '';
      this.selectedDocType =
        this.paymentDetails['Doc Type'] ?
          this.docTypeList.find((type) => type['Doc Code'] == this.paymentDetails['Doc Type'])
        : null;
      this.isAdvancePayment = this.paymentDetails['Chk Advance'] == '1';
      this.isPnC = this.paymentDetails['Chk PC'] == '1';
      this.selectedSelfBilledType =
        this.paymentDetails['Self-Billed'] == '0' ?
          null
        : this.selfBilledOptions.find((s) => s['APIKey'] == this.paymentDetails['Self-Billed']);
      this.remarks = this.paymentDetails['Remark'] || '';
    },

    // Retrieve and populate payment all details
    populatePaymentDetails: async function (paymentno) {
      this.$fullscreenLoading('Getting data');
      this.paymentDetails = [];
      this.paymentDetails = (
        await this.$apiCallV2('/Master/GetDetailsMaster', {
          masterfile: 'payment',
          recordid: paymentno,
        })
      )['Payment'];
      if (!this.paymentDetails?.['LHDN Ref No']) {
        this.paymentDetails['LHDN Ref No'] = 'P';
      }
      await this.populatePaymentHeader();
      this.paymentNo = this.paymentDetails['Payment No'] ?? '';
      this.payeeCodeNameDisabled = false;
      this.chequeGiroNo = this.paymentDetails['Cheque No'] ?? '';
      this.totalPaymentAmt = this.$currencyFormat(this.paymentDetails['Total']) ?? '0.00';
      this.totalPaymentFAmt = this.$currencyFormat(this.paymentDetails['F.Total']) ?? '0.00';
      this.totalOutstanding = this.$currencyFormat(this.paymentDetails['Outstanding']) ?? '0.00';
      this.totalFOutstanding = this.$currencyFormat(this.paymentDetails['F.Outstanding']) ?? '0.00';

      // Will show bank recon statement no in summary of events if available, update summaryItems Label (statement date) and Indicator (statement no)
      if (this.paymentDetails['Statement No'] && this.paymentDetails['Statement No'].trim() != '') {
        const recon = this.summaryItems.find((item) => item['Title'] == 'Statement No');
        recon['Label'] = [this.paymentDetails['Statement Date']];
        recon['Indicator'][0]['name'] = this.paymentDetails['Statement No'];
      }

      this.jobChargesDetailsList = this.paymentDetails['JobCharges'] ?? [];
      if (this.jobChargesDetailsList.length > 0) {
        this.totalAmountCharges = this.$currencyFormat(this.jobChargesDetailsList[0]['Total Amount']) ?? '0.00';
        this.totalFAmountCharges = this.$currencyFormat(this.jobChargesDetailsList[0]['F.Total Amount']) ?? '0.00';
        this.totalTaxCharges = this.$currencyFormat(this.jobChargesDetailsList[0]['Total Tax']) ?? '0.00';
        this.updateJCIndex();
        this.APInvoiceListing = this.filterAPInvoiceListing();
        this.jobChargesListing = this.filterJobChargesListing();
        this.updateJobChargesListStatus();
      }

      this.directPostingDetailsList = this.paymentDetails['DirectPosting'] ?? [];
      if (this.directPostingDetailsList.length > 0) {
        this.totalDPAmt = this.$currencyFormat(this.directPostingDetailsList[0]['Total DP Amount']) ?? '0.00';
        this.totalDPFAmt = this.$currencyFormat(this.directPostingDetailsList[0]['Total DP F.Amount']) ?? '0.00';
        this.totalTaxDP = this.$currencyFormat(this.directPostingDetailsList[0]['Total Tax']) ?? '0.00';
        this.updateDPIndex();
        this.expensesListing = this.filterExpensesListing();
      }

      this.attachmentsList = this.paymentDetails['Attachments'] ?? [];
      if (this.attachmentsList.length > 0) {
        this.attachmentsList.forEach((att) => {
          att['Doc Date'] = new Date(att['Doc Date']).formatString('yyyy-MM-dd');
        });
      }
      this.glEntryList = this.paymentDetails['GL'] ?? [];
      this.updateTotalDrCr();
      this.eInvoiceList = this.paymentDetails.eInvoice ?? [];

      if (this.eInvoiceList.length > 0) {
        const getLatestObj = this.eInvoiceList.reduce((acc, item) => {
          return item['#'] > acc['#'] ? item : acc;
        });
        this.paymentDetails['LHDN Submission'] = getLatestObj['Status'][0];
      } else {
        this.paymentDetails['LHDN Submission'] = 'P';
      }
      this.$fullscreenLoading();
    },

    // For summary of events
    updateSummaryOfEvents: function () {
      for (const item of this.summaryItems) {
        const title = item['Title'];
        const value = this.paymentDetails[title];

        if (title == 'Statement No' && value) {
          item['indicatorToShow'] = value;
          item['visible'] = true;
        } else if (title == 'PV Count') {
          item['indicatorToShow'] = value || '0';
        } else if (value && item['Indicator']) {
          const indicator = item['Indicator'].find((i) => i['code'] === value);
          if (indicator) {
            item['indicatorToShow'] = indicator['name'];
            item['bgcolorToShow'] = indicator['bg'];
            item['visible'] = true;
          }
        }
      }
    },

    // Populate payee code list
    getPayeeCodeList: async function (payeeType) {
      this.payeeCodeList =
        payeeType ?
          await this.$apiCallV2('/Master/GetDetailsMaster', {
            masterfile: 'payeecode',
            recordid: payeeType['Table'],
          })
        : [];
      if (this.payeeCodeList.length > 0) {
        const codeKeys = Object.keys(this.payeeCodeList[0]);
        this.payeeCodeKey = codeKeys[0];
        this.payeeCodeDescKey = codeKeys[1];
      }
    },

    // Update payment total amount
    updatePaymentTotals: function () {
      const totalMYRAmt =
        Number(this.totalAmountCharges.replaceAll(',', '')) + Number(this.totalDPAmt.replaceAll(',', ''));
      const totalFAmt =
        Number(this.totalFAmountCharges.replaceAll(',', '')) + Number(this.totalDPFAmt.replaceAll(',', ''));

      this.totalPaymentAmt = this.$currencyFormat(`${totalMYRAmt}`);
      this.totalPaymentFAmt = this.$currencyFormat(`${totalFAmt}`);

      this.totalOutstanding = this.totalPaymentAmt;
      this.totalFOutstanding = this.totalPaymentFAmt;

      if (this.jobChargesDetailsList.length > 0 || this.directPostingDetailsList.length > 0) {
        this.multiCurr = !(
          this.jobChargesDetailsList.every((d) => d['Currency'] == this.selectedHeaderCurr['Currency Code']) &&
          this.directPostingDetailsList.every((d) => d['Currency'] == this.selectedHeaderCurr['Currency Code'])
        );
      }
    },

    // Calculate gl debit and credit total
    updateTotalDrCr: function () {
      let debit = 0;
      let credit = 0;
      if (this.glEntryList.length == 0) {
        this.totalDebit = this.$currencyFormat('0');
        this.totalCredit = this.$currencyFormat('0');
        return;
      }

      this.glEntryList.forEach((gl) => {
        if (gl['Debit Ac'] != '0' && gl['Credit Ac'] == '0') {
          debit += Number(gl['Amount']);
        } else if (gl['Credit Ac'] != '0' && gl['Debit Ac'] == '0') {
          credit += Number(gl['Amount']);
        }
      });

      this.totalDebit = this.$currencyFormat(`${debit}`);
      this.totalCredit = this.$currencyFormat(`${credit}`);
    },

    jobnoRowLimitChanged: async function (focus) {
      if (!focus) {
        this.$fullscreenLoading('Getting Job No');
        this.jobNoList = [];
        this.jobNoList = await this.$apiCallV2(`/Payment/GetJobListing`, {
          top: this.jobnoRowLimit,
          jobtypeid: this.selectedPaymentType['JobTypeID'],
        });
        this.$fullscreenLoading();
      }
    },

    // Container no list row limit changed
    contnoRowLimitChanged: async function (focus) {
      if (!focus) {
        this.$fullscreenLoading('Getting Container No');
        this.containerList = [];
        this.containerList = await this.$apiCallV2(`/Payment/GetQueryByJobConfig`, {
          table: 'Container',
          jobno: this.selectedJobNo ? this.selectedJobNo['Job No'] : null,
          top: this.contnoRowLimit,
        });
        this.$fullscreenLoading();
      }
    },

    // Job charges listing row limit changed (input query table box can't show all job charges at once)
    // jobChargesListingRowChange: async function (focus) {
    //   let contNoArr = '';
    //   if (this.searchContNo.trim().length > 0) {
    //     let containers = this.searchContNo.trim().replaceAll(',', '').split('\n');
    //     containers = containers.filter((cont) => cont.trim() != '');
    //     contNoArr = containers.join(',');
    //   }

    //   if (!focus) {
    //     const dataToPost = {
    //       payeetype: this.showAllPayeeType ? '' : this.selectedPayeeType['Party Code'],
    //       payeecode: this.showAllPayeeType ? '' : this.selectedPayeeCode[this.payeeCodeKey],
    //       allpayeetype: this.showAllPayeeType ? '1' : '0',
    //       asat: this.filterJobByAsAt ? new Date(this.asAtDate).formatString('yyyy-MM-dd') : '',
    //       chargecode: this.selectedChargeCodeSearch ? this.selectedChargeCodeSearch['Expenses Code'] : '',
    //       containerno: contNoArr,
    //       jobno: this.selectedJobNoSearch ? this.selectedJobNoSearch['Job No'] : '',
    //       top: this.jobchargesRowLimit,
    //     };
    //     this.$fullscreenLoading('Getting Job Charges Listing');
    //     this.jobChargesTableBoxLoading = true;
    //     this.oriJobChargesListing = [];
    //     this.oriJobChargesListing = await this.$apiCallV2(`/Payment/GetPaymentJobChargesListing`, dataToPost);
    //     this.jobChargesListing = this.filterJobChargesListing();
    //     this.updateJobChargesListStatus();
    //     this.jobChargesTableBoxLoading = false;
    //     this.$fullscreenLoading();
    //   }
    // },

    // Open Print Payment Voucher page in new window
    openPrintPVPage: function (payno) {
      let printPVRoute;
      if (payno) {
        printPVRoute = this.$router.resolve(
          `/Home/${this.$route.params.user}/FINPayment/Print Payment Voucher?payno=${payno.trim?.() ?? ''}`,
        );
      } else {
        printPVRoute = this.$router.resolve(`/Home/${this.$route.params.user}/FINPayment/Print Payment Voucher`);
      }
      window.open(printPVRoute.href, '_blank');
    },

    //#region Job Charges Tab functions
    // Check if selected job is completed or requires password to use the job, or is open to all
    checkJobStatus: async function (jobno) {
      const jobstatus = await this.$apiCallV2('/Payment/CheckJobStatus', {
        jobno: jobno,
      });
      if (jobstatus[0]['Indicator'] == 'A') {
        const confirmProceed = await this.$showAlert('warning', `Job ${jobno} is complete`, `Proceed?`, [
          { text: 'Yes', color: 'indianred', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ]);
        if (confirmProceed == 'No') {
          return false;
        }
        return true;
      } else if (jobstatus[0]['Indicator'] == 'O') {
        if (this.settingsJobClosePswd == '') {
          await this.$showAlert('error', `Job ${jobno} is complete`, 'You do not have the permission to use this Job!');
          return false;
        }
        const pswd = await this.showPasswordInput();
        if (pswd !== this.settingsJobClosePswd) {
          await this.$showAlert('error', `Incorrect Password`, '');
          return false;
        }
        return true;
      }
      return true;
    },

    // Button clicks in password popup
    pswdPopupClicked: function (e) {
      this.jobclosepswdErr = '';
      switch (e) {
        case 'OK':
          if (this.jobclosepswd.trim() == '') {
            this.jobclosepswdErr = 'Job Close Password is required!';
            return;
          }
          this.$emit('passwordEntered', this.jobclosepswd);
          break;

        case 'Cancel':
          this.jobclosepswd = '';
          this.$emit('passwordCancelled');
          break;
      }
      this.showEnterPswd = false;
    },

    // Show password input popup
    showPasswordInput: function () {
      this.jobclosepswd = '';
      return new Promise((resolve, reject) => {
        this.showEnterPswd = true;
        this.$once('passwordEntered', (password) => {
          this.$off('passwordCancelled');
          resolve(password);
        });

        this.$once('passwordCancelled', () => {
          this.$off('passwordEntered');
          reject('Cancel');
        });
      });
    },

    // Check if payee type and payee code is selected, then open AP Invoices input
    openAPInvoices: async function () {
      const discard = await this.checkPayeeTypeCodeSelected();
      if (discard) {
        this.showAPInvoices = true;
        this.showJobCharges = false;
        this.showInputCharges = false;
      }
    },

    // Check if payee type and payee code is selected, then open Job Charges input
    openJobCharges: async function () {
      const discard = await this.checkPayeeTypeCodeSelected();
      if (discard) {
        this.showJobCharges = true;
        this.showAPInvoices = false;
        this.showInputCharges = false;
      }
    },

    // Open new charges input fields
    openNewChargeCodeFields: function () {
      this.requiredError = [];
      this.headerExchRateErr = '';
      // Check if header exchange rate is 0
      if (Number(this.headerExchRate) == 0) {
        this.headerExchRateErr = 'Exchange Rate is invalid!';
        this.requiredError.push({
          key: 'headerrate',
          value: this.headerExchRateErr,
        });
        return;
      }
      this.clearAllChargesDetails();
      this.selectedJobChargeEntry = [];
      this.selectedChargesCurrency = this.currencyList.find(
        (curr) =>
          curr['Currency Code'] === (this.selectedHeaderCurr ? this.selectedHeaderCurr['Currency Code'] : 'MYR'),
      );
      this.chargesCurrSelected(this.selectedChargesCurrency);
      this.chargesExchangeRate = Number(this.headerExchRate).toFixed(4);
      this.chargesFactor = Number(this.headerExchFactor);
      this.chargesRebate = this.$currencyFormat('0');
      this.chargesUPrice = this.$currencyFormat('0');
      this.chargesTotalPrice = this.$currencyFormat('0');
      this.myrChargesUPrice = this.$currencyFormat('0');
      this.myrChargesTotalPrice = this.$currencyFormat('0');
      this.chargesTaxAmt = this.$currencyFormat('0');
      this.chargesUPriceInclTax = this.$currencyFormat('0');
      this.chargesTotalPriceInclTax = this.$currencyFormat('0');
      this.myrChargesTaxAmt = this.$currencyFormat('0');
      this.myrChargesUPriceInclTax = this.$currencyFormat('0');
      this.myrChargesTotalPriceInclTax = this.$currencyFormat('0');
      this.chargesTaxRate = '0';

      this.showInputCharges = true;
      this.showJobCharges = false;
      this.showAPInvoices = false;
      this.saveJCAction = 'N';
      this.subtitleJC = 'New Job Charges';
      this.chargeCodeRequired = true;
      // this.containerList = await this.$apiCallV2('/Payment/GetQueryByJobConfig', { table: 'Container' });
    },

    // Delete selected job charges
    deleteJobCharges: async function () {
      if (this.selectedJobChargeEntry.length == 0) {
        await this.$showAlert('error', 'No Job Charges Selected', 'Please select at least 1 job charge to delete.');
        return;
      }

      const confirmCancel = await this.$showAlert(
        'warning',
        'Deleting Job Charges Record',
        `Are you sure you want to delete this record ?`,
        [
          { text: 'Yes', color: 'indianred', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );
      if (confirmCancel == 'No') return;

      let temp = [];
      if (this.selectedJobChargeEntry[0]['action'] && this.selectedJobChargeEntry[0]['action'] == 'N') {
        temp = this.jobChargesDetailsList.filter((j) => j['Seq No'] != this.selectedJobChargeEntry[0]['Seq No']);
      } else {
        this.jobChargesDetailsList.forEach((j) => {
          if (j['Seq No'] == this.selectedJobChargeEntry[0]['Seq No']) {
            //TxnID
            j['action'] = 'D';
            this.deletedJobChargesDetails.push(j);
          }
        });
        temp = this.jobChargesDetailsList.filter((j) => j['action'] != 'D');
      }

      this.jobChargesDetailsList = [];
      this.showJobChargesTable = false;
      this.$nextTick(() => {
        this.jobChargesDetailsList = temp;
        this.selectedJobChargeEntry = [];
        this.clearAllChargesDetails();
        this.updateJCIndex();
        this.updateChargesTotals();
        this.APInvoiceListing = this.filterAPInvoiceListing();
        this.jobChargesListing = this.filterJobChargesListing();
        this.updateJobChargesListStatus();
        this.showJobChargesTable = true;
      });
    },

    // Button click on job charges/apinv table
    jobChargesTableButtonClicked: function (_btn, data) {
      this.showInputCharges = true;
      this.chargeCodeRequired = data['AP Inv No'] == '';
      this.billedJob = data['Pending Billing Charges'] == '0';
      this.selectedJobChargeEntry = [data];
      this.saveJCAction =
        this.selectedJobChargeEntry[0]['action'] && this.selectedJobChargeEntry[0]['action'] == 'N' ? 'N' : 'E';
      this.subtitleJC = this.chargeCodeRequired ? 'Edit Job Charges' : 'Edit AP Invoice Offset';
      this.populateChargesDetails(data);
    },

    // Behaviour when select all/deselect all ap invoices
    selectDeselectAPClicked: function () {
      if (this.selectedAPInvoices.length != this.APInvoiceListing.length) {
        this.selectedAPInvoices = [];
        this.selectedAPInvoices = [...this.APInvoiceListing];
      } else {
        this.selectedAPInvoices = [];
      }
    },

    // Add selected AP Invoices
    addSelectedAPInv: async function () {
      if (this.selectedAPInvoices.length == 0) {
        await this.$showAlert('error', 'No AP Invoice Selected', 'Please select at least 1 AP invoice to add.');
        return;
      }

      const temp = this.jobChargesDetailsList;
      this.jobChargesDetailsList = [];
      for (const inv of this.selectedAPInvoices) {
        const highestSeq = temp.reduce((max, d) => (parseInt(d['Seq No']) > max ? parseInt(d['Seq No']) : max), 0);
        const dataToSave = {
          '#': '',
          action: 'N',
          'AP Inv No': inv['AP Inv No'],
          'Job No': inv['Job No'],
          'Cont No': '',
          'Spp Inv No': inv['Inv No'],
          'Charges Code': '',
          Amount: inv['Balance'], //local
          'Tax Code': '',
          'Tax Rate': '0.00', //local
          'Tax Amt': '0.00', //local
          'Net Inclusive Tax': inv['Balance'], //local
          'F.Amount': inv['F.Balance'], //foreign
          'F.Tax Amt': '',
          'F.Net Inclusive Tax': inv['F.Balance'], //foreign
          Currency: inv['Currency'],
          Rate: inv['Rate'],
          'User ID': '',
          'Date Update': '',
          Remark: inv['Remark'],
          'Seq No': highestSeq + 1,
          Qty: 1,
          UOM: '',
          'Rebate Amt': '',
          'Cost Code': '',
          'SMK No': '',
          'Unit Price': inv['Balance'],
          'F.Unit Price': inv['F.Balance'],
          'Ref #': '',
          JobChargesID: '',
          TxnID: '',
          RecordSource: '',
          'Pending Billing Charges': '',
          execnewjc: '',
        };
        temp.push(dataToSave);
      }

      this.showAPInvoices = false;
      this.showJobChargesTable = false;
      this.$nextTick(() => {
        this.jobChargesDetailsList = temp;
        this.updateJCIndex();
        this.showJobChargesTable = true;
        this.updateChargesTotals();
        this.APInvoiceListing = this.filterAPInvoiceListing();
      });
    },

    // Close ap invoice table
    cancelSelectAPInv: function () {
      this.showAPInvoices = false;
      this.selectedAPInvoices = [];
    },

    // Filter ap invoice listing
    filterAPInvoiceListing: function () {
      const saved = [];
      this.jobChargesDetailsList.forEach((j) => {
        if (j['AP Inv No'] != '') saved.push(j['AP Inv No']);
      });
      return this.oriAPInvoiceListing.filter((inv) => !saved.includes(inv['AP Inv No']));
    },

    // Behaviour when select all/deselect all job charges
    selectDeselectJobChargesClicked: function () {
      if (this.selectedJobCharges.length != this.jobChargesListing.length) {
        this.selectedJobCharges = [];
        this.selectedJobCharges = [...this.jobChargesListing];
      } else {
        this.selectedJobCharges = [];
      }
    },

    // Add selected job charges
    addSelectedJobCharges: async function () {
      if (this.selectedJobCharges.length == 0) {
        await this.$showAlert('error', 'No Job Charges Selected', 'Please select at least 1 Job Charges to add.');
        return;
      }

      const temp = this.jobChargesDetailsList;
      for (const job of this.selectedJobCharges) {
        const proceed = await this.checkJobStatus(job['Job No']);
        if (!proceed) return;

        const highestSeq = temp.reduce((max, d) => (parseInt(d['Seq No']) > max ? parseInt(d['Seq No']) : max), 0);
        const dataToSave = {
          '#': '',
          action: 'N',
          'AP Inv No': '',
          'Job No': job['Job No'],
          'Cont No': job['Cont No'],
          'Spp Inv No': '',
          'Charges Code': job['Charges Code'],
          Amount: job['Amount'], //local
          'Tax Code': job['Tax Code'],
          'Tax Rate': job['Tax Rate'], //local
          'Tax Amt': job['Tax Amt'], //local
          'Net Inclusive Tax': Number(job['Amount']) + Number(job['Tax Amt']), //local
          'F.Amount': job['F.Amount'], //foreign
          'F.Tax Amt': job['F.Tax Amount'],
          'F.Net Inclusive Tax': Number(job['F.Amount']) + Number(job['F.Tax Amt'] ?? 0), //foreign
          Currency: job['Currency'],
          Rate: job['Rate'],
          'User ID': '',
          'Date Update': '',
          Remark: job['Remark'],
          'Seq No': highestSeq + 1,
          Qty: job['Qty'],
          UOM: job['UOM'],
          'Rebate Amt': job['Rebate'],
          'Cost Code': job['Cost Code'],
          'SMK No': '',
          'Unit Price': Number(job['Amount']) / Number(job['Qty']),
          'F.Unit Price': Number(job['F.Amount']) / Number(job['Qty']),
          'Ref #': '',
          JobChargesID: job['TxnID'],
          TxnID: '',
          RecordSource: '0', //temp hard code
          'Pending Billing Charges': job['Pending Billing Charges'],
          execnewjc: 'N',
        };
        temp.push(dataToSave);
      }

      this.showJobCharges = false;
      this.showJobChargesTable = false;
      this.jobChargesDetailsList = [];
      this.$nextTick(() => {
        this.jobChargesDetailsList = temp;
        this.updateJCIndex();
        this.showJobChargesTable = true;
        this.jobChargesListing = this.filterJobChargesListing();
        this.updateJobChargesListStatus();
        this.updateChargesTotals();
      });
    },

    // Filter job charges listing
    filterJobChargesListing: function () {
      const saved = [];
      this.jobChargesDetailsList.forEach((j) => {
        if (j['JobChargesID'] && j['JobChargesID'].trim() != '') saved.push(j['JobChargesID']);
      });
      return this.oriJobChargesListing.filter((inv) => !saved.includes(inv['TxnID']));
    },

    // Check charges input values
    checkChargesFields: function () {
      this.clearAllChargesError();
      let error = false;
      if (this.chargeCodeRequired && !this.selectedCharges) {
        this.chargesErr = 'Charges Code is required!';
        this.requiredError.push({ key: 'jccharges', value: this.chargesErr });
        error = true;
      }

      if (this.smkNoJC.trim().length > 35) {
        this.smkNoJCErr = 'SMK No cannot exceed 35 characters!';
        this.requiredError.push({ key: 'jcsmkno', value: this.smkNoJCErr });
        error = true;
      }

      if (this.jobChargeRefNo.trim().length > 35) {
        this.jobChargeRefNoErr = 'Ref # cannot exceed 35 characters!';
        this.requiredError.push({
          key: 'jcrefno',
          value: this.jobChargeRefNoErr,
        });
        error = true;
      }

      if (this.chargesQty.trim() == '') {
        this.chargesQtyErr = 'Quantity is required!';
        this.requiredError.push({ key: 'jcqty', value: this.chargesQtyErr });
        error = true;
      } else if (Number(this.chargesQty) <= 0) {
        this.chargesQty = 'Quantity is invalid!';
        this.requiredError.push({ key: 'jcqty', value: this.chargesQty });
        error = true;
      } else if (this.chargesQty.trim().length > 4) {
        this.chargesQty = 'Quantity cannot exceed 4 characters';
        this.requiredError.push({ key: 'jcqty', value: this.chargesQty });
        error = true;
      }

      if (!this.selectedChargesCurrency) {
        this.chargesCurrencyErr = 'Currency is required!';
        this.requiredError.push({
          key: 'chargesCurrency',
          value: this.chargesCurrencyErr,
        });
        error = true;
      } else if (
        this.selectedChargesCurrency &&
        this.selectedHeaderCurr['Currency Code'] != 'MYR' &&
        this.selectedChargesCurrency['Currency Code'] != this.selectedHeaderCurr['Currency Code']
      ) {
        this.chargesCurrencyErr = 'Selected currency is different from header currency!';
        this.requiredError.push({
          key: 'chargesCurrency',
          value: this.chargesCurrencyErr,
        });
        error = true;
      }

      if (this.chargesUPrice.trim() == '') {
        this.chargesUPriceErr = 'Unit Price is required!';
        this.requiredError.push({
          key: 'jcuprice',
          value: this.chargesUPriceErr,
        });
        error = true;
      }

      if (this.chargesTotalPrice.trim() == '') {
        this.chargesTotalPriceErr = 'Total Price is required!';
        this.requiredError.push({
          key: 'jxtotprice',
          value: this.chargesTotalPriceErr,
        });
        error = true;
      }

      if (this.chargesExchangeRate.trim() == '') {
        this.chargesExchangeRateErr = 'Exchange Rate is required!';
        this.requiredError.push({
          key: 'jcexchgrate',
          value: this.chargesExchangeRateErr,
        });
        error = true;
      } else if (Number(this.chargesExchangeRate) == 0) {
        this.chargesExchangeRateErr = 'Exchange Rate is invalid!';
        this.requiredError.push({
          key: 'jcexchgrate',
          value: this.chargesExchangeRateErr,
        });
        error = true;
      } else if (
        this.selectedHeaderCurr &&
        this.selectedHeaderCurr['Currency Code'] != 'MYR' &&
        Number(this.chargesExchangeRate) != Number(this.headerExchRate)
      ) {
        this.chargesExchangeRateErr = 'Selected exchange rate is different from header exchange rate!';
        this.requiredError.push({
          key: 'jcexchgrate',
          value: this.chargesExchangeRateErr,
        });
        error = true;
      }

      if (this.myrChargesUPrice.trim() == '') {
        this.myrChargesUPriceErr = 'Unit Price is required!';
        this.requiredError.push({
          key: 'jcmyruprice',
          value: this.myrChargesUPriceErr,
        });
        error = true;
      }

      if (this.myrChargesTotalPrice.trim() == '') {
        this.myrChargesTotalPriceErr = 'Total Price is required!';
        this.requiredError.push({
          key: 'jcmyrtotprice',
          value: this.myrChargesTotalPriceErr,
        });
        error = true;
      }

      if (this.chargesTaxAmt.trim() == '') {
        this.chargesTaxAmtErr = 'Tax Amt is required!';
        this.requiredError.push({
          key: 'jctaxamt',
          value: this.chargesTaxAmtErr,
        });
      }
      return error;
    },

    // Check if job charge exists
    checkJobChargeExist: async function (charge, job) {
      this.$fullscreenLoading('Checking Job Charge');
      const dataToPost = {
        payeetype: '',
        payeecode: '',
        allpayeetype: '',
        asat: '',
        chargecode: charge ?? '',
        containerno: '',
        jobno: job ?? '',
      };

      let jobAvailable = [];
      jobAvailable = await this.$apiCallV2('/Payment/GetPaymentJobChargesListing', dataToPost);
      this.$fullscreenLoading();
      return jobAvailable;
    },

    // Adding charges to list
    saveChargesClicked: async function () {
      if (this.checkChargesFields()) return;

      if (this.selectedJobChargeEntry.length > 0 && this.selectedJobChargeEntry[0]['AP Inv No'] != '') {
        this.$fullscreenLoading('Validating Offset Amount');
        const dataToPost = {
          apinvno: this.selectedJobChargeEntry[0]['AP Inv No'],
          total: this.myrChargesTotalPriceInclTax.replaceAll(',', ''),
          ftotal: this.chargesTotalPriceInclTax.replaceAll(',', ''),
          action:
            this.action == 'N' ? 'N'
            : this.selectedJobChargeEntry.length == 0 ? 'N'
            : this.saveJCAction,
          txnid: this.selectedJobChargeEntry.length > 0 ? this.selectedJobChargeEntry[0]['TxnID'] : '',
        };
        const validationResults = await this.$apiCallV2('/Payment/OutstandingValidation', dataToPost);
        this.$fullscreenLoading();
        if (validationResults[0]['Results'] == '1') {
          await this.$showAlert('error', 'Total Amount exceeded outstanding!', validationResults[0]['ErrMsg']);
          this.myrChargesTotalPrice = Number(this.selectedJobChargeEntry[0]['Amount']).toFixed(4);
          this.chargesTotalPrice = Number(this.selectedJobChargeEntry[0]['F.Amount']).toFixed(4);
          this.chargesPriceLossFocus(false, 'totalPrice');
          return;
        }
      }

      if (this.selectedJobChargeEntry.length == 0 && this.selectedJobNo && this.selectedCharges) {
        const jobAvailable = await this.checkJobChargeExist(
          this.selectedCharges['Expenses Code'],
          this.selectedJobNo['Job No'],
        );
        if (jobAvailable.length > 0) {
          const confirmAdd = await this.$showAlert(
            'warning',
            `There is an existing unpaid job charge for Job#: ${jobAvailable[0]['Job No']}, Charge Code: ${jobAvailable[0]['Charges Code']}, Amount: ${jobAvailable[0]['Currency']} ${jobAvailable[0]['Amount']}`,
            "You can use 'Select from Job Charges' to add this item. Do you still want to add this item?",
            [
              { text: 'Yes', color: 'indianred', textColor: 'white' },
              { text: 'No', color: 'lightgray', textColor: 'black' },
            ],
          );
          if (confirmAdd == 'No') return;
        }
      }

      const temp = this.jobChargesDetailsList;
      const highestSeq = temp.reduce((max, d) => (parseInt(d['Seq No']) > max ? parseInt(d['Seq No']) : max), 0);
      this.jobChargesDetailsList = [];
      const dataToSave = {
        '#': '',
        action:
          this.action == 'N' ? 'N'
          : this.selectedJobChargeEntry.length == 0 ? 'N'
          : this.saveJCAction,
        'AP Inv No': '',
        'Job No': this.selectedJobNo ? this.selectedJobNo['Job No'] : '',
        'Cont No': this.selectedContainer ? this.selectedContainer['Cont No'] : '',
        'Spp Inv No': '',
        'Charges Code': this.selectedCharges ? this.selectedCharges['Expenses Code'] : '',
        Amount: Number(this.myrChargesTotalPrice || 0).toFixed(2), //local
        'Tax Code': this.selectedChargesTaxCode ? this.selectedChargesTaxCode['Tax Code'] : '',
        'Tax Rate': this.chargesTaxRate || '0', //local
        'Tax Amt': Number(this.myrChargesTaxAmt || 0).toFixed(2), //local
        'Net Inclusive Tax': Number(this.myrChargesTotalPriceInclTax || 0).toFixed(2), //local
        'F.Amount': Number(this.chargesTotalPrice || 0).toFixed(2), //foreign
        'F.Tax Amt': Number(this.chargesTaxAmt || 0).toFixed(2),
        'F.Net Inclusive Tax': Number(this.chargesTotalPriceInclTax || 0).toFixed(2), //foreign
        Currency: this.selectedChargesCurrency ? this.selectedChargesCurrency['Currency Code'] : 'MYR',
        Rate: Number(this.chargesExchangeRate || '1.0000').toFixed(4),
        'User ID': '',
        'Date Update': '',
        Remark: this.jobChargeRemarks || '',
        'Seq No': '',
        Qty: this.chargesQty || '1',
        UOM: this.selectedChargesUOM ? this.selectedChargesUOM['UOM Code'] : '',
        'Rebate Amt': this.chargesRebate || '0.0000',
        'Cost Code': this.selectedJCCostCode ? this.selectedJCCostCode['Cost Code'] : '',
        'SMK No': this.smkNoJC.trim() || '',
        'Unit Price': Number(this.myrChargesUPrice || 0).toFixed(4),
        'F.Unit Price': Number(this.chargesUPrice || 0).toFixed(4),
        'Ref #': this.jobChargeRefNo.trim() || '',
        JobChargesID: '',
        TxnID: '',
        RecordSource: '',
        'Pending Billing Charges': this.billedJob ? '0' : '1',
        execnewjc: 'Y',
      };

      if (this.selectedJobChargeEntry.length > 0) {
        dataToSave['AP Inv No'] = this.selectedJobChargeEntry[0]['AP Inv No'];
        dataToSave['Spp Inv No'] = this.selectedJobChargeEntry[0]['Spp Inv No'];
        // dataToSave['User ID'] = this.selectedJobChargeEntry[0]['User ID'];
        // dataToSave['Date Updated'] = this.selectedJobChargeEntry[0]['Date Updated'];
        dataToSave['Seq No'] = this.selectedJobChargeEntry[0]['Seq No'];
        dataToSave['JobChargesID'] = this.selectedJobChargeEntry[0]['JobChargesID'];
        dataToSave['TxnID'] = this.selectedJobChargeEntry[0]['TxnID'];
        dataToSave['RecordSource'] = this.selectedJobChargeEntry[0]['RecordSource'];
        dataToSave['execnewjc'] = this.selectedJobChargeEntry[0]['execnewjc'];
        temp.splice(Number(this.selectedJobChargeEntry[0]['#']) - 1, 1, dataToSave);
      } else {
        dataToSave['Seq No'] = highestSeq + 1;
        dataToSave['RecordSource'] = '3';
        temp.push(dataToSave);
      }

      this.showInputCharges = false;
      this.showJobChargesTable = false;
      this.$nextTick(() => {
        this.jobChargesDetailsList = temp;
        this.clearAllChargesDetails();
        this.requiredError = [];
        this.updateJCIndex();
        this.selectedJobChargeEntry = [];
        this.saveJCAction = '';
        this.APInvoiceListing = this.filterAPInvoiceListing();
        this.jobChargesListing = this.filterJobChargesListing();
        this.updateJobChargesListStatus();
        this.showJobChargesTable = true;
        this.updateChargesTotals();
      });
    },

    // Close job charges table
    cancelSelectJobCharges: function () {
      this.showJobCharges = false;
      this.selectedJobCharges = [];
    },

    // Close charges input fields
    cancelJobChargesClicked: function () {
      this.showInputCharges = false;
      this.clearAllChargesDetails();
    },

    // Loss focus on charges amount fields
    chargesPriceLossFocus: function (state, currentData) {
      if (!state && !this.viewMode && this.showPopup) {
        switch (currentData) {
          case 'exchgRate':
            this.chargesExchangeRate =
              this.chargesExchangeRate ?
                Number(this.chargesExchangeRate).toFixed(4)
              : Number(this.selectedChargesCurrency['Currency Rate']).toFixed(4);
            this.updateFtoMYRUnitC();
            this.updateMYRTotalC();
            this.updateMYRTaxAmtC();
            this.updateMYRTaxUPriceC();
            this.updateMYRTaxTotalC();
            break;

          case 'qty':
            this.updateFTotalC();
            this.updateMYRTotalC();
            this.updateFTaxAmtC();
            this.updateFTaxTotalC();
            this.updateMYRTaxAmtC();
            this.updateMYRTaxTotalC();
            break;

          case 'unitPrice':
            this.chargesUPrice = Number(this.chargesUPrice).toFixed(4);
            this.updateFTotalC();
            this.updateFtoMYRUnitC();
            this.updateMYRTotalC();
            this.updateChrgTaxRowC();
            break;

          case 'totalPrice':
            this.chargesTotalPrice = Number(this.chargesTotalPrice).toFixed(4);
            this.updateFUnitC();
            this.updateFtoMYRUnitC();
            this.updateMYRTotalC();
            this.updateChrgTaxRowC();
            break;

          case 'unitMYRPrice':
            this.myrChargesUPrice = Number(this.myrChargesUPrice).toFixed(4);
            this.updateMYRtoFUnitC();
            this.updateFTotalC();
            this.updateMYRTotalC();
            this.updateChrgTaxRowC();
            break;

          case 'totalMYRPrice':
            this.myrChargesTotalPrice = Number(this.myrChargesTotalPrice).toFixed(4);
            this.updateMYRUnitC();
            this.updateMYRtoFUnitC();
            this.updateFTotalC();
            this.updateChrgTaxRowC();
            break;

          case 'taxRate':
            this.chargesTaxRate = this.chargesTaxRate ? Number(this.chargesTaxRate).toString() : '0';
            this.updateChrgTaxRowC();
            break;
        }
      }
    },

    // update charges foreign total excluding tax
    updateFTotalC: function () {
      let totalF = 0;
      totalF = Number(this.chargesQty) * Number(this.chargesUPrice.replaceAll(',', ''));
      this.chargesTotalPrice = !isNaN(totalF) ? Number(totalF).toFixed(2) : '0.00';
    },
    // update charges myr unit price (foreign to myr)
    updateFtoMYRUnitC: function () {
      let myrUnit = 0;
      myrUnit =
        (Number(this.chargesUPrice.replaceAll(',', '')) * Number(this.chargesExchangeRate)) /
        Number(this.chargesFactor);
      this.myrChargesUPrice = !isNaN(myrUnit) ? Number(myrUnit).toFixed(4) : '0.0000';
    },
    // update charges myr total excluding tax
    updateMYRTotalC: function () {
      let myrTot = 0;
      myrTot = Number(this.myrChargesUPrice.replaceAll(',', '')) * Number(this.chargesQty);
      this.myrChargesTotalPrice = !isNaN(myrTot) ? Number(myrTot).toFixed(2) : '0.00';
    },
    // update charges foreign unit price (myr to foreign)
    updateMYRtoFUnitC: function () {
      let fUnit = 0;
      fUnit =
        (Number(this.myrChargesUPrice.replaceAll(',', '')) / Number(this.chargesExchangeRate)) *
        Number(this.chargesFactor);
      this.chargesUPrice = !isNaN(fUnit) ? Number(fUnit).toFixed(4) : '0.0000';
    },
    // update foreign unit price
    updateFUnitC: function () {
      let unit = 0;
      unit = Number(this.chargesTotalPrice.replaceAll(',', '')) / Number(this.chargesQty);
      this.chargesUPrice = !isNaN(unit) ? Number(unit).toFixed(4) : '0.0000';
    },
    // update myr unit price
    updateMYRUnitC: function () {
      let unit = 0;
      unit = Number(this.myrChargesTotalPrice.replaceAll(',', '')) / Number(this.chargesQty);
      this.myrChargesUPrice = !isNaN(unit) ? Number(unit).toFixed(4) : '0.0000';
    },
    // update charges foreign tax amount
    updateFTaxAmtC: function () {
      let tax = 0;
      tax = Number(this.chargesTotalPrice.replaceAll(',', '')) * (Number(this.chargesTaxRate) / 100);
      this.chargesTaxAmt = !isNaN(tax) ? Number(tax).toFixed(2) : '0.00';
    },
    // update charges foreign unit price inclusive tax
    updateFTaxUPriceC: function () {
      let taxUnit = 0;
      let unitTaxAmt = 0;
      unitTaxAmt = Number(this.chargesUPrice.replaceAll(',', '')) * (Number(this.chargesTaxRate) / 100);
      taxUnit = Number(this.chargesUPrice.replaceAll(',', '')) + Number(unitTaxAmt);
      this.chargesUPriceInclTax = !isNaN(taxUnit) ? Number(taxUnit).toFixed(4) : '0.0000';
    },
    // update charges foreign total inclusive tax
    updateFTaxTotalC: function () {
      let taxTot = 0;
      taxTot = Number(this.chargesTotalPrice.replaceAll(',', '')) + Number(this.chargesTaxAmt);
      this.chargesTotalPriceInclTax = !isNaN(taxTot) ? Number(taxTot).toFixed(2) : '0.00';
    },
    // update charges myr tax amount
    updateMYRTaxAmtC: function () {
      let myrTax = 0;
      myrTax = Number(this.myrChargesTotalPrice.replaceAll(',', '')) * (Number(this.chargesTaxRate) / 100);
      this.myrChargesTaxAmt = !isNaN(myrTax) ? Number(myrTax).toFixed(2) : '0.00';
    },
    // update charges myr unit price inclusive tax
    updateMYRTaxUPriceC: function () {
      let taxUnit = 0;
      let unitTaxAmt = 0;
      unitTaxAmt = Number(this.myrChargesUPrice.replaceAll(',', '')) * (Number(this.chargesTaxRate) / 100);
      taxUnit = Number(this.myrChargesUPrice.replaceAll(',', '')) + Number(unitTaxAmt);
      this.myrChargesUPriceInclTax = !isNaN(taxUnit) ? Number(taxUnit).toFixed(4) : '0.0000';
    },
    // update charges myr total inclusive tax
    updateMYRTaxTotalC: function () {
      let taxTot = 0;
      taxTot = Number(this.myrChargesTotalPrice.replaceAll(',', '')) + Number(this.myrChargesTaxAmt);
      this.myrChargesTotalPriceInclTax = !isNaN(taxTot) ? Number(taxTot).toFixed(2) : '0.00';
    },
    // update charges tax row
    updateChrgTaxRowC: function () {
      this.updateFTaxAmtC();
      this.updateFTaxUPriceC();
      this.updateFTaxTotalC();
      this.updateMYRTaxAmtC();
      this.updateMYRTaxUPriceC();
      this.updateMYRTaxTotalC();
    },

    // update job charges index
    updateJCIndex: function () {
      const orderedList = this.jobChargesDetailsList.map((n, index) => ({
        ...n,
        '#': index + 1,
      }));
      this.jobChargesDetailsList = orderedList;
    },

    // Populate charges details
    populateChargesDetails: function (data) {
      this.selectedCharges =
        data['Charges Code'].trim() != '' ?
          this.expenseCodeList.find((code) => code['Expenses Code'] === data['Charges Code'])
        : null;
      this.selectedJobNo =
        data['Job No'].trim() != '' ? this.jobNoList.find((job) => job['Job No'] === data['Job No']) : null;
      this.selectedContainer =
        data['Cont No'].trim() != '' ? this.containerList.find((cont) => cont['Cont No'] === data['Cont No']) : null;
      this.selectedJCCostCode =
        data['Cost Code'] != '' ? this.costCodeList.find((code) => code['Cost Code'] === data['Cost Code']) : null;
      this.smkNoJC = data['SMK No'].trim();
      this.jobChargeRefNo = data['Ref #'].trim();
      this.jobChargeRemarks = data['Remark'];
      this.chargesQty = `${data['Qty']}`;
      this.selectedChargesUOM =
        data['UOM'].trim() != '' ? this.uomList.find((uom) => uom['UOM Code'] === data['UOM']) : null;
      this.selectedChargesCurrency =
        data['Currency'] != '' ? this.currencyList.find((curr) => curr['Currency Code'] === data['Currency']) : null;
      this.chargesCurrSelected(this.selectedChargesCurrency);
      this.chargesUPrice = Number(data['F.Unit Price']).toFixed(4) || '0.0000';
      this.chargesTotalPrice = Number(data['F.Amount']).toFixed(2) || '0.00';
      this.chargesExchangeRate = Number(data['Rate']).toFixed(4) || '1.0000';
      this.myrChargesUPrice = Number(data['Unit Price']).toFixed(4) || '0.0000';
      this.myrChargesTotalPrice = Number(data['Amount']).toFixed(2) || '0.00';
      this.selectedChargesTaxCode =
        data['Tax Code'] != '' ? this.taxCodeList.find((tax) => tax['Tax Code'] === data['Tax Code']) : null;
      this.chargesTaxRate = `${Number(data['Tax Rate'])}` || '0';
      this.chargesTaxAmt = Number(data['F.Tax Amt']).toFixed(2) || '0.00';
      this.chargesTotalPriceInclTax = Number(data['F.Net Inclusive Tax']).toFixed(2) || '0.00';
      this.chargesUPriceInclTax = Number(Number(this.chargesTotalPriceInclTax) / Number(this.chargesQty)).toFixed(4);
      this.myrChargesTaxAmt = Number(data['Tax Amt']).toFixed(2) || '0.00';
      this.myrChargesTotalPriceInclTax = Number(data['Net Inclusive Tax']).toFixed(2) || '0.00';
      this.myrChargesUPriceInclTax = Number(Number(this.myrChargesTotalPriceInclTax) / Number(this.chargesQty)).toFixed(
        4,
      );
      this.chargesRebate = Number(data['Rebate Amt']).toFixed(2) || '0.00';
    },

    // update charges total (foreign, local, tax)
    updateChargesTotals: function () {
      let totalTax = 0;
      let totalMYRAmt = 0;
      let totalFAmt = 0;
      this.jobChargesDetailsList.forEach((chrg) => {
        totalTax += Number(chrg['Tax Amt']);
        totalMYRAmt += Number(chrg['Net Inclusive Tax']);
        totalFAmt += Number(chrg['F.Net Inclusive Tax']);
      });
      this.totalTaxCharges = this.$currencyFormat(`${totalTax}`);
      this.totalAmountCharges = this.$currencyFormat(`${totalMYRAmt}`);
      this.totalFAmountCharges = this.$currencyFormat(`${totalFAmt}`);

      this.jcMultiCurr = !this.jobChargesDetailsList.every(
        (d) => d['Currency'] == this.selectedHeaderCurr['Currency Code'],
      );
      this.updatePaymentTotals();
    },
    //#end region

    //#region Direct Posting Tab functions
    // Check if payee type and code is selected, then open expenses
    openExpenses: async function () {
      const discard = await this.checkPayeeTypeCodeSelected();
      if (discard) {
        if (this.oriExpensesListing.length == 0) {
          await this.refreshClicked('Expenses');
        }
        this.showExpenses = true;
        this.showInputDirectPosting = false;
      }
    },

    // Behaviour when select all/deselect all expenses
    selectDeselectExpensesClicked: function () {
      if (this.selectedExpenses.length != this.expensesListing.length) {
        this.selectedExpenses = [];
        this.selectedExpenses = [...this.expensesListing];
      } else {
        this.selectedExpenses = [];
      }
    },

    // Add selected expenses
    addSelectedExpenses: async function () {
      if (this.selectedExpenses.length == 0) {
        await this.$showAlert('error', 'No Expenses Selected', 'Please select at least 1 Expense to add.');
        return;
      }

      const temp = this.directPostingDetailsList;
      this.directPostingDetailsList = [];
      for (const exp of this.selectedExpenses) {
        const highestSeq = temp.reduce((max, d) => (parseInt(d['Seq No']) > max ? parseInt(d['Seq No']) : max), 0);
        const debit = this.debitAccList.find((debit) => debit['Acc No'] === exp['Debit Acc']);
        const dataToSave = {
          '#': '',
          action: 'N',
          Debit: exp['Debit Acc'],
          'Dr. Acc. Desc': debit ? debit['Description'] : '',
          Amount: exp['Amount'],
          'Tax Dr.Acc.': exp['Tax Dr Acc.'],
          'Tax Code': exp['Tax Code'],
          'Tax Rate': exp['Tax Rate'],
          'Taxable Amt': exp['Tax Amount'],
          'Net Inclusive Tax': exp['Net Incl Tax'],
          'F.Amount': exp['F.Amount'],
          'F.Taxable Amt': exp['F.Tax Amount'] ?? '0.00',
          'F.Net Inclusive Tax': exp['F.Net Incl Tax'],
          Currency: exp['Currency'],
          Rate: exp['Rate'],
          UserID: '',
          'Date Update': '',
          Remark: exp['Remark'],
          'Seq No': highestSeq + 1,
          'Cost Code': '',
          'Vehicle No.': exp['Vehicle No'],
          'SMK.No.': '',
          'Payee Name': '',
          'Registration No.': '',
          'GST License No.': '',
          'Invoice No.': '',
          'Invoice Date': '',
          ExpenseGUID: exp['ExpenseGUID'],
        };
        temp.push(dataToSave);
      }

      this.showExpenses = false;
      this.showDirectPostingTable = false;
      this.$nextTick(() => {
        this.directPostingDetailsList = temp;
        this.updateDPIndex();
        this.showDirectPostingTable = true;
        this.expensesListing = this.filterExpensesListing();
        this.updateDPTotals();
      });
    },

    // Close expenses table
    cancelSelectExpenses: function () {
      this.showExpenses = false;
      this.selectedExpenses = [];
    },

    // Open direct posting input fields
    openNewDirectPostingFields: function () {
      if (Number(this.headerExchRate) == 0) {
        this.headerExchRateErr = 'Exchange Rate is invalid!';
        this.requiredError.push({
          key: 'headerrate',
          value: this.headerExchRateErr,
        });
        return;
      }
      this.selectedDPCurrency = this.currencyList.find(
        (curr) =>
          curr['Currency Code'] === (this.selectedHeaderCurr ? this.selectedHeaderCurr['Currency Code'] : 'MYR'),
      );
      this.dpCurrSelected(this.selectedDPCurrency);
      this.dpExchangeRate = Number(this.headerExchRate).toFixed(4);
      this.dpFactor = Number(this.headerExchFactor);
      this.dpTotalPrice = this.$currencyFormat('0');
      this.myrDPTotalPrice = this.$currencyFormat('0');
      this.dpTaxAmt = this.$currencyFormat('0');
      this.dpTotalPriceInclTax = this.$currencyFormat('0');
      this.myrDPTaxAmt = this.$currencyFormat('0');
      this.myrDPTotalPriceInclTax = this.$currencyFormat('0');
      this.dpTaxRate = '0';

      this.showExpenses = false;
      this.saveDPAction = 'N';
      this.subtitleDP = 'New Direct Posting';
      this.showInputDirectPosting = true;
    },

    // Delete selected direct posting
    deleteDirectPosting: async function () {
      if (this.selectedDirectPostingEntry.length == 0) {
        this.$showAlert('error', 'No Direct Posting Selected', 'Please select at least 1 direct posting to delete.');
        return;
      }

      const confirmCancel = await this.$showAlert(
        'warning',
        'Deleting Direct Posting Record',
        `Are you sure you want to delete this record ?`,
        [
          { text: 'Yes', color: 'indianred', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );
      if (confirmCancel == 'No') return;

      let temp = [];
      if (this.selectedDirectPostingEntry[0]['action'] && this.selectedDirectPostingEntry[0]['action'] == 'N') {
        temp = this.directPostingDetailsList.filter((j) => j['Seq No'] != this.selectedDirectPostingEntry[0]['Seq No']);
      } else {
        this.directPostingDetailsList.forEach((j) => {
          if (j['Seq No'] == this.selectedDirectPostingEntry[0]['Seq No']) {
            j['action'] = 'D';
            this.deletedDirectPostingDetails.push(j);
          }
        });
        temp = this.directPostingDetailsList.filter((j) => j['action'] != 'D');
      }

      this.directPostingDetailsList = [];
      this.showDirectPostingTable = false;
      this.$nextTick(() => {
        this.directPostingDetailsList = temp;
        this.selectedDirectPostingEntry = [];
        this.clearAllDirectPostingDetails();
        this.updateDPIndex();
        this.updateDPTotals();
        this.expensesListing = this.filterExpensesListing();
        this.showDirectPostingTable = true;
      });
    },

    // Check direct posting input fields
    checkDPFields: function () {
      this.clearAllDPError();
      let error = false;
      if (!this.selectedDebitAcc) {
        this.selectedDebitAccErr = 'Debit Acc is required!';
        this.requiredError.push({
          key: 'dpdebitacc',
          value: this.selectedDebitAccErr,
        });
        error = true;
      }

      if (!this.selectedDPCurrency) {
        this.dpCurrencyErr = 'Currency is required!';
        this.requiredError.push({
          key: 'dpCurrency',
          value: this.dpCurrencyErr,
        });
        error = true;
      } else if (
        this.selectedHeaderCurr &&
        this.selectedHeaderCurr['Currency Code'] != 'MYR' &&
        this.selectedDPCurrency['Currency Code'] != this.selectedHeaderCurr['Currency Code']
      ) {
        this.dpCurrencyErr = 'Selected currency is different from header currency!';
        this.requiredError.push({
          key: 'dpCurrency',
          value: this.dpCurrencyErr,
        });
        error = true;
      }

      if (!this.dpExchangeRate.trim()) {
        this.dpExchangeRateErr = 'Exchange Rate is required!';
        this.requiredError.push({
          key: 'dpexchrate',
          value: this.dpExchangeRateErr,
        });
      } else if (Number(this.dpExchangeRate) == 0) {
        this.dpExchangeRateErr = 'Exchange Rate is invalid!';
        this.requiredError.push({
          key: 'dpexchrate',
          value: this.dpExchangeRateErr,
        });
        error = true;
      } else if (
        this.selectedHeaderCurr &&
        this.selectedHeaderCurr['Currency Code'] != 'MYR' &&
        Number(this.dpExchangeRate) != Number(this.headerExchRate)
      ) {
        this.dpExchangeRateErr = 'Selected exchange rate is different from header exchange rate!';
        this.requiredError.push({
          key: 'dpexchrate',
          value: this.dpExchangeRateErr,
        });
        error = true;
      }

      if (this.smkNoDP.trim().length > 35) {
        this.smkNoDPErr = 'SMK No cannot exceed 35 characters!';
        this.requiredError.push({ key: 'dpsmkno', value: this.smkNoDPErr });
        error = true;
      }

      if (this.gstInvDateChecked && !this.gstInvDate) {
        this.gstInvDateErr = 'Invoice Date is required!';
        this.requiredError.push({
          key: 'gstinvdate',
          value: this.gstInvDateErr,
        });
        error = true;
      }
      return error;
    },

    // Add direct posting
    saveDirectPosting: function () {
      this.clearAllDPError();
      if (this.checkDPFields()) return;
      const temp = this.directPostingDetailsList;
      this.directPostingDetailsList = [];
      const highestSeq = temp.reduce((max, d) => (parseInt(d['Seq No']) > max ? parseInt(d['Seq No']) : max), 0);
      const dataToSave = {
        '#': '',
        action: this.action == 'N' ? 'N' : this.saveDPAction,
        Debit: this.selectedDebitAcc ? this.selectedDebitAcc['Acc No'] : '',
        'Dr. Acc. Desc': this.selectedDebitAcc ? this.selectedDebitAcc['Description'] : '',
        Amount: Number(this.myrDPTotalPrice || 0).toFixed(2),
        'Tax Dr.Acc.': this.selectedTaxDebitAcc ? this.selectedTaxDebitAcc['Acc No'] : '',
        'Tax Code': this.selectedDPTaxCode ? this.selectedDPTaxCode['Tax Code'] : '',
        'Tax Rate': this.dpTaxRate || '0',
        'Taxable Amt': Number(this.myrDPTaxAmt || 0).toFixed(2),
        'Net Inclusive Tax': Number(this.myrDPTotalPriceInclTax || 0).toFixed(2),
        'F.Amount': Number(this.dpTotalPrice || 0).toFixed(2),
        'F.Taxable Amt': Number(this.dpTaxAmt || 0).toFixed(2),
        'F.Net Inclusive Tax': Number(this.dpTotalPriceInclTax || 0).toFixed(2),
        Currency: this.selectedDPCurrency ? this.selectedDPCurrency['Currency Code'] : 'MYR',
        Rate: Number(this.dpExchangeRate || 1).toFixed(4),
        UserID: '',
        'Date Update': '',
        Remark: this.directPostingRemarks || '',
        'Seq No': '',
        'Cost Code': this.selectedDPCostCode ? this.selectedDPCostCode['Cost Code'] : '',
        'Vehicle No.': this.selectedDPVehicleNo ? this.selectedDPVehicleNo['Lorry No'] : '',
        'SMK.No.': this.smkNoDP || '',
        'Payee Name': this.gstPayeeName || '',
        'Registration No.': this.gstRegNo || '',
        'GST License No.': this.gstLicenseNo || '',
        'Invoice No.': this.gstInvNo || '',
        'Invoice Date': this.gstInvDateChecked ? new Date(this.gstInvDate).formatString('yyyy-MM-dd') : '',
        ExpenseGUID: '',
      };

      if (this.selectedDirectPostingEntry.length > 0) {
        // dataToSave['User ID'] = this.selectedDirectPostingEntry[0]['User ID'];
        // dataToSave['Date Updated'] = this.selectedDirectPostingEntry[0]['Date Updated'];
        dataToSave['Seq No'] = this.selectedDirectPostingEntry[0]['Seq No'];
        dataToSave['ExpenseGUID'] = this.selectedDirectPostingEntry[0]['ExpenseGUID'];
        temp.splice(Number(this.selectedDirectPostingEntry[0]['#']) - 1, 1, dataToSave);
      } else {
        dataToSave['Seq No'] = highestSeq + 1;
        temp.push(dataToSave);
      }

      this.showInputDirectPosting = false;
      this.showDirectPostingTable = false;
      this.$nextTick(() => {
        this.directPostingDetailsList = temp;
        this.clearAllDirectPostingDetails();
        this.updateDPIndex();
        this.selectedDirectPostingEntry = [];
        this.saveDPAction = '';
        this.expensesListing = this.filterExpensesListing();
        this.showDirectPostingTable = true;
        this.updateDPTotals();
      });
    },

    // Close direct posting input fields
    cancelDirectPostingClicked: function () {
      this.clearAllDirectPostingDetails();
      this.showInputDirectPosting = false;
    },

    // Button clicked on direct posting table
    directPostingTableButtonClicked: function (_btn, data) {
      this.saveDPAction = 'E';
      this.subtitleDP = 'Edit Direct Posting';
      this.showInputDirectPosting = true;
      this.selectedDirectPostingEntry = [data];
      this.populateDPDetails(data);
    },

    // Loss focus on direct posting amount fields
    dpPriceLossFocus: function (state, currentData) {
      if (!state && !this.viewMode && this.showPopup) {
        switch (currentData) {
          case 'totalPrice':
            this.dpTotalPrice = Number(this.dpTotalPrice).toFixed(4);
            this.updateMYRTotalD();
            this.updateDPTaxRows();
            break;

          case 'exchgRate':
            this.dpExchangeRate =
              this.dpExchangeRate ?
                Number(this.dpExchangeRate).toFixed(4)
              : Number(this.selectedDPCurrency['Currency Rate']).toFixed(4);
            this.updateMYRTotalD();
            this.updateDPTaxRows();
            break;

          case 'totalMYRPrice':
            this.myrDPTotalPrice = Number(this.myrDPTotalPrice).toFixed(4);
            this.updateFTotalD();
            this.updateDPTaxRows();
            break;

          case 'taxRate':
            this.dpTaxRate = this.dpTaxRate ? Number(this.dpTaxRate).toString() : '0';
            this.updateDPTaxRows();
            break;
        }
      }
    },

    // Update dp foreign total price excluding tax
    updateFTotalD: function () {
      let total = 0;
      total = (Number(this.myrDPTotalPrice.replaceAll(',', '')) / Number(this.dpExchangeRate)) * Number(this.dpFactor);
      this.dpTotalPrice = Number(total).toFixed(2);
    },
    // Update dp myr total price excluding tax
    updateMYRTotalD: function () {
      let total = 0;
      total = (Number(this.dpTotalPrice.replaceAll(',', '')) * Number(this.dpExchangeRate)) / Number(this.dpFactor);
      this.myrDPTotalPrice = !isNaN(total) ? Number(total).toFixed(2) : '0.00';
    },
    // Update dp foreign tax amount
    updateFTaxAmtD: function () {
      let tax = 0;
      tax = Number(this.dpTotalPrice.replaceAll(',', '')) * (Number(this.dpTaxRate) / 100);
      this.dpTaxAmt = !isNaN(tax) ? Number(tax).toFixed(2) : '0.00';
    },
    // update dp foreign total price inclusive tax
    updateFTaxTotalD: function () {
      let taxTot = 0;
      taxTot = Number(this.dpTotalPrice.replaceAll(',', '')) + Number(this.dpTaxAmt);
      this.dpTotalPriceInclTax = !isNaN(taxTot) ? Number(taxTot).toFixed(2) : '0.00';
    },
    // Update dp myr tax amount
    updateMYRTaxAmtD: function () {
      let tax = 0;
      tax = Number(this.myrDPTotalPrice.replaceAll(',', '')) * (Number(this.dpTaxRate) / 100);
      this.myrDPTaxAmt = !isNaN(tax) ? Number(tax).toFixed(2) : '0.00';
    },
    // Update dp myr total price inclusive tax
    updateMYRTaxTotalD: function () {
      let taxTot = 0;
      taxTot = Number(this.myrDPTotalPrice.replaceAll(',', '')) + Number(this.myrDPTaxAmt);
      this.myrDPTotalPriceInclTax = !isNaN(taxTot) ? Number(taxTot).toFixed(2) : '0.00';
    },
    // Update dp tax rows
    updateDPTaxRows: function () {
      this.updateFTaxAmtD();
      this.updateFTaxTotalD();
      this.updateMYRTaxAmtD();
      this.updateMYRTaxTotalD();
    },

    // Update dp listing index
    updateDPIndex: function () {
      const orderedList = this.directPostingDetailsList.map((n, index) => ({
        ...n,
        '#': index + 1,
      }));
      this.directPostingDetailsList = orderedList;
    },

    // Update dp totals (tax, foreign, local)
    updateDPTotals: function () {
      let totalTax = 0;
      let totalMYRAmt = 0;
      let totalFAmt = 0;

      this.directPostingDetailsList.forEach((dp) => {
        totalTax += Number(dp['Taxable Amt']);
        totalMYRAmt += Number(dp['Net Inclusive Tax']);
        totalFAmt += Number(dp['F.Net Inclusive Tax']);
      });

      this.totalTaxDP = this.$currencyFormat(`${totalTax}`);
      this.totalDPAmt = this.$currencyFormat(`${totalMYRAmt}`);
      this.totalDPFAmt = this.$currencyFormat(`${totalFAmt}`);

      this.dpMultiCurr = !this.directPostingDetailsList.every(
        (d) => d['Currency'] == this.selectedHeaderCurr['Currency Code'],
      );
      this.updatePaymentTotals();
    },

    // Populate DP details
    populateDPDetails: function (data) {
      this.selectedDebitAcc = data['Debit'] != '' ? this.debitAccList.find((d) => d['Acc No'] === data['Debit']) : null;
      this.selectedTaxDebitAcc =
        data['Tax Dr.Acc.'] != '' ? this.debitAccList.find((d) => d['Acc No'] === data['Tax Dr.Acc.']) : null;
      this.selectedDPCostCode =
        data['Cost Code'] != '' ? this.costCodeList.find((c) => c['Cost Code'] === data['Cost Code']) : null;
      this.smkNoDP = data['SMK.No.'] || '';
      this.selectedDPVehicleNo =
        data['Vehicle No.'] != '' ? this.vehicleNoList.find((v) => v['Lorry No'] === data['Vehicle No.']) : null;
      this.directPostingRemarks = data['Remark'] || '';
      this.selectedDPCurrency =
        data['Currency'] != '' ? this.currencyList.find((c) => c['Currency Code'] === data['Currency']) : null;
      this.dpCurrSelected(this.selectedDPCurrency);
      this.dpTotalPrice = Number(data['F.Amount']).toFixed(2) || '0.00';
      this.dpExchangeRate = Number(data['Rate']).toFixed(4) || '1.0000';
      this.myrDPTotalPrice = Number(data['Amount']).toFixed(2) || '0.00';
      this.selectedDPTaxCode =
        data['Tax Code'] != '' ? this.taxCodeList.find((t) => t['Tax Code'] === data['Tax Code']) : null;
      this.dpTaxRate = Number(data['Tax Rate']).toFixed(0) || '0';
      this.dpTaxAmt = Number(data['F.Taxable Amt']).toFixed(2) || '0.00';
      this.dpTotalPriceInclTax = Number(data['F.Net Inclusive Tax']).toFixed(2) || '0.00';
      this.myrDPTaxAmt = Number(data['Taxable Amt']).toFixed(2) || '0.00';
      this.myrDPTotalPriceInclTax = Number(data['Net Inclusive Tax']).toFixed(2) || '0.00';
      this.gstPayeeName = data['Payee Name'] || '';
      this.gstRegNo = data['Registration No.'] || '';
      this.gstLicenseNo = data['GST License No.'] || '';
      this.gstInvNo = data['Invoice No.'] || '';
      this.gstInvDate = data['Invoice Date'] ? new Date(data['Invoice Date']) : new Date();
      this.gstInvDateChecked = data['Invoice Date'] != '';
      this.gstDetailsCollapse = !(
        this.gstPayeeName ||
        this.gstRegNo ||
        this.gstLicenseNo ||
        this.gstInvNo ||
        this.gstInvDateChecked
      );
    },

    // Filter expenses listing
    filterExpensesListing: function () {
      const saved = [];
      this.directPostingDetailsList.forEach((d) => {
        if (d['ExpenseGUID'] != '') saved.push(d['ExpenseGUID']);
      });
      const filter1 = this.oriExpensesListing.filter((exp) => exp['Debit Acc'] != '');
      filter1.splice(0, 1);
      return filter1.filter((exp) => !saved.includes(exp['ExpenseGUID']));
    },
    //#endregion

    //#region Attachment Tab functions
    // Open attachment popup when click on add/edit
    openAttachmentPopup: function (type) {
      this.clearAllAttData();
      switch (type) {
        case 'new': {
          this.attachmentsAction = 'N';
          this.selectedAtt = [];
          this.attachmentsPopupButtons = [{ text: 'Add' }, { text: 'Cancel', color: 'lightgray' }];
          this.attachmentsPopupTitle = 'Add Attachment';
          break;
        }

        case 'edit': {
          if (this.selectedAtt.length == 0) {
            this.$showAlert('error', 'No Attachment Selected', 'Please select an Attachment to edit');
            return;
          }
          this.attachmentsAction = 'E';
          this.attachmentsPopupButtons = [{ text: 'Edit' }, { text: 'Cancel', color: 'lightgray' }];
          this.attachmentsPopupTitle = 'Edit Attachment';

          this.documents = {
            docName: this.selectedAtt[0]['File Name'],
            attachmentFile: this.selectedAtt[0]['Doc Image'],
            filetype: this.selectedAtt[0]['File Type'],
            docGuid: this.selectedAtt[0]['Document GUID'],
            remarks: this.selectedAtt[0]['Doc Remark'],
            docID: this.selectedAtt[0]['ID'],
          };
          break;
        }
      }
      this.showAttachmentsPopup = true;
    },

    // Delete selected attachment
    deleteDocClicked: async function () {
      if (this.selectedAtt.length == 0) {
        this.$showAlert('error', 'No Document Selected', 'Please select at least 1 document to delete.');
        return;
      }

      const confirmCancel = await this.$showAlert(
        'warning',
        'Deleting Attachment',
        `Are you sure you want to delete this attachment ?`,
        [
          { text: 'Yes', color: 'red', textColor: 'white' },
          { text: 'No', color: 'lightgray', textColor: 'black' },
        ],
      );
      if (confirmCancel == 'No') return;

      let existingData = false;
      const tempDeleted = this.deletedAttList;
      this.deletedAttList = [];

      for (const d of this.selectedAtt) {
        const doc = {
          Action: 'D',
          'Document GUID': d['Document GUID'],
        };

        if (d['Document GUID']) {
          tempDeleted.push(doc);
          existingData = true;
        }
      }

      const temp = this.attachmentsList;
      const index = temp.findIndex((item) => {
        return existingData ?
            item['Document GUID'] == this.selectedAtt[0]['Document GUID']
          : item['ID'] == this.selectedAtt[0]['ID'];
      });
      temp.splice(index, 1);
      this.attachmentsList = [];

      this.$nextTick(() => {
        this.deletedAttList = tempDeleted;
        this.attachmentsList = temp;
        this.selectedAtt = [];
      });
    },

    // Buttons on attachment table clicked
    attAdditionalBtnsClicked: async function (btn, data) {
      switch (btn) {
        case 'View':
          this.attachmentsAction = 'V';
          await this.projectAttachment({
            'File Name': data['File Name'],
            'MIME Type': null,
            'File Data': data['Doc Image'],
          });
          break;
      }
    },

    // Buttons on attachment popup clicked
    attachmentsPopupBtnsClicked: async function (btn) {
      let temp;
      let highestID = 1;
      let doc = null;

      switch (btn) {
        case 'Add': {
          if (!this.documents['docName']) {
            this.attachmentsRequiredErrorMessage = 'Please attach at least one file';
            return;
          }

          if (this.attachmentsList.length > 0) {
            let duplicate = false;
            this.attachmentsList.forEach((d) => {
              if (d['File Name'] === this.documents['docName']) duplicate = true;
            });

            if (duplicate) {
              await this.$showAlert('error', 'Selected file already exist!', 'Please select other attachment');
              return;
            }
          }

          temp = [...this.attachmentsList];
          this.attachmentsList = [];
          highestID = temp.reduce((max, d) => (d['ID'] > max ? d['ID'] : max), 1);

          doc = {
            Action: 'N',
            'Doc Remark': this.documents['remarks'] ? this.documents['remarks'] : '',
            'Doc Date': new Date().formatString('yyyy-MM-dd'),
            'File Name': this.documents['docName'] ? this.documents['docName'] : '',
            'File Type': this.documents['fileType'] ? this.documents['fileType'] : '',
            'Doc Image': this.documents['attachmentFile'] ? this.documents['attachmentFile'] : '',
            'Document GUID': this.documents['docGuid'] ? this.documents['docGuid'] : '',
            ID: this.documents['docID'] ? this.documents['docID'] : ++highestID,
            _attachmentAction: 'New',
          };

          this.$nextTick(() => {
            temp.push(doc);
            this.attachmentsList = temp;
            this.oldDoc = this.documents['docName'];
            this.clearAllAttData();
          });
          this.showAttachmentsPopup = false;
          break;
        }

        case 'Edit': {
          if (this.attachmentsList.length > 0) {
            let duplicate = false;
            this.attachmentsList.forEach((d) => {
              if (
                d['File Name'] === this.documents['docName'] &&
                this.documents['docName'] != this.selectedAtt[0]['File Name']
              )
                duplicate = true;
            });

            if (duplicate) {
              this.$showAlert('error', 'Selected file already exist!', 'Please select other attachment');
              return;
            }
          }
          temp = [...this.attachmentsList];
          highestID = temp.reduce((max, d) => (d['ID'] > max ? d['ID'] : max), 1);

          let existingData = false;
          for (const d of this.selectedAtt) {
            doc = {
              Action: d['Action'] ? d['Action'] : 'E',
              'Doc Remark': this.documents['remarks'] ? this.documents['remarks'] : '',
              'Doc Date': new Date().formatString('yyyy-MM-dd'),
              'File Name': this.documents['docName'] ? this.documents['docName'] : '',
              'File Type': this.documents['filetype'] ? this.documents['filetype'] : '',
              'Doc Image': this.documents['attachmentFile'] ? this.documents['attachmentFile'] : '',
              'Document GUID': this.documents['docGuid'] ? this.documents['docGuid'] : '',
              ID: this.documents['docID'] ? this.documents['docID'] : ++highestID,
            };

            if (d['Document GUID']) existingData = true;
          }

          const temp2 = this.attachmentsList;
          const index = temp2.findIndex((item) => {
            return existingData ?
                item['Document GUID'] == this.selectedAtt[0]['Document GUID']
              : item['ID'] == this.selectedAtt[0]['ID'];
          });
          temp2.splice(index, 1, doc);
          this.attachmentsList = [];
          this.$nextTick(() => {
            this.oldDoc = this.documents['docName'];
            this.attachmentsList = temp2;
            this.clearAllAttData();
          });
          this.showAttachmentsPopup = false;
          break;
        }

        case 'Cancel':
          this.showAttachmentsPopup = false;
          this.clearAllAttData();
          break;
      }
    },

    // handler for file upload
    handleFileUpload: async function (file) {
      if (file.length == 0) {
        this.documents['docName'] = '';
        this.documents['fileType'] = '';
        this.documents['attachmentFile'] = '';
      } else {
        this.$fullscreenLoading('Adding File');
        this.documents['docName'] = file[0].name;
        this.documents['fileType'] = file[0].name.substring(file[0].name.indexOf('.'));
        const tempAtt = await file[0].toBase64();
        this.documents['attachmentFile'] = tempAtt.substring(tempAtt.indexOf(',') + 1);
        this.$fullscreenLoading();
      }
    },

    // Project attchment to viewer
    projectAttachment: async function (file) {
      if (!file && process.env['NODE_ENV'] == 'development') {
        throw new Error('No file was passed to projectAttachment. Check for logic errors');
      }

      this.$fullscreenLoading('Preparing file');
      if (file instanceof File) {
        this.attachmentProjectorFileName = file['name'];
        this.attachmentProjectorType = file['type'];
        this.attachmentProjectorData = URL.createObjectURL(file);
      } else {
        this.attachmentProjectorFileName = file['File Name'];
        this.attachmentProjectorType =
          file['MIME Type'] ||
          (await this.$apiCallV2(`/Payment/GetMIMEType?filename=${this.attachmentProjectorFileName}`));

        const byteCharacters = atob(
          file['File Data'].startsWith('data:') ? file['File Data'].split(',')[1] : file['File Data'],
        );

        const untypedByteArray = [];
        for (let i = 0; i < byteCharacters.length; i++) {
          untypedByteArray.push(byteCharacters.charCodeAt(i));
        }

        const blob = new Blob([new Uint8Array(untypedByteArray)], {
          type: this.attachmentProjectorType,
        });
        this.attachmentProjectorData = URL.createObjectURL(blob);
      }

      this.isProjectable = this.attachmentProjectable();
      this.showAttachmentProjector = true;
      this.$fullscreenLoading();
    },

    // Download attachment
    downloadAttachment: function () {
      const anchor = document.createElement('a');
      anchor['href'] = this.attachmentProjectorData;
      anchor['download'] = this.selectedAtt[0]['File Name'];
      anchor.click();
    },

    // Close attachment projector
    closeProjector: function () {
      this.attachmentProjectorType = '';
      this.attachmentProjectorData = '';
      this.showAttachmentProjector = false;
    },

    // Check if attachment is projectable
    attachmentProjectable: function () {
      if (!this.attachmentProjectorFileName || !this.attachmentProjectorType) {
        return false;
      }

      const fileNameSplit = this.attachmentProjectorFileName.trim().split('.');
      const fileExtension = fileNameSplit[fileNameSplit.length - 1];
      const projectableExtensions = ['pdf', 'txt', 'json', 'mp3', 'wav', 'mp4', 'webm', 'ogg'];
      return this.attachmentProjectorType.startsWith('image/') || projectableExtensions.includes(fileExtension);
    },
    //#endregion

    //#region Events Tab functions
    //#endregion

    //#region input query selected
    // Get job listing when payment type selected
    payTypeSelected: async function (type) {
      this.$fullscreenLoading('Getting Job No');
      await this.getJobTypeListing(type);
      this.$fullscreenLoading();

      // this.viewMode = this.action == '';
      // this.disablePaymentNo = !type['DocManual'] || type['DocManual'] == '0';
      // this.paymentNo = this.disablePaymentNo ? '**********' : '';
    },

    getJobTypeListing: async function (type) {
      this.jobNoList = await this.$apiCallV2(`/Payment/GetJobListing`, {
        top: this.jobnoRowLimit,
        jobtypeid: type['JobTypeID'],
      });

      this.viewMode = this.action == '';
      this.disablePaymentNo = !type['DocManual'] || type['DocManual'] == '0';
      this.paymentNo = this.disablePaymentNo ? '**********' : '';
    },

    // Alert user if payee type changes when side input fields opened
    payeeTypeSelected: async function (newType, oldType) {
      let discard = 'Discard';
      if (this.showAPInvoices || this.showJobCharges || this.showExpenses) {
        discard = await this.$showAlert(
          'warning',
          'Discard Changes',
          "There's already data added in the tabs. Do you wish to discard the changes and change payee type?",
          [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
        );
      }
      if (discard == 'Cancel') {
        this.selectedPayeeType = oldType;
        return;
      }

      this.showAPInvoices = false;
      this.showJobCharges = false;
      this.showExpenses = false;

      this.payeeCodeNameDisabled = !newType;
      this.$fullscreenLoading('Getting Payee Codes');
      await this.getPayeeCodeList(newType);
      this.$fullscreenLoading();
      this.selectedPayeeCode = null;
      this.payeeName = '';
    },

    // Get payee bank details and ap invoice listing when payee code selected
    payeeCodeSelected: async function (newCode) {
      this.payeeName = newCode ? newCode[this.payeeCodeDescKey] : '';
      const apinvPostBody = {
        payeetype: this.selectedPayeeType['Party Code'],
        payeecode: this.selectedPayeeCode[this.payeeCodeKey],
        currency: this.selectedHeaderCurr ? this.selectedHeaderCurr['Currency Code'] : 'MYR',
      };
      const apInvOpen = this.showAPInvoices;
      this.$fullscreenLoading("Getting AP Invoice Listing");
      this.oriAPInvoiceListing = await this.$apiCallV2('/Payment/GetPaymentAPInvoiceListing', apinvPostBody);
      this.$fullscreenLoading();
      this.showAPInvoices = false;
      this.$nextTick(() => {
        this.APInvoiceListing = this.filterAPInvoiceListing();
        if (apInvOpen) this.showAPInvoices = true;
      });

      await this.refreshClicked('Job');
      const selectJCOpen = this.showJobCharges;
      this.showJobCharges = false;
      this.$nextTick(() => {
        this.jobChargesListing = this.filterJobChargesListing();
        this.updateJobChargesListStatus();
        if (selectJCOpen) this.showJobCharges = true;
      });

      if (newCode) {
        const bankDetails = (
          await this.$apiCallV2(`/Master/GetDetailsMaster`, {
            module: 'Master',
            masterfile: 'PAYEEBANK',
            recordid: newCode[this.payeeCodeKey],
            payeetype: this.selectedPayeeType['Party Code'],
          })
        )['PayeeBank'];
        if (bankDetails) {
          this.bankName = bankDetails['Bank Name'];
          this.bankAccNo = bankDetails['Bank Acc No'];
          this.bankSwift = bankDetails['Bank SwiftCode'];
          this.coRegNo = bankDetails['Co Registration No'];
        }
      }
    },

    // Check if selected cont matches with selected job
    contNoSelected: function (newCont) {
      if (!this.selectedJobNo) {
        this.selectedJobNo = this.jobNoList.find((j) => j['Job No'] == newCont['Job No']);
      }
    },

    // Charges currency selected
    chargesCurrSelected: function (newCurr) {
      const rate =
        newCurr['Currency Code'] == this.selectedHeaderCurr['Currency Code'] ? this.headerExchRate
        : newCurr ? Number(newCurr['Currency Rate']).toFixed(4)
        : '1.0000';
      this.chargesExchangeRate = rate;
      this.chargeExchgRateDisabled = newCurr && newCurr['Currency Code'] === 'MYR';
      this.chargesFactor = newCurr ? newCurr['Factor'] : '1';

      this.chargesPriceLossFocus(false, 'exchgRate');
    },

    // Charges tax code selected
    taxCodeChrgSelected: function (newCode) {
      this.chargesTaxRate = newCode ? newCode['Tax Rate'].toString() : '0';
      this.chargesPriceLossFocus(false, 'taxRate');
    },

    // DP currency selected
    dpCurrSelected: function (newCurr) {
      const rate =
        newCurr['Currency Code'] == this.selectedHeaderCurr['Currency Code'] ? this.headerExchRate
        : newCurr ? Number(newCurr['Currency Rate']).toFixed(4)
        : '1.0000';
      this.dpExchangeRate = rate;
      this.dpExchgRateDisabled = newCurr && newCurr['Currency Code'] === 'MYR';
      this.dpFactor = newCurr ? newCurr['Factor'] : '1';

      this.dpPriceLossFocus(false, 'exchgRate');
    },

    // DP tax code selected
    taxCodeDPSelected: function (newCode) {
      this.dpTaxRate = newCode ? newCode['Tax Rate'].toString() : '0';
      this.dpPriceLossFocus(false, 'taxRate');
    },

    // Check if selected currency is 0, if yes then show error, else update exchange rate and factor
    headerCurrencySelected: async function (newCurr) {
      this.headerExchRate = newCurr ? Number(newCurr['Currency Rate']).toFixed(4) : '1.0000';
      if (newCurr && Number(newCurr['Currency Rate']) == 0) {
        await this.$showAlert('error', 'Exchange Rate Invalid', 'Exchange rate cannot be 0!');
        this.headerExchRate = '1.0000';
      }
      this.headerExchFactor = newCurr ? newCurr['Factor'].toString() : '';
      this.oldRate = newCurr ? newCurr['Currency Rate'] : '';
      this.selectedChargesCurrency = this.currencyList.find(
        (c) => c['Currency Code'] == (newCurr ? newCurr['Currency Code'] : 'MYR'),
      );
      this.chargeExchgRateDisabled = this.selectedChargesCurrency['Currency Code'] === 'MYR';
      this.selectedDPCurrency = this.currencyList.find(
        (c) => c['Currency Code'] == (newCurr ? newCurr['Currency Code'] : 'MYR'),
      );
      this.dpExchgRateDisabled = this.selectedDPCurrency['Currency Code'] === 'MYR';
    },

    paymentMethodSelected: function (newMethod) {
      this.createCheque = newMethod && newMethod['Payment Type'] == 'CS' && this.allowChq;
    },
    //#endregion
  },
  async mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Payment') {
        x['data'].forEach((k) => {
          const selectedMenu = this.menu.find((m) => m['key'] == k['Control']);
          if (selectedMenu) {
            selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] != 'False';
          }

          const fieldKeys = Object.keys(this.sasFieldsControl);
          if (fieldKeys.includes(k['Control'])) {
            this.sasFieldsControl[k['Control']] = k['PropertiesValue'] != 'False';
          }
        });
      }
    });

    const mountedPromises = [];
    let mountedPromisesFulfilledCount = 0;
    mountedPromises.push(
      this.$apiCallV2('/Payment/GetJobTypeConfig', { doctype: 'P' }).then((result) => {
        this.paymentTypeList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'PAYMENTTYPE',
      }).then((result) => {
        this.paymentMethodList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PARTY', module: 'PAYMENT' }).then((result) => {
        this.payeeTypeList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2(`/Master/GetListMaster`, {
        masterfile: 'DOC',
        module: 'null',
      }).then((result) => {
        this.docTypeList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'BANK' }).then((result) => {
        this.oriBankList = result;
        this.bankList = this.oriBankList;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Payment/GetQueryByJobConfig', {
        table: 'Container',
        top: this.contnoRowLimit,
      }).then((result) => {
        this.containerList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'COSTCODE' }).then((result) => {
        this.costCodeList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'LORRYNO' }).then((result) => {
        this.vehicleNoList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', {masterfile: 'APTAXCODE'}).then((result) => {
        this.taxCodeList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'UOM' }).then((result) => {
        this.uomList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CURRENCY' }).then((result) => {
        this.currencyList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'GLACCOUNT',
      }).then((result) => {
        this.debitAccList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Payment/GetChargesExpensesListing').then((result) => {
        this.expenseCodeList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Payment/GetExpensesDocRefNoListing').then((result) => {
        this.expensesDocRefNoList = result;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=JobClosedPassword').then((result) => {
        this.settingsJobClosePswd = result[0].SettingsValue;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=DefaultBankCS').then((result) => {
        this.defaultCSBank = result[0].SettingsValue;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=DefaultBankCH').then((result) => {
        this.defaultCHBank = result[0].SettingsValue;
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=PVBank').then((result) => {
        this.bankRequired = result[0].SettingsValue != '0';
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=EnableVendorRebate').then((result) => {
        this.enableVendorRebate = result[0].SettingsValue == 'Y';
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FINPay_MandatoryChq').then((result) => {
        this.mandatoryChq = result[0].SettingsValue.toUpperCase() == 'Y';
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FINPay_IssueCheque').then((result) => {
        this.allowChq = result[0].SettingsValue.toUpperCase() == 'TRUE';
        this.$topLoading(++mountedPromisesFulfilledCount / mountedPromises.length);
      }),
    );

    await Promise.allSettled(mountedPromises).then(async () => {
      await this.refreshClicked('PaymentList');
      this.menu.forEach((m) => (m['enabled'] = true));
    });

    if (this.$route.query['docno']) {
      this.filterPaymentBySearch = true;
      this.filterPaymentByDate = false;
      this.searchByValue = this.searchFilterItems.getSelected('Payment No', 'Search By');
      this.selectedFilterSearchCriteria = this.searchCriteriaItems.getSelected('EQUALS', 'name');
      this.searchInputValue = this.$route.query['docno'];
      await this.refreshClicked('PaymentList');
      this.selectedPaymentListing = this.paymentListing.filter((r) => r['Payment No'] === this.$route.query['docno']);
      // SAS Control
      const menuTitle = this.$route.query['action'] == 'E' ? 'Edit Payment' : 'View Payment';
      const selectedMenu = this.menu.getSelected(menuTitle, 'title');
      if (selectedMenu['visible'] === false) {
        this.$showAlert('warning', 'Access Denied', `You do not have permission to ${menuTitle}`);
        return;
      }
      this.$nextTick(() => {
        this.menuClicked({ 
          title: menuTitle 
        });
      });
    }

    if (this.expensesDocRefNoList.length > 0) {
      this.expensesDocRefNoList = this.expensesDocRefNoList.filter((doc) => doc['Expenses  Ref #'].trim() != '');
    }
  },
  watch: {
    headerExchRate: function (newVal, oldVal) {
      if (Number(oldVal) != Number(newVal)) {
        this.oldRate = Number(oldVal).toFixed(4);
      }
      this.chargesExchangeRate = Number(newVal).toFixed(4);
      this.chargesPriceLossFocus(false, 'exchgRate');
      this.dpExchangeRate = Number(newVal).toFixed(4);
      this.dpPriceLossFocus(false, 'exchgRate');
    },

    selectedJobChargeEntry: function (val) {
      if (val.length > 0 && this.action == '' && this.viewMode) {
        this.populateChargesDetails(val[0]);
        this.subtitleJC = val[0]['AP Inv No'] == '' ? 'Job Charges' : 'AP Invoice Offset';
        this.showInputCharges = true;
      }
    },

    chargesQty: function (val) {
      this.chargesAmtInputDisable = !(val.trim().length > 0);
    },

    selectedDirectPostingEntry: function (val) {
      if (val.length > 0 && this.action == '' && this.viewMode) {
        this.populateDPDetails(val[0]);
        this.showInputDirectPosting = true;
      }
    },

    showAPInvoices: function (val) {
      if (!val) this.selectedAPInvoices = [];
    },

    showJobCharges: function (val) {
      if (!val) {
        this.selectedJobCharges = [];
        this.jobChargesFilterOpen = false;
      }
    },

    selectedPaymentMethod: function (val) {
      if (val) {
        this.bankList = this.oriBankList.filter((b) => b['BankType'] == val['Payment Type']);
        switch (val['Payment Type']) {
          case 'CH':
            this.selectedBank =
              this.defaultCHBank.trim() != '' ? this.bankList.find((b) => b['BankCode'] == this.defaultCHBank) : null;
            this.chequeGiroNo = '';
            break;

          case 'CS':
            this.selectedBank =
              this.defaultCSBank.trim() != '' ? this.bankList.find((b) => b['BankCode'] == this.defaultCSBank) : null;
            if (this.allowChq && this.createCheque) this.chequeGiroNo = '**********';
            break;

          case 'LG':
            this.createCheque = false;
            this.chequeGiroNo = '';
            break;

          default:
            this.selectedBank = null;
        }
      } else {
        this.bankList = this.oriBankList;
      }
    },

    createCheque: function (val) {
      if (!this.viewMode) {
        if (val && this.allowChq && this.selectedPaymentMethod['Payment Type'] == 'CS') {
          this.chequeGiroNo = '**********';
        } else {
          this.chequeGiroNo = '';
        }
      }
    },

    selectedJobNo: async function (newVal, oldVal) {
      const cont = this.selectedContainer ?? null;
      this.custCreditDisable = !(newVal && newVal['Bill To']);
      if (newVal && newVal['Bill To']) {
        const billcust = await this.$apiCallV2('/Master/GetListMaster', {
          masterfile: 'BILLCUSTNAME',
        });
        const cust = billcust.find((c) => c['Customer Code'] === newVal['Bill To']);
        this.billCustName = cust ? cust['Customer Name'] : newVal['Bill To'];

        const outstanding = await this.$apiCallV2('/Payment/GetAROutstanding', {
          custcode: newVal['Bill To'],
        });
        this.billCreditDetails = outstanding.length > 0 ? outstanding[0] : null;
        if (this.billCreditDetails) {
          this.billCreditDetails['Outstanding'] = this.$currencyFormat(this.billCreditDetails['Outstanding']);
          this.billCreditDetails['CreditLimit'] = this.$currencyFormat(this.billCreditDetails['CreditLimit']);
        }
        this.custCreditDisable = false;
      } else {
        this.custCreditOpen = false;
        this.custCreditDisable = true;
        this.billCustName = '';
        this.billCreditDetails = null;
      }

      let newContList = [];
      if (newVal && this.selectedJobChargeEntry.length == 0) {
        const proceed = await this.checkJobStatus(newVal['Job No']);
        if (!proceed) {
          this.selectedJobNo = oldVal;
          return;
        }
        newContList = await this.$apiCallV2('/Payment/GetQueryByJobConfig', {
          table: 'Container',
          jobno: newVal['Job No'],
          top: this.contnoRowLimit,
        });
      } else {
        newContList = await this.$apiCallV2('/Payment/GetQueryByJobConfig', {
          table: 'Container',
          top: this.contnoRowLimit,
        });
      }
      if (this.selectedContainer) {
        const exist = newContList.some((cont) => cont['Cont No'] === this.selectedContainer['Cont No']);
        if (!exist) {
          await this.$showAlert(
            'warning',
            `Invalid Job No for Container ${this.selectedContainer['Cont No']}`,
            `${this.selectedContainer['Cont No']} does not belong to ${newVal['Job No'] ?? ''}`,
          );
          this.selectedJobNo = oldVal;
          return;
        }
      }
      this.containerList = [];
      this.containerList = newContList;
      if (cont) {
        this.selectedContainer = this.containerList.find((c) => c['Cont No'] == cont['Cont No']);
      }
    },
    selectedPaymentListing: function (val) {
      const authoBtn = this.menu.find((m) => m['key'] === 'mnAuthorized');
      if (val.length > 0) {
        authoBtn['title'] = val[0]['Authorized'] === 'Authorized' ? 'Unauthorize' : 'Authorize';
      } else {
        authoBtn['title'] = 'Authorize';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
@import '@/css/FIN.scss';

.sbi-main {
  display: flex;
  font-size: 12px;
  padding: 10px 45px;

  > .left-menu {
    display: flex;
    justify-content: center;
  }

  > .right-container {
    width: 100%;
    margin-left: 5px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    > .top-section,
    > .bottom-section {
      background-color: white;
      border-radius: 5px;
      padding: 5px;
    }

    > .top-section {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      > .filter-desc {
        padding-right: 8px;
      }
    }

    > .bottom-section {
      height: calc(100% - 38px - 5px); // deduct height of top-section and margin
      margin-top: 5px;
    }
  }

  > .attachments-popup-container {
    > .attachments-popup {
      ::v-deep {
        // deep needed to target the content class inside the PopupV2 component
        .content {
          // NOT the content class for slot, but content class inside PopupV2 itself
          overflow: visible;
        }
      }

      .attachments-popup-content {
        overflow: visible;

        > .attached-file {
          text-align: left;
          margin-bottom: 8px;

          > .file-link {
            cursor: pointer;
            user-select: none;

            &:hover {
              text-decoration: underline;
            }

            &:active {
              background-color: black;
              color: white;
            }
          }
        }
      }
    }
  }

  > .attachment-projector {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 99999;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;

    > .attachment-projection {
      height: 95%;
      width: 80%;

      > object {
        height: 100%;
        width: 100%;
        object-fit: scale-down;
      }

      > .attachment-not-projectable-container {
        height: 100%;
        width: 100%;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-size: 40px;

        > span {
          margin: 10px 0 10px 0;
          color: white;
        }

        > button {
          height: 50px;
          width: 150px;
          margin: 10px 0 10px 0;
          font-size: 16px;

          > img {
            height: 24px;
            width: 24px;
          }
        }
      }
    }

    > .close-button {
      height: 50px;
      width: 50px;
      position: absolute;
      top: 10px;
      right: 10px;
      border-radius: 100px;
      background-color: white;
      text-align: center;
      font-size: xx-large;
      cursor: pointer;
      user-select: none;
    }
  }
}

.button-filter-component {
  margin-right: 5px;
  position: relative;

  .filter-popup {
    display: flex;
    flex-direction: column;
    z-index: 10;

    > button {
      margin-top: 5px;
      width: fit-content;
      align-self: flex-end;
    }
  }
}

.payment-entryfields {
  height: calc(100% - 27px);
  width: 100%;
  overflow: scroll;

  .payment-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    > .header-containers {
      width: 100%;
      margin-right: 5px;
    }
  }
}

.total-block {
  border: 1px solid rgba(128, 128, 128, 0.5);
  padding: 5px 10px;
  align-content: space-evenly;
  min-width: 75px;
}

.tab-slot {
  display: flex;
  height: 100%;

  > .tab-details-styling {
    margin-right: 3px;
    height: 100%;
    overflow: scroll;

    .tab-buttons {
      display: flex;
      flex-direction: row;
      margin: 5px 5px;
      align-items: center;

      > button {
        margin-right: 5px;
        height: fit-content;
      }
    }
  }

  > .side-entry-open {
    height: 100%;
    border-left: 1px solid black;
    overflow: scroll;
    margin-left: 5px;
    padding-left: 10px;

    .title-button {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      height: 50px;
      margin: 5px 0px;
      padding: 5px 0px;
      position: sticky;
      top: 0;
      z-index: 5;
      background-color: white;

      .buttons-arr {
        display: flex;

        button {
          margin-right: 5px;
          height: fit-content;
        }
      }
    }
  }
}

.foreign-currency-background {
  > .background-left {
    width: calc(33.33% + 4px);
    border-right: 3px solid white;
  }

  > .background-middle {
    flex: 1;
    border-left: 3px solid white;
    border-right: 3px solid white;
  }

  > .background-right {
    width: calc(33.33% + 4px);
    border-left: 3px solid white;
  }
}

.status-container {
  border: 1px solid rgb(200, 200, 200);
  padding: 3px;
  margin-right: 5px;
  margin-left: 3px;
}

.outstanding-prog {
  display: flex;
  border: 1px solid rgb(200, 200, 200);
  position: relative;
  width: calc(100% - 2px);
  margin-left: 3px;
  padding: 3px;

  > .progress-ind {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 50%;
    background-color: rgba(51, 231, 255, 0.5);
  }
}
</style>
